/**
 * Grupostop booking (Flowww): clinics, calendar, availability, patient form, save.
 * Availability sends product_ids as indexed query array.
 * Save posts to POST /bookings (GS-26) with slot metadata from Comando 1042.
 */

// === Utilities ================================================================

function _gstStr(v) { return v == null ? '' : String(v).trim(); }

function _el(id) { return document.getElementById(id); }

function _esc(str) {
  if (!str) return '';
  return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function _gstNormalizeSearchText(str) {
  return String(str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
}

function _gstUniqueTerms(list) {
  const seen = {};
  return (list || []).filter(function (item) {
    const key = _gstNormalizeSearchText(item);
    if (!key || seen[key]) return false;
    seen[key] = true;
    return true;
  });
}

// === State ====================================================================

const GstBookingState = {
  KEYS: {
    productTitle:    'gst_product_title',
    productHandle:   'gst_product_handle',
    variantId:       'gst_variant_id',
    productId:       'gst_product_id',
    clinicId:        'gst_clinic_id',
    calendarId:      'gst_calendar_id',
    clinicName:      'gst_clinic_name',
    clinicAddress:   'gst_clinic_address',
    slotDate:        'gst_slot_date',
    slotTime:        'gst_slot_time',
    slotTimecode:    'gst_slot_timecode',
    formValues:      'gst_form_values',
    parentProductId: 'gst_parent_product_id',
    parentVariantId: 'gst_parent_variant_id',
    sessionsCount:   'gst_sessions_count',
    sessionsLabel:   'gst_sessions_label',
    zones:           'gst_zones',
    zoneEntries:     'gst_zone_entries',
    flowwwClientId:  'gst_flowww_client_id',
    // Bono being rebooked from the portal; lets the API cap the calendar at its expiry.
    voucherId:       'gst_voucher_id',
    flowwwNewCustomer: 'gst_flowww_new_customer',
    slotEndTime:     'gst_slot_end_time',
    cabineId:        'gst_cabine_id',
    clientReference: 'gst_client_reference',
    bookingOnly:     'gst_booking_only',
    me:              'gst_me',
    v1:              'gst_v1',
    priceLabel:      'gst_price_label',
    // GS-160 — what the API told us about the Flowww slot hold, so the "held for N
    // minutes" message is rendered from the server's own window rather than a
    // duration hard-coded here that could drift away from it.
    holdPlaced:      'gst_hold_placed',
    holdMinutes:     'gst_hold_minutes',
  },

  get: function (key) {
    try { return sessionStorage.getItem(this.KEYS[key]); } catch (e) { return null; }
  },
  set: function (key, value) {
    try { sessionStorage.setItem(this.KEYS[key], value); } catch (e) {}
  },
  getJson: function (key) {
    const raw = this.get(key);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  },
  setJson: function (key, value) {
    this.set(key, JSON.stringify(value));
  },
  clear: function () {
    const self = this;
    // GS-160 — every call site of clear() means "this booking flow is over": entering a
    // bookable PDP, restarting after a stale slot, seeding a portal rebook, or the debug
    // panel's reset. A Flowww block is held for the slot the customer is walking away
    // from and Flowww never expires it, so the release is triggered from the one place
    // they all pass through rather than from each of them.
    //
    // Best effort only. The customer may close the tab instead, so the API also releases
    // the block when the hold expires and sweeps anything left behind.
    _gstReleaseHoldFor(self.get('clientReference'));

    Object.keys(this.KEYS).forEach(function (k) {
      try { sessionStorage.removeItem(self.KEYS[k]); } catch (e) {}
    });
  },
};

try { window.GstBookingState = GstBookingState; } catch (e) {}

// The debug logger's own state (its section is further down, with the rest of the
// logger). It is assigned here because the rebook hand-off IIFE below calls
// GstBookingState.clear() -> _gstReleaseHoldFor() -> _gstDebugLog() at module-evaluation
// time (GS-160). `var` hoists the declarations but not the assignments, so leaving these
// in the Debug section left both `undefined` for that first call: the entry was written
// to sessionStorage['undefined'] and skipped the entry cap, silently — writing to the
// wrong key does not throw, so the try/catch around the log call never saw it.

var GST_DEBUG_KEY = 'gst_booking_debug_log';
var GST_DEBUG_MAX = 120;

// === Rebook hand-off from the customer-account portal =========================
// With Shopify "new customer accounts" the portal lives in a Shopify-hosted page
// on a different origin, so it cannot seed this tab's sessionStorage the way the
// classic theme portal does (assets/customer-bookings.js:startVoucherBooking).
// Instead the extension links here with ?gst_rebook=1&… query params; we hydrate
// GstBookingState from them synchronously — before any DOMContentLoaded handler
// reads the state — and set the same rebook flags the flow already expects.
(function _gstSeedRebookFromQuery() {
  var params;
  try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
  if (!params || params.get('gst_rebook') !== '1') return;

  function norm(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Vouchers carry only a clinic *name*, so resolve the clinic/calendar id from
  // the theme's centros list by name — mirroring customer-bookings.js.
  function resolveCentroByName(name) {
    var list = Array.isArray(window.__GST_BOOKING_CENTROS__) ? window.__GST_BOOKING_CENTROS__ : [];
    var target = norm(name);
    if (!target) return null;
    for (var i = 0; i < list.length; i++) {
      if (norm(list[i] && list[i].name) === target) return list[i];
    }
    return null;
  }

  var voucherId      = params.get('voucher_id') || '';
  var flowwwClientId = params.get('flowww_client_id') || '';
  var clinicName     = params.get('clinic_name') || '';
  var clinicAddress  = params.get('clinic_address') || '';
  var clinicIdParam  = params.get('clinic_id') || '';
  var productId      = params.get('product_id') || '';
  var productTitle   = params.get('product_title') || '';
  var sessions       = params.get('sessions') || '';

  // Prefer the explicit clinic_id from the API; fall back to resolving the
  // clinic/calendar id from the theme's centros list by name (which is empty
  // when the centros product isn't configured) — mirroring customer-bookings.js.
  var centro     = resolveCentroByName(clinicName);
  var clinicId   = (centro && centro.id) || clinicIdParam || '';
  var calendarId = (centro && centro.calendarId) || (centro && centro.id) || clinicIdParam || '';

  GstBookingState.clear();
  if (productTitle) GstBookingState.set('productTitle', productTitle);
  if (productId)    GstBookingState.set('productId', productId);
  GstBookingState.set('clinicId', String(clinicId));
  GstBookingState.set('calendarId', String(calendarId));
  GstBookingState.set('clinicName', clinicName || (centro && centro.name) || '');
  // Prefer the explicit address from the API; fall back to collection metafields
  // (same source as the homepage map), then the centros catalog.
  var rebookCoords = clinicId ? _gstClinicCoordsById(clinicId) : null;
  GstBookingState.set('clinicAddress', clinicAddress || _gstFormatClinicAddressFromCoords(rebookCoords) || (centro && centro.address) || '');
  if (sessions) {
    GstBookingState.set('sessionsCount', sessions);
    GstBookingState.set('sessionsLabel', sessions);
  }
  if (flowwwClientId) GstBookingState.set('flowwwClientId', flowwwClientId);
  if (voucherId)      GstBookingState.set('voucherId', voucherId);

  try {
    sessionStorage.setItem('gst_rebook', '1');
    sessionStorage.setItem('gst_rebook_voucher_id', voucherId);
  } catch (e) {}

  // Strip the params so a refresh doesn't re-seed and ids don't linger in the URL.
  try {
    window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
  } catch (e) {}
})();

// === Config ===================================================================

function _gstBookingCfg() {
  return typeof window !== 'undefined' && window.__GST_BOOKING__ ? window.__GST_BOOKING__ : {};
}

function _gstApiBaseUrl() {
  let u = _gstBookingCfg().apiBaseUrl;
  if (u == null || typeof u !== 'string') return '';
  u = u.trim().replace(/\/$/, '');
  return (!u || u === '-') ? '' : u;
}

function _gstApiVersion() {
  const v = _gstBookingCfg().apiVersion;
  if (!v || typeof v !== 'string') return 'v1';
  return v.replace(/^\/+/, '').replace(/\/+$/, '') || 'v1';
}

function _gstUseBookingApi() {
  return !!_gstBookingCfg().apiEnabled && _gstApiBaseUrl().length > 0;
}

function _gstAppointmentTypeId() {
  return GstBookingState.get('productId') || '';
}

// === Debug ====================================================================
// GST_DEBUG_KEY / GST_DEBUG_MAX are assigned above the rebook hand-off IIFE, which
// logs before this point in the file — see the note there.

function _gstDebugSafeDetail(d) {
  if (d === undefined) return undefined;
  try { return JSON.parse(JSON.stringify(d)); } catch (e) {
    try { return String(d); } catch (e2) { return '[unserializable]'; }
  }
}

function _gstDebugLog(msg, detail) {
  var entry = { t: new Date().toISOString(), m: String(msg || '') };
  var sd = _gstDebugSafeDetail(detail);
  if (sd !== undefined) entry.d = sd;
  try {
    var raw = sessionStorage.getItem(GST_DEBUG_KEY);
    var arr = [];
    if (raw) { try { arr = JSON.parse(raw); } catch (e) { arr = []; } }
    if (!Array.isArray(arr)) arr = [];
    arr.push(entry);
    if (arr.length > GST_DEBUG_MAX) arr = arr.slice(-GST_DEBUG_MAX);
    sessionStorage.setItem(GST_DEBUG_KEY, JSON.stringify(arr));
  } catch (e) {}
  try {
    if (window.console && console.warn) {
      console.warn('[GST Booking]', entry.m, entry.d !== undefined ? entry.d : '');
    }
  } catch (e2) {}
}

function _gstDebugRead() {
  try {
    var raw = sessionStorage.getItem(GST_DEBUG_KEY);
    if (!raw) return [];
    var arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (e) { return []; }
}

function _gstDebugClear() {
  try { sessionStorage.removeItem(GST_DEBUG_KEY); } catch (e) {}
}

function _gstDebugSnapshotSession() {
  var booking = {};
  try {
    Object.keys(GstBookingState.KEYS).forEach(function (k) {
      booking[k] = GstBookingState.get(k);
    });
  } catch (e) {}

  var other = {};
  try {
    for (var i = 0; i < sessionStorage.length; i++) {
      var k = sessionStorage.key(i);
      if (!k || String(k).toLowerCase().indexOf('gst') !== 0) continue;
      var managed = Object.keys(GstBookingState.KEYS).some(function (lk) {
        return GstBookingState.KEYS[lk] === k;
      });
      if (managed || k === GST_DEBUG_KEY) continue;
      try { other[k] = sessionStorage.getItem(k); } catch (e3) {}
    }
  } catch (e) {}

  return { bookingState: booking, otherGstKeys: other };
}

// Opt-in only: ?gst_debug=1 activates, ?gst_debug=0 deactivates.
function _gstShouldRenderDebugPanel() {
  try {
    if ((location.search || '').indexOf('gst_debug=1') !== -1) {
      sessionStorage.setItem('gst_debug_panel', '1');
    } else if ((location.search || '').indexOf('gst_debug=0') !== -1) {
      sessionStorage.removeItem('gst_debug_panel');
    }
    return sessionStorage.getItem('gst_debug_panel') === '1';
  } catch (e) { return false; }
}

function _gstRenderCartDebugPanel() {
  if (!_gstShouldRenderDebugPanel()) return;

  var mount = document.getElementById('gst-booking-debug-mount');
  if (!mount) {
    mount = document.createElement('div');
    mount.id = 'gst-booking-debug-mount';
    var anchor = document.querySelector('.cart .page-width') ||
        document.querySelector('main .page-width') ||
        document.querySelector('main') || document.body;
    anchor.insertBefore(mount, anchor.firstChild);
  }

  var entries = _gstDebugRead();
  var snap = _gstDebugSnapshotSession();
  var esc = function (s) {
    return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  };

  var lines = entries.map(function (e) {
    var detail = e.d !== undefined ? ' ' + esc(JSON.stringify(e.d)) : '';
    return '<li><span class="gst-booking-debug-cart__meta">' + esc(e.t) + '</span> — ' + esc(e.m) + detail + '</li>';
  }).join('');

  var bookingRows = Object.keys(snap.bookingState).map(function (k) {
    var v = snap.bookingState[k];
    var cls = (v == null || v === '') ? ' gst-booking-debug-cart__row--empty' : '';
    return '<tr class="gst-booking-debug-cart__row' + cls + '"><th>' + esc(k) + '</th><td>' + esc(v == null ? '∅' : v) + '</td></tr>';
  }).join('');

  var otherKeys = Object.keys(snap.otherGstKeys);
  var otherRows = otherKeys.map(function (k) {
    return '<tr class="gst-booking-debug-cart__row gst-booking-debug-cart__row--unmanaged"><th>' + esc(k) + '</th><td>' + esc(snap.otherGstKeys[k]) + '</td></tr>';
  }).join('');

  mount.innerHTML =
      '<aside class="gst-booking-debug-cart" role="region" aria-label="Booking debug log">' +
      '<p class="gst-booking-debug-cart__title">Booking flow debug (session) — <span class="gst-booking-debug-cart__path">' + esc(location.pathname) + '</span></p>' +
      '<p class="gst-booking-debug-cart__links">' +
      '<a href="/pages/reservar-datos">Patient details</a>' +
      '<a href="/pages/reservar-fecha">Calendar</a>' +
      '</p>' +
      '<details class="gst-booking-debug-cart__details" open>' +
      '<summary>sessionStorage — managed booking state</summary>' +
      '<table class="gst-booking-debug-cart__table">' + bookingRows + '</table>' +
      '</details>' +
      (otherKeys.length
          ? '<details class="gst-booking-debug-cart__details" open>' +
          '<summary>sessionStorage — other gst* keys (' + otherKeys.length + ')</summary>' +
          '<table class="gst-booking-debug-cart__table">' + otherRows + '</table>' +
          '</details>'
          : '') +
      '<details class="gst-booking-debug-cart__details">' +
      '<summary>Event log (' + entries.length + ')</summary>' +
      (lines
          ? '<ul class="gst-booking-debug-cart__list">' + lines + '</ul>'
          : '<p class="gst-booking-debug-cart__meta">No log entries yet.</p>') +
      '</details>' +
      '<div class="gst-booking-debug-cart__actions">' +
      '<button type="button" class="gst-booking-debug-cart__btn" id="gst-debug-clear">Clear log</button>' +
      '<button type="button" class="gst-booking-debug-cart__btn" id="gst-debug-refresh">Refresh</button>' +
      '<button type="button" class="gst-booking-debug-cart__btn" id="gst-debug-state-clear">Clear booking state</button>' +
      '</div>' +
      '</aside>';

  var clearBtn = document.getElementById('gst-debug-clear');
  if (clearBtn) clearBtn.addEventListener('click', function () { _gstDebugClear(); _gstRenderCartDebugPanel(); });
  var refreshBtn = document.getElementById('gst-debug-refresh');
  if (refreshBtn) refreshBtn.addEventListener('click', _gstRenderCartDebugPanel);
  var stateClearBtn = document.getElementById('gst-debug-state-clear');
  if (stateClearBtn) {
    stateClearBtn.addEventListener('click', function () {
      try { GstBookingState.clear(); } catch (e) {}
      _gstRenderCartDebugPanel();
    });
  }
}

try {
  window.GstBookingDebug = {
    read:  _gstDebugRead,
    clear: _gstDebugClear,
    state: function () { return _gstDebugSnapshotSession(); },
    audit: function () {
      var pdp = window.__GST_BOOKABLE_PRODUCT__;
      if (!pdp) return null;
      return { id: pdp.id, handle: pdp.handle, title: pdp.title, variantClinicMap: pdp.variantClinicMap || {} };
    },
    dump:  function () {
      try {
        var snap = _gstDebugSnapshotSession();
        var entries = _gstDebugRead();
        console.group('[GST Booking] debug dump @ ' + location.pathname);
        console.log('sessionStorage (booking state):', snap.bookingState);
        if (Object.keys(snap.otherGstKeys).length) {
          console.log('sessionStorage (other gst* keys):', snap.otherGstKeys);
        }
        if (console.table) {
          console.table(entries.map(function (e) {
            return { t: e.t, msg: e.m, detail: e.d ? JSON.stringify(e.d) : '' };
          }));
        } else {
          console.log('Event log:', entries);
        }
        console.groupEnd();
        return entries.length;
      } catch (e) { console.warn('dump failed', e); return 0; }
    }
  };
} catch (e) {}

(function _gstDebugInit() {
  var snap = _gstDebugSnapshotSession();
  _gstDebugLog('booking.js execute', {
    path: location.pathname,
    bookablePdp: !!window.__GST_BOOKABLE_PDP__,
    noBooking: window.__GST_BOOKABLE_PRODUCT__ ? !!window.__GST_BOOKABLE_PRODUCT__.noBooking : null,
    bookingOnly: window.__GST_BOOKABLE_PRODUCT__ ? !!window.__GST_BOOKABLE_PRODUCT__.bookingOnly : null,
    ea: window.__GST_BOOKABLE_PRODUCT__ ? !!window.__GST_BOOKABLE_PRODUCT__.ea : null,
    v1: window.__GST_BOOKABLE_PRODUCT__ ? (window.__GST_BOOKABLE_PRODUCT__.v1 || null) : null,
    productId: window.__GST_BOOKABLE_PRODUCT__ && window.__GST_BOOKABLE_PRODUCT__.id,
    centrosHandle: typeof window.__GST_BOOKING_CENTROS_HANDLE !== 'undefined' ? window.__GST_BOOKING_CENTROS_HANDLE : null,
    centrosVariantCount: Array.isArray(window.__GST_BOOKING_CENTROS__) ? window.__GST_BOOKING_CENTROS__.length : 0,
    sessionStorage: snap.bookingState
  });

  if (_gstShouldRenderDebugPanel()) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', _gstRenderCartDebugPanel);
    } else {
      _gstRenderCartDebugPanel();
    }
  }
})();

// === Flowww IDs ===============================================================
//
// product_ids (= session.productId) on a bookable PDP comes from the Zona/Zone
// GPO option. Every zone is its own Shopify product whose variant SKU is the
// Flowww product id. We resolve the SKU via /products/{handle}.js at click time.
//
// clinic_id is resolved purely by ID — clinic display names intentionally
// differ between Shopify and Flowww, so any name-based matching is unreliable.
//   1. Bookable variant SKU = Flowww clinic ID (universal; always set on
//      every clinic variant of every bookable product).
//   2. variant.metafields.custom.flowww_clinic_id (explicit per-variant override).
// The resolved id is then used to look up the centros catalog by id, so the
// calendar step can show canonical clinic name/address/calendarId.

var _gstProductCache = {};

function _gstFetchProductByHandle(handle) {
  var h = _gstStr(handle);
  if (!h) return Promise.resolve(null);
  if (_gstProductCache[h]) return _gstProductCache[h];
  var url = '/products/' + encodeURIComponent(h) + '.js';
  var p = fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) return null;
        // Shopify sends JSON with Content-Type "text/javascript"; parse manually.
        return res.text().then(function (body) {
          try { return JSON.parse(body); }
          catch (e) { return null; }
        });
      })
      .catch(function () { return null; });
  _gstProductCache[h] = p;
  return p;
}

function _gstFetchVariantSkuByHandle(handle, variantId) {
  return _gstFetchProductByHandle(handle).then(function (json) {
    if (!json) return '';
    var variants = Array.isArray(json.variants) ? json.variants : [];
    if (!variants.length) return '';
    var vid = _gstStr(variantId);
    var match = vid
        ? variants.find(function (v) { return v && _gstStr(v.id) === vid; })
        : null;
    return _gstStr((match || variants[0]).sku);
  });
}

// Resolve a comma-separated productId string by mapping every selected zone
// entry to its variant SKU. Multiple zones → multiple product_ids in the
// availability and booking requests. Empty/duplicate IDs are dropped while
// preserving the user's selection order.
function _gstResolveAppointmentProductId(zoneEntries) {
  var list = Array.isArray(zoneEntries) ? zoneEntries : [];
  var withHandle = list.filter(function (z) { return z && _gstStr(z.handle); });
  if (!withHandle.length) return Promise.resolve('');
  return Promise.all(withHandle.map(function (z) {
    return _gstFetchVariantSkuByHandle(z.handle, z.variantId).catch(function () { return ''; });
  })).then(function (ids) {
    var seen = {};
    var out = [];
    ids.forEach(function (id) {
      var v = _gstStr(id);
      if (!v || seen[v]) return;
      seen[v] = true;
      out.push(v);
    });
    return out.join(',');
  });
}

// Products tagged `bookingonly` (e.g. Hilos tensores) have no Zona GPO options,
// so zone-based resolution yields an empty product_id and the availability call
// would fail. For those, use the product's `custom.V1` metafield as the Flowww
// product_id (exposed on __GST_BOOKABLE_PRODUCT__.v1). Everything else keeps the
// existing zone-to-SKU resolution.
function _gstResolvePdpProductId(pdp, data) {
  // EA (Estética Avanzada) products never use custom.V1 for the MAIN "book and
  // pay" CTA: their Flowww service id always comes from the options app (GPO
  // zone selection). V1 is reserved for the separate "Reserva ahora" consulta
  // button. So for EA we stop looking at bookingonly entirely (theme.liquid
  // already neutralises it; this keeps the rule true even if that changes).
  var isEa = !!(pdp && pdp.ea);
  var bookingOnly = !isEa && !!(pdp && pdp.bookingOnly);
  var v1 = pdp ? _gstStr(pdp.v1) : '';
  _gstDebugLog('resolve pdp product id', {
    ea:          isEa,
    bookingOnly: bookingOnly,
    v1:          v1 || 'MISSING (custom.V1)',
    usingV1:     bookingOnly && !!v1,
    zoneEntries: (data && data.zoneEntries) || []
  });
  if (bookingOnly && v1) {
    return Promise.resolve(v1);
  }
  return _gstResolveAppointmentProductId(data.zoneEntries);
}

// Resolve each zone entry to a Shopify variant id suitable for /cart/add.js.
// Prefer the variantId captured from the GPO radio (`gpo-data-variant-id`);
// when missing — e.g. some GPO config branches only expose the handle — fall
// back to the first variant of the linked product. Duplicates are dropped so
// repeated picks of the same body part don't create duplicate cart lines.
function _gstResolveZoneCartItems(zoneEntries) {
  var list = Array.isArray(zoneEntries) ? zoneEntries : [];
  var withHandle = list.filter(function (z) { return z && _gstStr(z.handle); });
  if (!withHandle.length) return Promise.resolve([]);
  return Promise.all(withHandle.map(function (z) {
    var label = _gstStr(z.label);
    var handle = _gstStr(z.handle);
    var vid = _gstStr(z.variantId);
    var sessions = _gstStr(z.sessions);
    var qty = parseInt(z.quantity, 10);
    if (isNaN(qty) || qty < 1) qty = 1;
    if (vid) return Promise.resolve({ label: label, variantId: vid, handle: handle, quantity: qty, sessions: sessions });
    return _gstFetchProductByHandle(handle).then(function (json) {
      var variants = json && Array.isArray(json.variants) ? json.variants : [];
      var first = variants[0];
      return { label: label, variantId: first ? _gstStr(first.id) : '', handle: handle, quantity: qty, sessions: sessions };
    }).catch(function () {
      return { label: label, variantId: '', handle: handle, quantity: qty, sessions: sessions };
    });
  })).then(function (entries) {
    // Drop entries without a variant, and merge duplicates of the same variant
    // by summing their quantities so repeated picks add up instead of vanishing.
    var byVariant = {};
    var out = [];
    entries.forEach(function (e) {
      if (!e.variantId) return;
      if (byVariant[e.variantId] != null) {
        out[byVariant[e.variantId]].quantity += (e.quantity || 1);
        return;
      }
      byVariant[e.variantId] = out.length;
      out.push(e);
    });
    return out;
  });
}

function _gstNormalizeClinicName(name) {
  return _gstStr(name).toLowerCase().replace(/\s+/g, ' ').trim();
}

// When a promo product's centro variants ship without SKU / flowww_clinic_id
// (product-template gap), resolve the Flowww clinic ID by matching the variant
// label against the centros catalog or the collection title map from theme.liquid.
function _gstResolveClinicIdByName(clinicName) {
  var norm = _gstNormalizeClinicName(clinicName);
  if (!norm) return '';

  var list = _gstCentrosCatalog();
  for (var i = 0; i < list.length; i++) {
    var c = list[i];
    if (!c) continue;
    var cNorm = _gstNormalizeClinicName(c.name);
    if (cNorm && cNorm === norm) return _gstStr(c.id);
  }

  var map = (typeof window !== 'undefined' && window.__GST_CLINIC_NAME_MAP__) || null;
  if (map && typeof map === 'object') {
    var cityPart = norm.split(' - ')[0].trim();
    if (cityPart && map[cityPart]) return _gstStr(map[cityPart]);

    var keys = Object.keys(map);
    for (var j = 0; j < keys.length; j++) {
      var key = keys[j];
      var keyNorm = _gstNormalizeClinicName(key);
      if (!keyNorm) continue;
      if (keyNorm === norm || norm.indexOf(keyNorm) !== -1 || keyNorm.indexOf(norm) !== -1) {
        return _gstStr(map[key]);
      }
    }
  }
  return '';
}

function _gstResolveClinicIdFromVariant(vc) {
  if (!vc) return '';
  // The bookable variant's SKU is the Flowww clinic ID for every clinic in
  // every bookable product. Universal, name-independent, always set.
  if (_gstStr(vc.sku)) return _gstStr(vc.sku);
  // Explicit per-variant override via custom.flowww_clinic_id metafield.
  if (_gstStr(vc.clinicId)) return _gstStr(vc.clinicId);
  return _gstResolveClinicIdByName(vc.clinicName);
}

function _gstDefaultSessionsCount() {
  var n = parseInt(typeof window !== 'undefined' ? window.__GST_DEFAULT_SESSIONS__ : 0, 10);
  return (!isNaN(n) && n > 0) ? n : 0;
}

// Quantity-combo product (custom.combo_qty, injected by layout/theme.liquid):
// the per-part stepper IS the session count and the unit price is per session,
// so one Shopify unit is one session.
function _gstIsComboQtyProduct() {
  try {
    return !!(typeof window !== 'undefined' && window.__GST_COMBO_QTY__);
  } catch (e) {
    return false;
  }
}

// Split a zone line's session count into what the invoice needs (`_sessions`,
// sessions per Shopify UNIT, which the API multiplies by the line quantity) and
// what the line should read as (`_sessions_label`, the true total).
//
// Outside the quantity combos, a resolved entry.sessions is per-unit: the pack
// size baked into the field/handle (x3 / x6), the paired "Número de sesiones"
// pick, or custom.default_sessions. Quantity-combo products carry the count in
// the quantity instead, so per-unit is 1 — writing the quantity into `_sessions`
// is what made 3 sessions at €22 arrive in Flowww as 3 × 3 = 9.
function _gstLineSessions(entry, qty) {
  // Quantity-combo products are decided by the flag alone: their per-part
  // stepper IS the session count, so one unit is one session whatever
  // entry.sessions resolved to. A stray "Número de sesiones" group on the PDP
  // lands in sessionsByGroup and, being the only distinct value, gets attached
  // to zones whose own group key did not match — which is how one tarifa plana
  // order sent 1 session for one zone and 9 for another.
  if (_gstIsComboQtyProduct()) {
    return { perUnit: 1, total: qty };
  }
  var own = parseInt(_gstStr(entry && entry.sessions), 10);
  if (!isNaN(own) && own > 0) {
    return { perUnit: own, total: own * qty };
  }
  // No pack marker and no sessions group. The only stepper on these PDPs is
  // labelled "sesiones" and the price is per session, so the quantity already
  // IS the session count and one unit buys one session. Writing the quantity
  // here instead squared the line, since the API multiplies `_sessions` by it:
  // 3 sessions reached Flowww as 3 x 3 = 9 (orders 45945, 45946).
  return { perUnit: 1, total: qty };
}

function _gstApplyDefaultSessions(data) {
  if (!data) return;
  var def = _gstDefaultSessionsCount();
  if (!def) return;
  if (!_gstStr(data.sessions)) data.sessions = String(def);
  if (Array.isArray(data.zoneEntries)) {
    data.zoneEntries.forEach(function (entry) {
      if (!entry) return;
      if (!_gstStr(entry.sessions)) entry.sessions = String(def);
    });
  }
}

function _gstResolveCalendarForVariant(vc, clinicId) {
  return _gstStr(vc && vc.calendarId) || _gstStr(clinicId);
}

function _gstCentrosCatalog() {
  return Array.isArray(window.__GST_BOOKING_CENTROS__) ? window.__GST_BOOKING_CENTROS__ : [];
}

// Look up centros catalog entry by Flowww clinic ID to enrich a variant-
// resolved clinic with canonical name/address/calendarId, since clinic
// names in Shopify and Flowww intentionally differ.
function _gstMatchCentroById(clinicId) {
  var cid = _gstStr(clinicId);
  if (!cid) return null;
  var list = _gstCentrosCatalog();
  return list.find(function (c) { return c && _gstStr(c.id) === cid; }) || null;
}

// Pre-stored coords from collection metafields (same source as homepage map).
function _gstClinicCoordsById(clinicId) {
  var cid = _gstStr(clinicId);
  if (!cid) return null;
  var map = window.__GST_CLINIC_COORDS__;
  if (!map || typeof map !== 'object') return null;
  return map[cid] || null;
}

function _gstFormatClinicAddressFromCoords(coords) {
  if (!coords) return '';
  var addr = _gstStr(coords.address);
  var city = _gstStr(coords.city);
  var pc = _gstStr(coords.postalCode);
  var parts = [];
  if (addr) parts.push(addr);
  if (pc && city) parts.push(pc + ' ' + city);
  else if (city) parts.push(city);
  else if (pc) parts.push(pc);
  return parts.join(', ');
}

// Resolve the clinic from the selected bookable variant by Flowww ID. Display
// fields prefer the variant (so the user sees the same label they clicked),
// with collection coords / centros catalog as fallbacks for address/calendarId.
function _gstClinicFromVariant(vc) {
  var vcId = _gstResolveClinicIdFromVariant(vc);
  if (!vcId) return null;
  var enrich = _gstMatchCentroById(vcId);
  var coords = _gstClinicCoordsById(vcId);
  var coordAddr = _gstFormatClinicAddressFromCoords(coords);
  return {
    clinicId:      vcId,
    clinicName:    _gstStr(vc && vc.clinicName)    || (enrich && _gstStr(enrich.name))    || '',
    clinicAddress: _gstStr(vc && vc.clinicAddress) || coordAddr || (enrich && _gstStr(enrich.address)) || '',
    calendarId:    _gstResolveCalendarForVariant(vc, vcId) || (enrich && _gstStr(enrich.calendarId)) || vcId,
    lat:           coords && isFinite(coords.lat) ? coords.lat : null,
    lng:           coords && isFinite(coords.lng) ? coords.lng : null
  };
}

// === HTTP + Parsers ===========================================================

function _gstApiPath(segment) {
  return _gstApiBaseUrl() + '/' + _gstApiVersion() + segment;
}

// Tell the API the customer has walked away from this slot, so the Flowww block held for
// it stops making the slot unavailable to everybody else (GS-160).
//
// A function DECLARATION, not an assignment: GstBookingState.clear() calls it, and one of
// clear()'s call sites is a module-level IIFE that runs before this point in the file.
//
// sendBeacon is preferred because clear() often runs on a navigation, and a plain fetch is
// cancelled when the page goes away. Its response is unreadable by design, which is fine —
// nothing here can act on the outcome, and the API answers 200 for an unknown or
// already-settled reference precisely so this call never needs error handling.
function _gstReleaseHoldFor(clientReference) {
  var ref = _gstStr(clientReference);
  if (!ref || !_gstUseBookingApi()) return;

  var url = _gstApiPath('/bookings/' + encodeURIComponent(ref) + '/release');

  // Safe from module-level callers: the earliest clear() runs from the rebook hand-off
  // IIFE, and the debug logger's own state is assigned above it for that reason.
  try { _gstDebugLog('hold: releasing', { clientReference: ref }); } catch (e) {}

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      if (navigator.sendBeacon(url)) return;
    }
  } catch (e) {}

  try {
    fetch(url, {
      method: 'POST',
      credentials: 'omit',
      keepalive: true,
      headers: _gstJsonPostHeaders(),
    }).catch(function () {});
  } catch (e) {}
}

function _gstFetchGet(url) {
  return fetch(url, {
    method: 'GET',
    credentials: 'omit',
    headers: { 'Accept': 'application/json, text/html, text/plain, */*' }
  });
}

function _gstJsonPostHeaders() {
  var h = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
  var cfg = _gstBookingCfg();
  if (cfg.apiToken) h['Authorization'] = 'Bearer ' + cfg.apiToken;
  return h;
}

// Session stores one string; split into array for Laravel's product_ids[] param.
function _gstAvailabilityProductIds() {
  const raw = _gstStr(GstBookingState.get('productId'));
  if (!raw) return [];
  return raw.split(/[\s,]+/).map(function (s) { return _gstStr(s); }).filter(Boolean);
}

function _gstBookingProductIds() {
  return _gstAvailabilityProductIds()
      .map(function (id) { return parseInt(id, 10); })
      .filter(function (n) { return n > 0; });
}

function _gstUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = Math.random() * 16 | 0;
    var v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

function _gstNormalizeTimeHHmm(time) {
  if (!time) return '';
  var parts = String(time).trim().split(':');
  if (parts.length < 2) return '';
  var h = parseInt(parts[0], 10);
  var m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m)) return '';
  return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
}

function _gstSlotSelectionParts() {
  var date  = _gstStr(GstBookingState.get('slotDate'));
  var start = _gstNormalizeTimeHHmm(GstBookingState.get('slotTime'));
  var end   = _gstNormalizeTimeHHmm(GstBookingState.get('slotEndTime'));
  var tc    = _gstStr(GstBookingState.get('slotTimecode'));

  if (tc.indexOf('T') !== -1) {
    var bits = tc.split('T');
    if (!date) date = bits[0];
    if (!start) start = _gstNormalizeTimeHHmm(bits[1]);
  }

  return {
    date:      date,
    startTime: start,
    endTime:   end,
    cabineId:  parseInt(GstBookingState.get('cabineId') || '0', 10),
  };
}

function _gstBookingError(code, detail) {
  var err = new Error(code);
  err.code = code;
  err.detail = detail;
  return err;
}

function _gstBuildBookingsPayload() {
  var clinicId   = parseInt(GstBookingState.get('clinicId') || '0', 10);
  var productIds = _gstBookingProductIds();
  var slot       = _gstSlotSelectionParts();
  var missing    = [];

  if (!clinicId) missing.push('clinic_id');
  if (!productIds.length) missing.push('product_ids');
  if (!slot.date) missing.push('date');
  if (!slot.startTime) missing.push('start_time');
  if (!slot.endTime) missing.push('end_time');
  if (!slot.cabineId) missing.push('cabine_id');

  if (missing.length) return { error: 'missing_fields', missing: missing };

  // Fresh booking_session_id per /bookings call. The API uses this only for log
  // correlation today; retries always create a new row keyed on client_reference.
  // Refresh-after-success duplicates are blocked by the clientReference guard
  // in _submitBooking.
  return {
    body: {
      clinic_id:          clinicId,
      product_ids:        productIds,
      date:               slot.date,
      start_time:         slot.startTime,
      end_time:           slot.endTime,
      cabine_id:          slot.cabineId,
      booking_session_id: _gstUuid(),
    }
  };
}

function _gstBookingFailureMessage(err) {
  if (!err) return null;
  // Patient step hidden: client-id error branches are commented out with the
  // client-resolve flow. Restore together with the patient-details step.
  // if (err.code === 'missing_fields') {
  //   return 'Faltan datos obligatorios. <a href="/pages/reservar-fecha">Completa el formulario</a> e inténtalo de nuevo.';
  // }
  // if (err.code === 'client_unresolved') {
  //   return 'No pudimos identificar tu perfil. <a href="/pages/reservar-fecha">Revisa tus datos</a> e inténtalo de nuevo.';
  // }
  if (err.code === 'missing_booking_fields') {
    return 'Faltan datos de la cita seleccionada. <a href="/pages/reservar-fecha">Elige fecha y hora de nuevo</a>.';
  }
  if (err.code === 'upstream_unavailable' || err.code === 'http_503') {
    return 'El sistema de reservas no está disponible en este momento. Inténtalo de nuevo en unos minutos.';
  }
  if (err.detail && err.detail.flowww_error_description) {
    return err.detail.flowww_error_description;
  }
  if (err.code === 'http_502') {
    return 'No pudimos confirmar la cita con el centro. Inténtalo de nuevo en unos minutos.';
  }
  var apiErr = err.detail && err.detail.error;
  if (apiErr === 'slot_unavailable') {
    return 'El horario ya no está disponible. <a href="/pages/reservar-fecha">Elige otro horario</a>.';
  }
  // if (apiErr === 'client_incomplete') {
  //   return 'No pudimos validar tus datos para reservar. <a href="/pages/reservar-fecha">Revisa la información</a> e inténtalo de nuevo.';
  // }
  return null;
}

// `withClientId=true` adds flowww_client_id, required by /availability/slots since GS-26.
// /availability/dates does not need it.
function _gstAvailabilityParams(withClientId) {
  const clinicId = _gstStr(GstBookingState.get('clinicId'));
  const productIds = _gstAvailabilityProductIds();
  const flowwwClientId = _gstStr(GstBookingState.get('flowwwClientId'));

  const params = new URLSearchParams();
  if (clinicId) params.set('clinic_id', clinicId);
  productIds.forEach(function (id, i) {
    params.append('product_ids[' + i + ']', id);
  });
  // Patient step hidden: flowww_client_id is no longer sent on availability calls.
  // Restore this together with the patient-details step.
  // if (withClientId && flowwwClientId) params.set('flowww_client_id', flowwwClientId);

  _gstDebugLog('availability params', {
    clinic_id:        clinicId || 'MISSING',
    product_ids:      productIds.length ? productIds : 'MISSING (session.productId)',
    flowww_client_id: withClientId ? (flowwwClientId || 'MISSING') : 'n/a'
  });

  return params;
}

function _gstParseCalendarDatesPayload(text, json, contentType) {
  if (json && typeof json === 'object') {
    if (json.html != null && typeof json.html === 'string') return { __mode: 'html', __html: json.html };
    if (Array.isArray(json.dates)) {
      // GS-25/28 shape: dates have status 'available' | 'full' | 'closed' | 'past' | 'blocked'.
      // Keep the raw status so the renderer can show why a date is unavailable.
      const map = {};
      json.dates.forEach(function (d) {
        if (d && typeof d.date === 'string') map[d.date] = typeof d.status === 'string' ? d.status : (d.status === true ? 'available' : 'closed');
      });
      return map;
    }
    if (json.dates && typeof json.dates === 'object') return json.dates;
    if (json.availability && typeof json.availability === 'object') return json.availability;
    const keys = Object.keys(json);
    if (keys.length && keys.every(function (k) { return /^\d{4}-\d{2}-\d{2}$/.test(k); })) return json;
  }
  if (typeof text === 'string' && text.trim()) {
    const t = text.trim();
    if (t.charAt(0) === '{') {
      try { return _gstParseCalendarDatesPayload('', JSON.parse(t), contentType); } catch (e) {}
    }
    if (t.charAt(0) !== '{') return { __mode: 'html', __html: text };
  }
  return {};
}

function _gstParseCalendarTimesPayload(text, json, contentType) {
  if (json && typeof json === 'object') {
    if (json.html != null && typeof json.html === 'string') return { __mode: 'html', __html: json.html };
    if (Array.isArray(json.slots)) {
      // GS-26 shape: { start_time, end_time, cabine_id }. Keep legacy { start, end } readable.
      return json.slots.map(function (s) {
        if (typeof s === 'string') return { time: s };
        return {
          time:     s.start_time || s.start || s.time || '',
          end:      s.end_time   || s.end   || '',
          cabineId: s.cabine_id != null ? s.cabine_id : (s.cabineId != null ? s.cabineId : null)
        };
      });
    }
    if (Array.isArray(json)) {
      if (json.length && typeof json[0] === 'string') return { __mode: 'html', __html: json[0] };
      return json;
    }
  }
  if (typeof text === 'string' && text.trim()) {
    const t = text.trim();
    if (t.charAt(0) === '[' || t.charAt(0) === '{') {
      try { return _gstParseCalendarTimesPayload('', JSON.parse(t), contentType); } catch (e) {}
    }
    return { __mode: 'html', __html: text };
  }
  return [];
}

// === Geocoding ================================================================

function _geocode(query) {
  const url = 'https://nominatim.openstreetmap.org/search?q=' +
      encodeURIComponent(query + ', España') +
      '&countrycodes=es&format=json&addressdetails=1&limit=1';
  return fetch(url)
      .then(function (res) { return res.ok ? res.json() : []; })
      .then(function (data) {
        if (!data.length) return null;
        const item = data[0];
        const addr = item.address || {};
        return {
          lat: parseFloat(item.lat),
          lng: parseFloat(item.lon),
          terms: _gstUniqueTerms([
            query, item.display_name,
            addr.city, addr.town, addr.village, addr.municipality,
            addr.county, addr.state, addr.province, addr.region, addr.postcode
          ])
        };
      })
      .catch(function () { return null; });
}

function _gstClinicMapQueries(clinicName, clinicAddress) {
  var candidates = [];
  var prefixRe = /^(?:cl[ií]nica|centro|grupostop)\s+/i;
  function add(q) {
    q = _gstStr(q);
    if (!q) return;
    if (candidates.indexOf(q) === -1) candidates.push(q);
  }
  function addStripped(q) {
    var stripped = _gstStr(q).replace(prefixRe, '');
    if (stripped && stripped.indexOf(' ') !== -1) add(stripped);
  }

  add(clinicAddress);
  add(clinicName);

  var name = _gstStr(clinicName);
  if (name) {
    var parts = name.split(/\s[-–—]\s/).map(_gstStr).filter(Boolean);
    if (parts.length > 1) add(parts[parts.length - 1]);
    addStripped(name);
    if (parts.length > 1) addStripped(parts[parts.length - 1]);
  }

  return candidates;
}

function _gstGeocodeClinic(clinicName, clinicAddress) {
  var queries = _gstClinicMapQueries(clinicName, clinicAddress);
  return queries.reduce(function (chain, query, idx) {
    return chain.then(function (hit) {
      if (hit) return hit;
      if (idx === 0) return _geocode(query);
      return new Promise(function (r) { setTimeout(r, 1100); }).then(function () {
        return _geocode(query);
      });
    });
  }, Promise.resolve(null));
}

// === Client resolve (T4) ======================================================

// Patient step hidden: the /clients/resolve preflight and flowww_client_id
// handling below are commented out (not deleted). Restore together with the
// patient-details step once the API no longer requires a client id.
/* --- BEGIN hidden client-resolve flow ---
// Builds the /clients/resolve body from current state + form values, or returns
// { error: 'missing_fields', missing: [...] } when required fields are absent.
function _gstBuildResolveBody(formValues) {
  let clinicId    = parseInt(GstBookingState.get('clinicId') || '0', 10);
  let storedId    = parseInt(GstBookingState.get('flowwwClientId') || '0', 10);
  let nombre      = _gstPatientField('firstName', formValues);
  let apellidos   = _gstPatientField('lastName',  formValues);
  let telefono    = _gstPatientField('phone',     formValues);
  let email       = _gstPatientField('email',     formValues);
  let productName = _gstStr(GstBookingState.get('productTitle')) || 'Unknown';

  var missing = [];
  if (!clinicId)  missing.push('clinic_id');
  if (!nombre)    missing.push('nombre');
  if (!apellidos) missing.push('apellidos');
  if (!telefono)  missing.push('telefono');
  if (!email)     missing.push('email');

  if (missing.length) return { error: 'missing_fields', missing: missing };

  var body = {
    clinic_id:    clinicId,
    nombre:       nombre,
    apellidos:    apellidos,
    telefono:     telefono,
    email:        email,
    product_name: productName,
  };
  if (storedId > 0) body.flowww_client_id = storedId;
  return { body: body };
}

// Calls /clients/resolve and stores the resulting flowww_client_id and new_customer
// flag (GS-21) in session state. Resolves with the parsed JSON, or null on failure.
// Bounded by a hard timeout so a slow Flowww call never freezes the UI.
var GST_RESOLVE_TIMEOUT_MS = 15000;

function _gstResolveFlowwwClient(formValues) {
  var built = _gstBuildResolveBody(formValues);
  if (built.error) {
    _gstDebugLog('resolve preflight failed', { missing: built.missing });
    return Promise.resolve(null);
  }

  var fetchPromise = fetch(_gstApiPath('/clients/resolve'), {
    method: 'POST',
    credentials: 'omit',
    headers: _gstJsonPostHeaders(),
    body: JSON.stringify(built.body),
  })
    .then(function (res) { return res.ok ? res.json() : null; })
    .then(function (data) {
      if (data && data.flowww_client_id) {
        GstBookingState.set('flowwwClientId', String(data.flowww_client_id));
      }
      // GS-21: API now returns new_customer (1 = newly created, 0 = existing).
      if (data && data.new_customer != null) {
        var nc = data.new_customer;
        GstBookingState.set('flowwwNewCustomer', (nc === 1 || nc === '1' || nc === true) ? '1' : '0');
      }
      return data;
    })
    .catch(function () { return null; });

  var timeoutPromise = new Promise(function (resolve) {
    setTimeout(function () {
      _gstDebugLog('resolve timed out', { ms: GST_RESOLVE_TIMEOUT_MS });
      resolve(null);
    }, GST_RESOLVE_TIMEOUT_MS);
  });

  return Promise.race([fetchPromise, timeoutPromise]);
}
--- END hidden client-resolve flow --- */

// === Provider =================================================================

// The Flowww client id used to let the backend enforce the laser hair-removal
// same-area interval (the "4 y 6 semanas" rule): when a client id is sent on
// /availability/dates, dates too close to the client's last session on the same
// zone come back as 'blocked'. Two sources, in priority order:
//   1. Customer-area rebook hand-off, seeded into GstBookingState from the
//      ?gst_rebook query params (portal flow).
//   2. The logged-in storefront customer's custom.flowww_id metafield, exposed
//      by theme.liquid as __GST_BOOKING__.customer.flowwwId. This makes the
//      SAME interval apply to normal product-page bookings whenever the shopper
//      is logged in — not just to portal rebookings.
// Empty string for guests, so the availability call stays unfiltered for them.
function _gstResolveAvailabilityClientId() {
  var seeded = _gstStr(GstBookingState.get('flowwwClientId'));
  if (seeded) return seeded;
  var customer = _gstBookingCfg().customer || {};
  return _gstStr(customer.flowwwId);
}

// Adds what the API needs to apply the client's booking window to dates AND
// slots: the client id (same-area interval, see above) and, on a portal rebook,
// the bono id so the calendar stops at the bono's expiry date. The API only
// honours the bono when it belongs to that client. No-op for guests.
function _gstSetBookingWindowParams(params) {
  var flowwwClientId = _gstResolveAvailabilityClientId();
  if (!flowwwClientId) return;
  params.set('flowww_client_id', flowwwClientId);
  var voucherId = _gstStr(GstBookingState.get('voucherId'));
  if (voucherId) params.set('voucher_id', voucherId);
}

const GstBookingProvider = {
  getAvailability: function (opts) {
    let params = _gstAvailabilityParams(false);
    params.set('month', opts.yearMonth);
    _gstSetBookingWindowParams(params);
    return _gstFetchGet(_gstApiPath('/availability/dates') + '?' + params.toString())
        .then(function (res) {
          let ct = res.headers.get('content-type') || '';
          if (ct.indexOf('application/json') !== -1) {
            return res.json().then(function (json) {
              let parsed = _gstParseCalendarDatesPayload('', json, ct);
              if (parsed && json && json.booking_window) parsed.__bookingWindow = json.booking_window;
              return parsed;
            });
          }
          return res.text().then(function (text) { return _gstParseCalendarDatesPayload(text, null, ct); });
        });
  },

  getSlots: function (opts) {
    // Patient step hidden: the 409 client_incomplete handling is commented out
    // with the rest of the client-id flow. Restore it with the patient-details step.
    let params = _gstAvailabilityParams(false);
    params.set('date', opts.date);
    _gstSetBookingWindowParams(params);
    return _gstFetchGet(_gstApiPath('/availability/slots') + '?' + params.toString())
        .then(function (res) {
          // if (res.status === 409) {
          //   GstBookingState.set('flowwwClientId', '');
          //   throw _gstBookingError('client_incomplete');
          // }
          // GS-26: 503 = Flowww upstream unavailable. Surface as recoverable.
          if (res.status === 503) {
            throw _gstBookingError('upstream_unavailable');
          }
          if (!res.ok) {
            // 4xx (other than 409) or 5xx — preserve the body for debugging.
            return res.text().then(function (body) {
              throw _gstBookingError('http_' + res.status, { body: body });
            });
          }
          let ct = res.headers.get('content-type') || '';
          if (ct.indexOf('application/json') !== -1) {
            return res.json().then(function (json) { return _gstParseCalendarTimesPayload('', json, ct); });
          }
          return res.text().then(function (text) { return _gstParseCalendarTimesPayload(text, null, ct); });
        })
        .then(function (parsed) {
          if (parsed && parsed.__mode === 'html') return parsed;
          if (!Array.isArray(parsed)) return [];
          return parsed.map(function (s) {
            if (typeof s === 'string') return { time: s, timecode: _formatSlotTimecode(opts.date, s) };
            let time   = s.time || s.start_time || s.start || s.label || '';
            let end    = s.end  || s.end_time   || '';
            let cabine = s.cabineId != null ? s.cabineId : (s.cabine_id != null ? s.cabine_id : null);
            return {
              time:     time,
              end:      end,
              cabineId: cabine,
              timecode: s.timecode || s.timeSlot || s.slotId || _formatSlotTimecode(opts.date, time)
            };
          });
        });
  },

  createBooking: function (opts) {
    // Patient step hidden: the /clients/resolve preflight and flowww_client_id
    // are no longer used. The booking is POSTed without a client id. Restore the
    // commented blocks together with the patient-details step.
    // let form = opts.patientData || {};
    //
    // var resolveBuilt = _gstBuildResolveBody(form);
    // if (resolveBuilt.error) {
    //   return Promise.reject(_gstBookingError('missing_fields', resolveBuilt.missing));
    // }

    var bookingsBuilt = _gstBuildBookingsPayload();
    if (bookingsBuilt.error) {
      return Promise.reject(_gstBookingError('missing_booking_fields', bookingsBuilt.missing));
    }

    var payload = bookingsBuilt.body;
    // payload.flowww_client_id = clientId;

    _gstDebugLog('bookings payload', {
      clinic_id:          payload.clinic_id,
      product_ids:        payload.product_ids,
      date:               payload.date,
      start_time:         payload.start_time,
      end_time:           payload.end_time,
      cabine_id:          payload.cabine_id,
      // flowww_client_id:   payload.flowww_client_id,
      booking_session_id: payload.booking_session_id,
    });

    return fetch(_gstApiPath('/bookings'), {
      method: 'POST',
      credentials: 'omit',
      headers: _gstJsonPostHeaders(),
      body: JSON.stringify(payload),
    })
        .then(function (res) {
          if (res.ok) return res.json();
          // Some API errors return JSON ({error: ...}); 422 returns {message, errors};
          // network proxies may return HTML. Treat all non-JSON bodies as opaque.
          var ct = res.headers.get('content-type') || '';
          var bodyPromise = ct.indexOf('application/json') !== -1
              ? res.json().catch(function () { return {}; })
              : res.text().then(function (t) { return { raw: t }; }, function () { return {}; });
          return bodyPromise.then(function (body) {
            // 409 client_incomplete handling commented out with the client-id flow.
            // if (res.status === 409 && body && body.error === 'client_incomplete') {
            //   GstBookingState.set('flowwwClientId', '');
            // }
            return Promise.reject(_gstBookingError('http_' + res.status, body));
          });
        })
        .then(function (data) {
          // Persist the canonical reference so a refresh of /pages/reservar-finalizar
          // does not duplicate the booking, and so cart line items can join on it.
          // We no longer prebook in Flowww here — the local API returns a
          // client_reference (UUID) that identifies the slot.
          if (data && data.client_reference) GstBookingState.set('clientReference', String(data.client_reference));

          // GS-160 — the slot is now BLOCKED in Flowww for hold_minutes, not booked. Both
          // values are persisted rather than passed along because the save step also renders
          // its message on the refresh path, which never sees this response again.
          GstBookingState.set('holdPlaced', data && data.hold_placed ? '1' : '0');
          if (data && data.hold_minutes) GstBookingState.set('holdMinutes', String(data.hold_minutes));

          var bookingId = data && data.client_reference != null ? data.client_reference : null;
          return {
            bookingId:       bookingId != null ? String(bookingId) : 'UNKNOWN',
            clientReference: data && data.client_reference,
            expiresAt:       data && data.expires_at,
            holdPlaced:      !!(data && data.hold_placed),
            holdMinutes:     data && data.hold_minutes,
          };
        });
  },

  createCustomerBooking: function () {
    var customer = (_gstBookingCfg().customer || {});
    var bookingsBuilt = _gstBuildBookingsPayload();
    var payload = bookingsBuilt.body || {};

    var missing = [];
    if (bookingsBuilt.error) missing = bookingsBuilt.missing || [];
    if (!customer.flowwwId) missing.push('flowww_client_id');
    if (missing.length) return Promise.reject(_gstBookingError('missing_booking_fields', missing));

    payload.flowww_client_id = parseInt(customer.flowwwId, 10);
    delete payload.booking_session_id;

    return fetch(_gstApiPath('/customer/bookings'), {
      method: 'POST',
      credentials: 'omit',
      headers: _gstJsonPostHeaders(),
      body: JSON.stringify(payload),
    }).then(function (res) {
      if (res.ok) return res.json();
      var ct = res.headers.get('content-type') || '';
      var bodyPromise = ct.indexOf('application/json') !== -1
          ? res.json().catch(function () { return {}; })
          : res.text().then(function (t) { return { raw: t }; }, function () { return {}; });
      return bodyPromise.then(function (body) {
        return Promise.reject(_gstBookingError('http_' + res.status, body));
      });
    });
  },
};

const GstProvider = GstBookingProvider;

// === DOM / format helpers =====================================================

const GST_ICONS = {
  pin: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"/></svg>',
  test: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714a2.25 2.25 0 001.5 2.121m-1.5-2.121L19 14.5"/></svg>',
  calendar: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5"/></svg>',
  person: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/></svg>',
  check: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
  tag: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z"/><path stroke-linecap="round" stroke-linejoin="round" d="M6 6h.008v.008H6V6z"/></svg>',
};

function _formatSlotTimecode(date, time) {
  return date + 'T' + time + ':00';
}

function _formatDisplayDate(isoDate) {
  let parts = isoDate.split('-').map(Number);
  let dt = new Date(parts[0], parts[1] - 1, parts[2]);
  return dt.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

function _gstShowSlotsTitle(show) {
  let t = _el('gst-slots-title');
  if (t) t.style.display = show ? 'block' : 'none';
}

let GST_PATIENT_KEYS = {
  firstName: ['gst-first-name', 'InputFName', 'firstName', 'first_name'],
  lastName:  ['gst-last-name',  'InputSName', 'lastName',  'last_name'],
  email:     ['gst-email',      'InputEmail', 'email'],
  phone:     ['gst-phone',      'InputPhone', 'phone', 'phoneNumber']
};

function _gstPatientField(field, values) {
  if (!values) return '';
  let keys = GST_PATIENT_KEYS[field];
  if (!keys) return '';
  for (let i = 0; i < keys.length; i++) {
    if (values[keys[i]] != null && values[keys[i]] !== '') return String(values[keys[i]]);
  }
  return '';
}

function _gstDeriveDob(values) {
  let dobKeys = ['gst-dob', 'bdayinput', 'dob', 'date_of_birth', 'dateOfBirth'];
  for (let i = 0; i < dobKeys.length; i++) {
    if (values[dobKeys[i]] && /^\d{4}-\d{2}-\d{2}$/.test(values[dobKeys[i]])) return values[dobKeys[i]];
  }
  let year  = values.bdayyear  || values.dob_year  || '';
  let month = values.bdaymonth || values.dob_month || '';
  let day   = values.bdayday   || values.dob_day   || '';
  if (year && month && day) {
    return String(year).padStart(4, '0') + '-' + String(month).padStart(2, '0') + '-' + String(day).padStart(2, '0');
  }
  return '';
}

function _isAdult(isoDate) {
  if (!isoDate) return false;
  let dob = new Date(isoDate + 'T00:00:00');
  if (isNaN(dob.getTime())) return false;
  let today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  let m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age >= 18;
}

function _gstShouldEnforceAdultCheck(form) {
  if (!form) return false;
  return !!(
      form.querySelector('#gst-dob, #bdayinput, #bdayday, #bdaymonth, #bdayyear') ||
      form.querySelector('[id*="dob" i], [name*="dob" i], [id*="dateofbirth" i], [name*="dateofbirth" i]')
  );
}

function _gstCheckoutPrefillEnabled(values) {
  let flag = '';
  let keys = ['gst-use-at-checkout', 'billingCheck', 'use_at_billing'];
  for (let i = 0; i < keys.length; i++) {
    if (values[keys[i]] != null && values[keys[i]] !== '') { flag = String(values[keys[i]]); break; }
  }
  return flag === 'on' || flag === 'yes' || flag === 'true' || flag === '1';
}

// True for a "0", "0,00 €", "0.00" style price string (no real amount).
function _gstIsZeroPriceText(t) {
  return /^[^0-9]*0+([.,]0+)?[^0-9]*$/.test(String(t == null ? '' : t).trim());
}

// The price the customer sees on the PDP, captured at booking handoff so the
// confirm step can show it. We read the theme's custom price block
// (#gpo-price-sale, populated by the PDP price engine / gpo-combo-qty total /
// fixed-price combos) and fall back to the native product price. Returns '' for
// missing/zero prices so the confirm row is simply omitted.
function _gstReadPdpPriceLabel() {
  try {
    var sale = document.getElementById('gpo-price-sale');
    var t = sale ? String(sale.textContent || '').trim() : '';
    if (t && !_gstIsZeroPriceText(t)) return t;
    var native = document.querySelector(
        '.product__block--price .price-item--sale, .product__block--price .price-item--regular, .price__container .price-item--sale, .price .price-item--sale');
    var nt = native ? String(native.textContent || '').trim() : '';
    if (nt && !_gstIsZeroPriceText(nt)) return nt;
  } catch (e) {}
  return '';
}

function _summaryRow(icon, title, lines) {
  let filtered = (lines || []).filter(Boolean);
  if (!title && !filtered.length) return '';
  return '<div class="gst-summary__row">' +
      '<span class="gst-summary__icon">' + icon + '</span>' +
      '<div class="gst-summary__detail">' +
      (title ? '<strong>' + _esc(title) + '</strong>' : '') +
      filtered.map(function (l) { return '<span>' + _esc(l) + '</span>'; }).join('') +
      '</div>' +
      '</div>';
}

// === Clinic Map (calendar step) ===============================================

var GST_CLINIC_LEAFLET_MAP = null;
var GST_LEAFLET_LOAD_PROMISE = null;

function _gstLoadLeaflet() {
  if (window.L && window.L.map) return Promise.resolve(window.L);
  if (GST_LEAFLET_LOAD_PROMISE) return GST_LEAFLET_LOAD_PROMISE;

  GST_LEAFLET_LOAD_PROMISE = new Promise(function (resolve, reject) {
    var existing = document.querySelector('script[src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"]');
    if (existing) {
      existing.addEventListener('load', function () { resolve(window.L); });
      existing.addEventListener('error', reject);
      return;
    }

    var script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = function () { resolve(window.L); };
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return GST_LEAFLET_LOAD_PROMISE;
}

function _gstPlotClinicLeafletMap(mapEl, lat, lng, popupLabel) {
  return _gstLoadLeaflet().then(function (L) {
    if (!L || !L.map) throw new Error('Leaflet failed to load');

    if (GST_CLINIC_LEAFLET_MAP && GST_CLINIC_LEAFLET_MAP.remove) {
      GST_CLINIC_LEAFLET_MAP.remove();
      GST_CLINIC_LEAFLET_MAP = null;
    }

    mapEl.innerHTML = '<div class="gst-clinic-map__leaflet" role="presentation"></div>';

    var mapNode = mapEl.querySelector('.gst-clinic-map__leaflet');
    var map = L.map(mapNode, {
      scrollWheelZoom: false,
      attributionControl: false
    }).setView([lat, lng], 15);

L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors',
  maxZoom: 19
}).addTo(map);

    L.marker([lat, lng]).addTo(map).bindPopup(_esc(popupLabel));
    GST_CLINIC_LEAFLET_MAP = map;
    setTimeout(function () { map.invalidateSize(); }, 0);
  });
}

function _gstRenderClinicMap(clinicName, clinicAddress, clinicId) {
  var mapEl = _el('gst-clinic-map');
  if (!mapEl) return;

  var coords = _gstClinicCoordsById(clinicId);
  var queries = _gstClinicMapQueries(clinicName, clinicAddress);
  var query = queries[0] || '';
  var searchUrl = 'https://www.openstreetmap.org/search?query=' + encodeURIComponent(query || '');
  function renderFallback(msg) {
    mapEl.innerHTML =
        '<div class="gst-clinic-map__fallback">' +
        '<p class="gst-clinic-map__fallback-text">' + _esc(msg) + '</p>' +
        (query
            ? '<a class="gst-clinic-map__fallback-link" href="' + _esc(searchUrl) + '" target="_blank" rel="noopener noreferrer">Ver ubicación en el mapa</a>'
            : '') +
        '</div>';
  }

  if (!query && !(coords && isFinite(coords.lat) && isFinite(coords.lng))) {
    renderFallback('No hay dirección de clínica para mostrar el mapa.');
    return;
  }

  mapEl.innerHTML = '<div class="gst-clinic-map__loading"><div class="gst-spinner"><span></span><span></span><span></span></div></div>';

  if (coords && isFinite(coords.lat) && isFinite(coords.lng)) {
    _gstPlotClinicLeafletMap(mapEl, coords.lat, coords.lng, clinicName || query)
        .catch(function () { renderFallback('No se pudo cargar el mapa de esta clínica.'); });
    return;
  }

  _gstGeocodeClinic(clinicName, clinicAddress).then(function (result) {
    if (!result || !isFinite(result.lat) || !isFinite(result.lng)) {
      renderFallback('No se pudo cargar el mapa de esta clínica.');
      return;
    }

    return _gstPlotClinicLeafletMap(mapEl, result.lat, result.lng, clinicName || query);
  }).catch(function () {
    renderFallback('No se pudo cargar el mapa de esta clínica.');
  });
}

// === Step 2: Calendar (shown after patient details) ===========================

function _gstCalendarSetContinueEnabled(on) {
  let b = _el('gst-calendar-continue');
  if (b) b.disabled = !on;
}

function _gstCalendarSyncContinueFromState() {
  _gstCalendarSetContinueEnabled(!!GstBookingState.get('slotTimecode'));
}

document.addEventListener('DOMContentLoaded', function () {
  let monthSelect = _el('gst-month-select');
  if (!monthSelect) return;

  let clinicName    = GstBookingState.get('clinicName');
  let clinicAddress = GstBookingState.get('clinicAddress');
  let clinicId      = GstBookingState.get('clinicId');
  if (!clinicAddress && clinicId) {
    clinicAddress = _gstFormatClinicAddressFromCoords(_gstClinicCoordsById(clinicId));
  }
  let titleEl = _el('gst-calendar-subtitle');
  let parentProductId = GstBookingState.get('parentProductId');

  if (parentProductId) {
    let parts = [];
    let productTitle = GstBookingState.get('productTitle') || '';
    let sessions     = GstBookingState.get('sessionsCount') || '';
    let zones        = GstBookingState.getJson('zones') || [];
    if (clinicName)   parts.push(_esc(clinicName));
    if (sessions)     parts.push('Sesiones: ' + _esc(sessions));
    if (zones.length) parts.push('Zonas: ' + zones.map(_esc).join(', '));
    if (titleEl) {
      titleEl.innerHTML = (productTitle ? '<strong>' + _esc(productTitle) + '</strong> — ' : '') + parts.join(' · ');
    }
  } else {
    if (titleEl && clinicName) titleEl.textContent = clinicName;
  }

  _gstRenderClinicMap(clinicName, clinicAddress, clinicId);

  // "Volver" now returns to the originating PDP (the patient-details step is
  // hidden). Fall back to the home page if no product handle is stored.
  let backLink = _el('gst-calendar-back-to-product');
  if (backLink) {
    let ph = GstBookingState.get('productHandle') || '';
    backLink.href = ph ? '/products/' + encodeURIComponent(ph) : '/';
  }

  let continueBtn = _el('gst-calendar-continue');
  if (continueBtn) {
    continueBtn.addEventListener('click', function () {
      if (!GstBookingState.get('slotTimecode')) return;
      window.location.href = '/pages/reservar-confirmar';
    });
  }
  _gstCalendarSyncContinueFromState();

  let nowYm = _gstYearMonth(new Date());
  _gstFillMonthOptions(monthSelect, nowYm, _gstAddMonths(nowYm, 3));

  monthSelect.addEventListener('change', function () {
    _gstMonthAutoPick = false;
    _loadMonth();
  });
  _updateMonthNavButtons();

  let prev = _el('gst-month-prev');
  let next = _el('gst-month-next');
  if (prev) prev.addEventListener('click', function () {
    if (monthSelect.selectedIndex > 0) { monthSelect.selectedIndex--; monthSelect.dispatchEvent(new Event('change')); }
  });
  if (next) next.addEventListener('click', function () {
    if (monthSelect.selectedIndex < monthSelect.options.length - 1) { monthSelect.selectedIndex++; monthSelect.dispatchEvent(new Event('change')); }
  });

  _loadMonth();
});

// Until the customer picks a month themselves, the calendar moves to the first
// month of their booking window that has a free day.
let _gstMonthAutoPick = true;

function _gstYearMonth(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}

function _gstAddMonths(yearMonth, n) {
  let parts = yearMonth.split('-').map(Number);
  return _gstYearMonth(new Date(parts[0], parts[1] - 1 + n, 1));
}

// ISO Y-m-d → DD/MM/YYYY.
function _gstFormatDmy(isoDate) {
  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(_gstStr(isoDate));
  return m ? m[3] + '/' + m[2] + '/' + m[1] : _gstStr(isoDate);
}

// One option per month from fromYm to toYm (inclusive). Keeps the selected
// month when it is still in range, otherwise selects the first one.
function _gstFillMonthOptions(monthSelect, fromYm, toYm) {
  let selected = monthSelect.value;
  monthSelect.innerHTML = '';
  for (let ym = fromYm, n = 0; ym <= toYm && n < 24; ym = _gstAddMonths(ym, 1), n++) {
    let parts = ym.split('-').map(Number);
    let label = new Date(parts[0], parts[1] - 1, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    let opt = document.createElement('option');
    opt.value = ym;
    opt.textContent = label.charAt(0).toUpperCase() + label.slice(1);
    monthSelect.appendChild(opt);
  }
  if (selected) monthSelect.value = selected;
  if (monthSelect.selectedIndex < 0) monthSelect.selectedIndex = 0;
}

// Narrow the month dropdown to the months of the API's booking_window (e.g. a
// 2nd laser session that can't be booked before the same-area interval ends, or
// a bono that expires before the usual 90 days). Returns true when the shown
// month had to change, so the caller reloads instead of rendering.
function _gstApplyBookingWindowToMonths(win) {
  let monthSelect = _el('gst-month-select');
  if (!monthSelect || !win || !win.earliest) return false;

  let nowYm = _gstYearMonth(new Date());
  let isYmd = function (v) { return /^\d{4}-\d{2}-\d{2}$/.test(_gstStr(v)); };
  if (!isYmd(win.earliest) || (win.latest && !isYmd(win.latest))) return false;

  let fromYm = win.earliest.slice(0, 7);
  if (fromYm < nowYm) fromYm = nowYm;
  let toYm = win.latest ? win.latest.slice(0, 7) : _gstAddMonths(fromYm, 3);
  if (toYm < fromYm) toYm = fromYm;

  let current = monthSelect.value;
  let options = monthSelect.options;
  if (!options.length || options[0].value !== fromYm || options[options.length - 1].value !== toYm) {
    _gstFillMonthOptions(monthSelect, fromYm, toYm);
  }
  _updateMonthNavButtons();
  return monthSelect.value !== current;
}

// Explain a booking window that is narrower than the usual 48h–90 days.
function _gstRenderBookingWindowNote(win) {
  let el = _el('gst-calendar-window-note');
  if (!el) return;

  let text = '';
  if (win && win.earliest && win.latest && win.earliest > win.latest) {
    text = win.latest_reason === 'voucher_expiry'
        ? 'No quedan fechas para reservar antes de que caduque tu bono (' + _gstFormatDmy(win.latest) + '). Contacta con tu centro y te ayudaremos.'
        : 'La próxima sesión de esta zona podrá reservarse a partir del ' + _gstFormatDmy(win.earliest) + '.';
  } else if (win) {
    let parts = [];
    if (win.earliest_reason === 'interval') {
      parts.push('Para respetar el tiempo entre sesiones de esta zona, puedes reservar a partir del ' + _gstFormatDmy(win.earliest) + '.');
    }
    if (win.latest_reason === 'voucher_expiry') {
      parts.push('Tu bono caduca el ' + _gstFormatDmy(win.latest) + ', así que puedes reservar hasta ese día.');
    }
    text = parts.join(' ');
  }

  el.textContent = text;
  el.hidden = !text;
}

// Tooltip for a greyed-out day, matching the limit that blocked it.
function _gstBlockedDayTitle(dateStr, win) {
  let reason = 'minimum_advance';
  if (win && win.earliest && dateStr < win.earliest && win.earliest_reason) reason = win.earliest_reason;
  else if (win && win.latest && dateStr > win.latest && win.latest_reason) reason = win.latest_reason;

  switch (reason) {
    case 'interval':        return 'Aún no ha pasado el tiempo mínimo entre sesiones de esta zona';
    case 'voucher_expiry':  return 'Tu bono habrá caducado en esta fecha';
    case 'maximum_advance': return 'Fuera del periodo de reserva';
    default:                return 'Reservas con antelación mínima';
  }
}

function _updateMonthNavButtons() {
  let monthSelect = _el('gst-month-select');
  let prev = _el('gst-month-prev');
  let next = _el('gst-month-next');
  if (!monthSelect) return;
  if (prev) prev.disabled = monthSelect.selectedIndex === 0;
  if (next) next.disabled = monthSelect.selectedIndex === monthSelect.options.length - 1;
}

function _gstBindCalendarHtmlEvents(calBody) {
  calBody.addEventListener('click', function (e) {
    let cell = e.target.closest('.calendar_status_open, [data-date]');
    if (!cell) return;
    let date = cell.getAttribute('data-date');
    if (!date) return;
    calBody.querySelectorAll('.active_date').forEach(function (x) { x.classList.remove('active_date'); });
    cell.classList.add('active_date');
    _loadTimeSlots(date);
  });
}

function _loadMonth() {
  let monthSelect = _el('gst-month-select');
  if (!monthSelect) return;

  _updateMonthNavButtons();
  let yearMonth = monthSelect.value;
  let calBody = _el('gst-calendar-body');
  if (calBody) calBody.innerHTML = '<tr><td colspan="7" class="gst-calendar__loading"><div class="gst-spinner"><span></span><span></span><span></span></div></td></tr>';

  let clinicId  = GstBookingState.get('clinicId') || '';
  let productId = GstBookingState.get('parentProductId') || GstBookingState.get('productId') || '';

  if (_gstUseBookingApi() && (!clinicId || !productId)) {
    let productHandle = GstBookingState.get('productHandle') || '';
    let productHref = productHandle ? '/products/' + encodeURIComponent(productHandle) : '/';
    if (calBody) calBody.innerHTML = '<tr><td colspan="7" class="gst-calendar__no-avail">Falta clínica o producto. <a href="' + _esc(productHref) + '">Vuelve al producto</a> y selecciona una clínica.</td></tr>';
    _gstCalendarSetContinueEnabled(false);
    return;
  }

  GstProvider.getAvailability({
    clinicId:   clinicId,
    calendarId: GstBookingState.get('calendarId') || '',
    productId:  GstBookingState.get('productId') || '',
    yearMonth:  yearMonth
  })
      .then(function (avail) {
        // The customer moved to another month while this one was loading.
        if (monthSelect.value !== yearMonth) return;

        if (avail && avail.__mode === 'html') {
          if (calBody) { calBody.innerHTML = avail.__html; _gstBindCalendarHtmlEvents(calBody); }
          _gstShowSlotsTitle(false);
          let timesEl = _el('gst-times');
          if (timesEl) timesEl.innerHTML = '';
          return;
        }

        let win = avail && avail.__bookingWindow;
        _gstRenderBookingWindowNote(win);
        if (_gstApplyBookingWindowToMonths(win)) {
          _loadMonth();
          return;
        }

        _renderCalendar(yearMonth, avail || {});

        if (_gstMonthAutoPick) {
          let hasFreeDay = Object.keys(avail || {}).some(function (k) { return avail[k] === 'available' || avail[k] === true; });
          if (!hasFreeDay && monthSelect.selectedIndex < monthSelect.options.length - 1) {
            monthSelect.selectedIndex++;
            _loadMonth();
            return;
          }
          _gstMonthAutoPick = false;
        }
      })
      .catch(function () {
        if (calBody) calBody.innerHTML = '<tr><td colspan="7" class="gst-calendar__no-avail">No hay disponibilidad para este mes.</td></tr>';
      });
}

function _renderCalendar(yearMonth, avail) {
  let parts = yearMonth.split('-').map(Number);
  let year = parts[0], month = parts[1];
  let calBody = _el('gst-calendar-body');
  if (!calBody) return;

  let daysInMonth = new Date(year, month, 0).getDate();
  let startDow = new Date(year, month - 1, 1).getDay() - 1;
  if (startDow < 0) startDow = 6;

  let cells = [];
  for (let i = 0; i < startDow; i++) cells.push('<td class="gst-calendar__cell gst-calendar__cell--empty"></td>');

  for (let d = 1; d <= daysInMonth; d++) {
    let dateStr = year + '-' + String(month).padStart(2, '0') + '-' + String(d).padStart(2, '0');
    let status  = avail[dateStr];
    let isAvail = status === true || status === 'available';
    if (isAvail) {
      cells.push('<td class="gst-calendar__cell gst-calendar__cell--available"><button type="button" class="gst-calendar__day gst-calendar__day--open" data-date="' + dateStr + '" aria-label="Seleccionar ' + dateStr + '">' + d + '</button></td>');
    } else {
      // 'blocked' = outside the client's booking window (advance notice, same-area
      // interval, bono expiry, 90-day limit). Surface which one so the user understands.
      let isBlocked = status === 'blocked';
      let cellCls   = 'gst-calendar__cell gst-calendar__cell--unavailable' + (isBlocked ? ' gst-calendar__cell--blocked' : '');
      let dayCls    = 'gst-calendar__day gst-calendar__day--closed' + (isBlocked ? ' gst-calendar__day--blocked' : '');
      let title     = isBlocked ? ' title="' + _esc(_gstBlockedDayTitle(dateStr, avail.__bookingWindow)) + '"' : '';
      cells.push('<td class="' + cellCls + '"><span class="' + dayCls + '" aria-disabled="true"' + title + '>' + d + '</span></td>');
    }
  }

  while (cells.length % 7 !== 0) cells.push('<td class="gst-calendar__cell gst-calendar__cell--empty"></td>');

  let rows = [];
  for (let r = 0; r < cells.length; r += 7) rows.push('<tr>' + cells.slice(r, r + 7).join('') + '</tr>');
  calBody.innerHTML = rows.join('');

  calBody.querySelectorAll('.gst-calendar__day--open').forEach(function (btn) {
    btn.addEventListener('click', function () {
      calBody.querySelectorAll('.gst-calendar__day--open').forEach(function (b) { b.classList.remove('gst-calendar__day--selected'); });
      btn.classList.add('gst-calendar__day--selected');
      _loadTimeSlots(btn.dataset.date);
    });
  });
}

function _gstBindTimesHtmlEvents(timesEl, date) {
  timesEl.addEventListener('click', function (e) {
    let el = e.target.closest('[data-gst-timecode],[data-timecode]');
    if (!el) return;
    let tc = el.getAttribute('data-gst-timecode') || el.getAttribute('data-timecode');
    let tm = el.getAttribute('data-gst-time') || el.getAttribute('data-time') || '';
    let en = el.getAttribute('data-gst-end')  || el.getAttribute('data-end')  || '';
    let cb = el.getAttribute('data-gst-cabine-id') || el.getAttribute('data-cabine-id') || '';
    if (!tc) return;
    timesEl.querySelectorAll('.gst-slot-btn--selected').forEach(function (node) {
      node.classList.remove('gst-slot-btn--selected');
    });
    if (el.classList) el.classList.add('gst-slot-btn--selected');
    GstBookingState.set('slotDate',     date);
    GstBookingState.set('slotTime',     tm);
    GstBookingState.set('slotEndTime',  en);
    GstBookingState.set('cabineId',     cb);
    GstBookingState.set('slotTimecode', tc);
    _gstCalendarSetContinueEnabled(true);
  });
}

window.get_patient_page = function (time, timecode) {
  let date = timecode && timecode.indexOf('T') !== -1 ? timecode.split('T')[0] : '';
  if (!date) {
    let active = document.querySelector('.active_date[data-date]');
    if (active) date = active.getAttribute('data-date') || '';
  }
  GstBookingState.set('slotDate', date);
  GstBookingState.set('slotTime', time);
  GstBookingState.set('slotTimecode', timecode);
  window.location.href = '/pages/reservar-confirmar';
};

function _loadTimeSlots(date) {
  GstBookingState.set('slotDate', '');
  GstBookingState.set('slotTime', '');
  GstBookingState.set('slotTimecode', '');
  _gstCalendarSetContinueEnabled(false);

  let timesEl = _el('gst-times');
  if (!timesEl) return;
  timesEl.innerHTML = '<div class="gst-spinner"><span></span><span></span><span></span></div>';
  timesEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  _gstShowSlotsTitle(true);

  GstProvider.getSlots({
    clinicId:   GstBookingState.get('clinicId') || '',
    calendarId: GstBookingState.get('calendarId') || '',
    productId:  GstBookingState.get('productId') || '',
    date:       date
  })
      .then(function (slots) {
        if (slots && slots.__mode === 'html') {
          timesEl.innerHTML = slots.__html;
          _gstBindTimesHtmlEvents(timesEl, date);
          return;
        }
        _renderSlots(date, slots);
      })
      .catch(function (err) {
        var msg;
        // Patient step hidden: the client_incomplete branch is commented out with
        // the client-id flow. Restore together with the patient-details step.
        // if (err && err.code === 'client_incomplete') {
        //   msg = 'No pudimos validar tus datos para reservar. Revisa la información e ' +
        //         '<a href="/pages/reservar-fecha">vuelve al formulario</a>.';
        // } else
        if (err && err.code === 'upstream_unavailable') {
          msg = 'El sistema de reservas no está disponible en este momento. Inténtalo de nuevo en unos minutos.';
        } else {
          msg = 'No se pudieron cargar los horarios.';
        }
        timesEl.innerHTML = '<p class="gst-alert gst-alert--error">' + msg + '</p>';
      });
}

function _renderSlots(date, slots) {
  let timesEl = _el('gst-times');
  if (!timesEl) return;

  if (!slots || slots.length === 0) {
    timesEl.innerHTML = '<p class="gst-no-results">No hay horarios disponibles para este día.</p>';
    return;
  }

  timesEl.innerHTML = '<div class="gst-slots">' +
      slots.map(function (slot) {
        let time   = slot.time || '';
        let end    = slot.end  || '';
        // cabine_id from the API is an integer; coerce defensively so we never
        // round-trip an unexpected string into the booking payload.
        let cabineN = parseInt(slot.cabineId, 10);
        let cabine  = isFinite(cabineN) && cabineN > 0 ? String(cabineN) : '';
        let tc      = slot.timecode || _formatSlotTimecode(date, time);
        return '<button type="button" class="gst-slot-btn"' +
            ' data-time="'      + _esc(time)   + '"' +
            ' data-end="'       + _esc(end)    + '"' +
            ' data-cabine-id="' + _esc(cabine) + '"' +
            ' data-timecode="'  + _esc(tc)     + '">' +
            _esc(time) + '</button>';
      }).join('') +
      '</div>';

  timesEl.querySelectorAll('.gst-slot-btn').forEach(function (btn) {
    btn.addEventListener('click', function () {
      timesEl.querySelectorAll('.gst-slot-btn').forEach(function (b) { b.classList.remove('gst-slot-btn--selected'); });
      btn.classList.add('gst-slot-btn--selected');
      GstBookingState.set('slotDate',     date);
      GstBookingState.set('slotTime',     btn.dataset.time || '');
      GstBookingState.set('slotEndTime',  btn.dataset.end  || '');
      GstBookingState.set('cabineId',     btn.dataset.cabineId || '');
      GstBookingState.set('slotTimecode', btn.dataset.timecode || '');
      _gstCalendarSetContinueEnabled(true);
    });
  });
}

// === Step 1: Patient Details (HIDDEN) =========================================
// The patient-details step is temporarily removed: the bookable CTA now routes
// straight to the calendar, and customer details will be gathered by a future
// checkout block. Everything below is commented out (not deleted) so the step
// can be restored as-is. _gstPatientField / GST_PATIENT_KEYS are left in place
// (harmless, reused on re-enable).
/* --- BEGIN hidden patient-details step ---
function _gstFindPatientForm() {
  return _el('gst-patient-form') || _el('patient-details');
}

function _gstCollectFormValues(form) {
  let values = {};
  if (!form) return values;
  form.querySelectorAll('input, select, textarea').forEach(function (el) {
    let key = el.id || el.name;
    if (!key) return;
    if (el.type === 'checkbox')   values[key] = el.checked ? 'on' : 'off';
    else if (el.type === 'radio') { if (el.checked) values[el.name] = el.value; }
    else                          values[key] = el.value;
  });
  return values;
}

function _gstRestoreFormValues(form, saved) {
  if (!form || !saved) return;
  form.querySelectorAll('input, select, textarea').forEach(function (el) {
    let key = el.id || el.name;
    let val = saved[key];
    if (val === undefined) return;
    if (el.type === 'checkbox')   el.checked = val === true || val === 'on' || val === 'true' || val === 'yes';
    else if (el.type === 'radio') { if (el.name && saved[el.name] != null) el.checked = el.value === saved[el.name]; }
    else                          el.value = val;
  });
}

function _gstBindPatientForm(form) {
  if (!form || form.dataset.gstBound === 'true') return;
  form.dataset.gstBound = 'true';
  _gstRestoreFormValues(form, GstBookingState.getJson('formValues'));
  form.addEventListener('submit', function (e) {
    e.preventDefault();
    _submitPatientForm(form);
  });
}

document.addEventListener('DOMContentLoaded', function () {
  let shell = _el('gst-patient-form-shell');
  if (!shell) return;

  let backToProduct = _el('gst-patient-back-to-product');
  if (backToProduct) {
    let ph = GstBookingState.get('productHandle') || '';
    backToProduct.href = ph ? ('/products/' + encodeURIComponent(ph)) : '/';
  }

  _gstBindPatientForm(_gstFindPatientForm());
});

function _submitPatientForm(form) {
  if (!form) return false;
  if (form.dataset.gstSubmitting === 'true') return false;
  if (typeof form.reportValidity === 'function' && !form.reportValidity()) return false;

  let values = _gstCollectFormValues(form);

  if (_gstShouldEnforceAdultCheck(form)) {
    let dob = _gstDeriveDob(values);
    if (!dob || !_isAdult(dob)) {
      alert('La reserva solo está disponible para mayores de 18 años.');
      return false;
    }
    values.bdayinput = dob;
    if (!values['gst-dob']) values['gst-dob'] = dob;
  }

  GstBookingState.setJson('formValues', values);

  // Disable the submit button and show a busy state while resolve is in flight.
  // _gstResolveFlowwwClient is internally bounded by GST_RESOLVE_TIMEOUT_MS, so this
  // can never hang indefinitely. We always navigate, even on resolve failure: getSlots
  // will surface a recoverable 409 if flowww_client_id is missing.
  form.dataset.gstSubmitting = 'true';
  var submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
  var originalLabel = null;
  if (submitBtn) {
    originalLabel = submitBtn.textContent;
    submitBtn.disabled = true;
    if (submitBtn.tagName === 'BUTTON') submitBtn.textContent = 'Procesando…';
  }

  _gstResolveFlowwwClient(values).then(function () {
    window.location.href = '/pages/reservar-fecha';
  }).catch(function () {
    // Belt-and-suspenders: _gstResolveFlowwwClient never rejects today, but
    // restore the form if that ever changes so the user is not stuck.
    form.dataset.gstSubmitting = 'false';
    if (submitBtn) {
      submitBtn.disabled = false;
      if (originalLabel != null && submitBtn.tagName === 'BUTTON') submitBtn.textContent = originalLabel;
    }
  });
  return false;
}

window.submit_patient_form = function () {
  return _submitPatientForm(_gstFindPatientForm());
};
--- END hidden patient-details step --- */

// === Step 3: Confirm ==========================================================

document.addEventListener('DOMContentLoaded', function () {
  let summaryEl = _el('gst-booking-summary');
  if (!summaryEl) return;

  _renderBookingSummary(summaryEl);
  
  let meNote = _el('gst-me-note');
  if (meNote && GstBookingState.get('me') === '1') meNote.hidden = false;

  let confirmBtn = _el('gst-confirm-btn');
  if (confirmBtn) {
   if (GstBookingState.get('me') === '1') {
      var meLabel = confirmBtn.getAttribute('data-me-label');
      if (meLabel) confirmBtn.textContent = meLabel;
    }
    confirmBtn.addEventListener('click', function () {
      confirmBtn.disabled = true;
      confirmBtn.textContent = 'Procesando…';
      window.location.href = '/pages/reservar-finalizar';
    });
  }
});

function _renderBookingSummary(container) {
  let productTitle  = GstBookingState.get('productTitle')  || 'Servicio';
  let clinicName    = GstBookingState.get('clinicName')    || '';
  let clinicAddress = GstBookingState.get('clinicAddress') || '';
  let slotDate      = GstBookingState.get('slotDate')      || '';
  let slotTime      = GstBookingState.get('slotTime')      || '';
  let formValues    = GstBookingState.getJson('formValues') || {};
  let sessionsLabel = GstBookingState.get('sessionsLabel') || GstBookingState.get('sessionsCount') || '';
  let zones         = GstBookingState.getJson('zones') || [];
  let priceLabel    = GstBookingState.get('priceLabel') || '';

  // Patient step hidden: patient name/email/phone are no longer collected here
  // (a future checkout block will capture them). Restore with the patient step.
  // let patientName  = [_gstPatientField('firstName', formValues), _gstPatientField('lastName', formValues)].filter(Boolean).join(' ');
  // let patientEmail = _gstPatientField('email', formValues);
  // let patientPhone = _gstPatientField('phone', formValues);
  let displayDate  = slotDate ? _formatDisplayDate(slotDate) : '';

  // Surface Sesiones/Zonas next to the service title so the user can verify
  // their body-part and session-count selection on the final confirm step.
  // Falsy values stay omitted because `_summaryRow` filters them out.
  let productLines = ['Consulta en clínica'];
  if (sessionsLabel)             productLines.push('Sesiones: ' + sessionsLabel);
  if (Array.isArray(zones) && zones.length) productLines.push('Zonas: ' + zones.join(', '));

  container.innerHTML = '<div class="gst-summary">' +
      _summaryRow(GST_ICONS.test,     productTitle, productLines) +
      _summaryRow(GST_ICONS.pin,      clinicName,   [clinicAddress]) +
      _summaryRow(GST_ICONS.calendar, displayDate,  [slotTime ? 'A las ' + slotTime : '']) +
      (priceLabel ? _summaryRow(GST_ICONS.tag, priceLabel, ['Precio total (impuestos incluidos)']) : '') +
      // _summaryRow(GST_ICONS.person,   patientName,  [patientEmail, patientPhone]) +
      '</div>';
}

// === Step 4: Save =============================================================

// Module-level guard so a stray re-entry (e.g., DOMContentLoaded firing twice)
// cannot trigger two parallel /bookings POSTs from the same page load.
var _gstBookingInFlight = false;

document.addEventListener('DOMContentLoaded', function () {
  let saveEl = _el('gst-booking-save');
  if (!saveEl) return;
  _submitBooking();
});

// GS-160 — the slot is HELD, not confirmed. Nothing exists in Flowww's diary at this point
// but a temporary block; the appointment is created when the payment webhook lands. Telling
// the customer their booking was confirmed here is what made a lapsed hold feel like a
// cancelled appointment.
//
// The duration comes from the API's hold_minutes, never from a constant in this file: the
// hold, the booking expiry and this message are one value on the server
// (FLOWWW_PENDING_BOOKING_TTL), and reading it back is what keeps them from drifting.
function _gstHoldMessage() {
  var minutes = parseInt(GstBookingState.get('holdMinutes') || '0', 10);

  if (GstBookingState.get('holdPlaced') === '1' && minutes > 0) {
    return '¡Reserva bloqueada durante ' + minutes + ' minutos! Completa el pago para confirmar tu cita.';
  }

  // No block was placed — the feature is off, or Flowww refused. The slot is chosen but
  // nothing is holding it, so promise neither a confirmation nor a duration.
  return '¡Hueco seleccionado! Completa el pago para confirmar tu cita.';
}

function _renderSaveSuccess(statusEl) {
  if (!statusEl) return;
  statusEl.innerHTML =
      '<div class="gst-save-success">' + GST_ICONS.check +
      '<p>' + _gstHoldMessage() + '</p>' +
      '<p class="gst-save-success__redirect">Redirigiendo al pago…</p>' +
      '</div>';
}

function _renderRebookSuccess(statusEl) {
  if (!statusEl) return;
  statusEl.innerHTML =
      '<div class="gst-save-success">' + GST_ICONS.check +
      '<p>¡Cita confirmada! Volviendo a tus pedidos…</p>' +
      '</div>';
}

function _gstIsRebookFlow() {
  try { return sessionStorage.getItem('gst_rebook') === '1'; } catch (e) { return false; }
}

function _gstClearRebookState() {
  try {
    sessionStorage.removeItem('gst_rebook');
    sessionStorage.removeItem('gst_rebook_voucher_id');
  } catch (e) {}
}

// === Booking journey (GTM) ====================================================
// Cart attribute `booking_journey` tells GTM which button led to checkout:
// "Pide tu cita y Compra" → point_of_purchase, "Compra ahora, reserva más
// tarde" → book_later. The last journey used overwrites the attribute.
//
// Tracking must never block a sale: the promise always resolves (true/false)
// and gives up after GST_JOURNEY_TIMEOUT_MS so a slow /cart/update.js cannot
// hold the customer on the page.
var GST_VALID_JOURNEYS = ['point_of_purchase', 'book_later'];
var GST_JOURNEY_TIMEOUT_MS = 2500;

function _gstSetBookingJourney(journey) {
  if (GST_VALID_JOURNEYS.indexOf(journey) === -1) {
    _gstDebugLog('journey: invalid value', { journey: journey });
    return Promise.resolve(false);
  }

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var request = fetch(root + 'cart/update.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ attributes: { booking_journey: journey } })
  }).then(function (res) {
    if (!res.ok) throw new Error('cart/update.js ' + res.status);
    _gstDebugLog('journey: saved', { journey: journey });
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('grupostop:booking_journey_selected', { booking_journey: journey });
      }
    } catch (e) {}
    return true;
  }).catch(function (err) {
    _gstDebugLog('journey: save failed', { journey: journey, err: String(err && err.message ? err.message : err) });
    return false;
  });

  var timeout = new Promise(function (resolve) {
    setTimeout(function () { resolve(false); }, GST_JOURNEY_TIMEOUT_MS);
  });

  return Promise.race([request, timeout]);
}

try { window.GrupostopBookingJourney = { set: _gstSetBookingJourney }; } catch (e) {}

function _submitBooking() {
  if (_gstBookingInFlight) return;
  _gstBookingInFlight = true;

  let statusEl   = _el('gst-save-status');
  let formValues = GstBookingState.getJson('formValues') || {};
  let variantId  = GstBookingState.get('variantId');

  if (_gstIsRebookFlow()) {
    GstProvider.createCustomerBooking()
        .then(function () {
          _gstClearRebookState();
          _renderRebookSuccess(statusEl);
          setTimeout(function () { window.location.href = '/account'; }, 1500);
        })
        .catch(function (err) {
          _gstBookingInFlight = false;
          console.error('[GST Booking] createCustomerBooking error', err);
          if (statusEl) {
            var customMsg = _gstBookingFailureMessage(err);
            statusEl.innerHTML =
                '<div class="gst-alert gst-alert--error">' +
                '<p>' + (customMsg ||
                    'Ocurrió un error al confirmar la cita. Por favor, <a href="/pages/reservar-fecha">inténtalo de nuevo</a>.') +
                '</p>' +
                '</div>';
          }
        });
    return;
  }

  // Idempotency: a successful booking persists clientReference in session.
  // If the user refreshes /pages/reservar-finalizar, do NOT re-POST to /bookings —
  // that would create a duplicate booking. Continue to checkout.
  let priorClientRef = _gstStr(GstBookingState.get('clientReference'));
  if (priorClientRef) {
    _gstDebugLog('save step: reusing existing booking', { clientReference: priorClientRef });
    _renderSaveSuccess(statusEl);
    let qs = _buildCheckoutQueryString(formValues);
    _gstSetBookingJourney('point_of_purchase').then(function () {
      setTimeout(function () { window.location.href = '/checkout' + qs; }, 1000);
    });
    return;
  }

  GstProvider.createBooking({
    clinicId:     GstBookingState.get('clinicId'),
    productId:    GstBookingState.get('productId') || '',
    slotTimecode: GstBookingState.get('slotTimecode'),
    patientData:  formValues
  })
      .then(function (result) {
        let cartPromise = variantId ? _addToCart(variantId, result.bookingId, formValues) : Promise.resolve();
        return cartPromise.then(function () { return result; });
      })
      .then(function () {
        // After /cart/add.js: two concurrent cart writes can drop one of them.
        return _gstSetBookingJourney('point_of_purchase');
      })
      .then(function () {
        _renderSaveSuccess(statusEl);
        let qs = _buildCheckoutQueryString(formValues);
        setTimeout(function () { window.location.href = '/checkout' + qs; }, 1500);
      })
      .catch(function (err) {
        _gstBookingInFlight = false;
        console.error('[GST Booking] createBooking error', err);
        if (statusEl) {
          var customMsg = _gstBookingFailureMessage(err);
          statusEl.innerHTML =
              '<div class="gst-alert gst-alert--error">' +
              '<p>' + (customMsg ||
                  'Ocurrió un error al confirmar la reserva. Por favor, <a href="/pages/reservar-fecha">inténtalo de nuevo</a>.') +
              '</p>' +
              '</div>';
        }
      });
}

function _buildCheckoutQueryString(formValues) {
  // Patient step hidden: customer details are no longer collected in the booking
  // flow, so there is nothing to prefill at checkout (a future checkout block
  // will capture them). Restore the body below with the patient-details step.
  return '';
  /*
  if (!_gstCheckoutPrefillEnabled(formValues)) return '';
  let parts = [];
  let fn = _gstPatientField('firstName', formValues);
  let ln = _gstPatientField('lastName', formValues);
  let em = _gstPatientField('email', formValues);
  let ph = _gstPatientField('phone', formValues);
  if (fn) parts.push('checkout[shipping_address][first_name]=' + encodeURIComponent(fn));
  if (ln) parts.push('checkout[shipping_address][last_name]='  + encodeURIComponent(ln));
  if (em) parts.push('checkout[email]=' + encodeURIComponent(em));
  if (ph) parts.push('checkout[shipping_address][phone]=' + encodeURIComponent(ph));
  return parts.length ? '?' + parts.join('&') : '';
  */
}

// === Pack de promo en checkout (app gs-promo-pack-checkout) ===================
//
// La app "gs-promo-pack-checkout" (Cart Transform) agrupa en una sola línea la
// promo a 0 € y sus zonas cuando comparten la propiedad oculta `_gst_group`,
// para que el checkout no muestre la promo como "GRATIS". La marca se pone en
// todas las reservas; la app solo agrupa si la línea principal es un producto
// con la etiqueta "promociones" a 0 €. false = no se marca nada (desactivado).
var GST_PROMO_PACK_ENABLED = true;

function _gstPromoPackEnabledFor(productId) {
  return GST_PROMO_PACK_ENABLED && !!_gstStr(productId);
}

function _addToCart(variantId, bookingId, formValues) {
  let baseProps = {
    _appointmentId: String(bookingId),
    _clinic:        GstBookingState.get('clinicName') || '',
    _appointment:   (GstBookingState.get('slotDate') || '') + ' ' + (GstBookingState.get('slotTime') || ''),
    "1ª cita de consulta gratuita" : GstBookingState.get('freeConsulation') || ''
    // Patient step hidden: patient name/email are no longer collected, so they
    // are not attached to the cart line item. Restore with the patient step.
    // _patient_name:  [_gstPatientField('firstName', formValues), _gstPatientField('lastName', formValues)].filter(Boolean).join(' '),
    // _patient_email: _gstPatientField('email', formValues)
  };

  // Datos para el bloque "Tu cita" del checkout: dirección del centro (enlace a
  // Maps) y nombre del tratamiento/promo reservado (el de la ficha, no el de
  // las zonas, que son productos aparte tipo "Promo-TarifaPlana XS Mejillas").
  let clinicAddress = GstBookingState.get('clinicAddress') || '';
  if (clinicAddress) baseProps._clinic_address = clinicAddress;
  let promoTitle = GstBookingState.get('productTitle') || '';
  if (promoTitle) baseProps._promo = promoTitle;

  let sessionsCount = GstBookingState.get('sessionsCount');
  let sessionsLabel = GstBookingState.get('sessionsLabel');
  let zones         = GstBookingState.getJson('zones');
  let zoneEntries   = GstBookingState.getJson('zoneEntries') || [];
  if (sessionsCount) baseProps._sessions = sessionsCount;
  if (sessionsLabel) baseProps._sessions_label = sessionsLabel;
  // Parent (clinic-chooser) variant carries the booking summary; the actual
  // price lives on the body-part products linked via GPO. Customer-visible
  // "Zona" (no underscore) so the selection renders in cart/checkout — the
  // theme's main-cart.liquid already special-cases this exact key.
  let parentProps = Object.assign({}, baseProps);
  if (Array.isArray(zones) && zones.length) {
    parentProps.Zona   = zones.join(', ');
    parentProps._zones = zones.join(', ');
  }
  // Pack de promo en checkout: misma marca en la promo y en sus zonas.
  let packGroup = (variantId && _gstPromoPackEnabledFor(GstBookingState.get('parentProductId'))) ? String(Date.now()) : '';
  if (packGroup) parentProps._gst_group = packGroup;
  return _gstResolveZoneCartItems(zoneEntries).then(function (resolved) {
    let items = [];
    if (variantId) {
      items.push({ id: Number(variantId), quantity: 1, properties: parentProps });
    }
    // Each body-part is its own Shopify product (linked via GPO). Add one
    // cart line per zone so the subtotal reflects the priced products and
    // checkout doesn't fall back to 0,00 €. Each line keeps the shared
    // booking metadata for traceability and a per-line "Zona" for clarity.
    resolved.forEach(function (entry) {
      let props = Object.assign({}, baseProps);
      if (entry.label) {
        props.Zona   = entry.label;
        props._zones = entry.label;
      }
      var qty = parseInt(entry.quantity, 10);
      if (isNaN(qty) || qty < 1) qty = 1;
      // Each product carries its OWN session count, not the booking-wide
      // combined value inherited from baseProps. This overrides the shared
      // baseProps._sessions that would otherwise make every line identical.
      var lineSessions = _gstLineSessions(entry, qty);
      props._sessions = String(lineSessions.perUnit);
      props._sessions_label = String(lineSessions.total);
      if (packGroup) props._gst_group = packGroup;
      items.push({ id: Number(entry.variantId), quantity: qty, properties: props });
    });

    if (!items.length) return;
    _gstDebugLog('cart: posting items', { count: items.length, parentVariantId: variantId, zones: resolved });

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items }),
      credentials: 'same-origin'
    })
        .then(function (res) {
          if (!res.ok) return res.text().then(function (t) { console.error('[GST Booking] Cart add failed:', t); });
        })
        .catch(function (err) { console.error('[GST Booking] Cart add error:', err); });
  });
}

// === PDP intercept ============================================================

// Reset transient booking state every time a bookable PDP loads. A leftover
// `slotTimecode` from a previous booking causes `_gstShouldSkipPdpIntercept`
// to return true on the next ATC click, which lets Shopify's native form
// submit run and dumps the user into /cart instead of the booking flow.
// Per-tab sessionStorage means clearing here can't stomp on a parallel
// flow in another tab.
(function () {
  if (typeof window === 'undefined' || !window.__GST_BOOKABLE_PDP__) return;
  try {
    GstBookingState.clear();
    _gstDebugLog('pdp: cleared booking state on entry', { referrer: document.referrer || '' });
  } catch (e) {}
})();

// GPO renders paired option groups per body part using a shared trailing index
// (e.g. the zone group "Zona1" and its session group "Sesiones1"). Extract that
// index so a zone entry can be matched to its own "Número de sesiones" pick.
// Returns '' when the group has no index (single-zone products), which still
// pairs a lone "Zona" with a lone "Sesiones".
function _gstOptionGroupKey(name) {
  var m = String(name || '').match(/(\d+)\s*$/);
  return m ? m[1] : '';
}

function _gstClassifyOptionName(name) {
  var n = String(name || '').toLowerCase();
  n = n.normalize ? n.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : n;
  if (/centro|clinic|sede|ubicacion|localizacion|location/.test(n)) return 'clinic';
  if (/sesion|session|numero/.test(n))                              return 'sessions';
  if (/zona|zone/.test(n))                                          return 'zone';
  return 'other';
}

// Live GPO zone tracking: capture handle/variantId on every change.
var _gstSelectedZoneGpo = {};
var _gstZoneFieldRe = /^(?:options|properties)\[(.+)\]$/;
var _gstGpoFieldSelector =
    'select[name^="properties["], input[name^="properties["], ' +
    'select[name^="options["],    input[name^="options["], ' +
    'select[data-name^="properties["], input[data-name^="properties["], ' +
    'select[data-name^="options["],    input[data-name^="options["]';

// GPO hides conditional zone/session groups via display:none on the
// `.gpo-element` wrapper. The actual input is also display:none (radios
// styled as custom buttons, real <select> hidden behind a custom UI), so
// the walk STARTS from the parent — never check the input itself.
// Verified against the live PDP: starting from el itself misclassifies every
// GPO field as inactive; starting from parent correctly distinguishes
// active groups (Zona1, sesiones1, more1) from collapsed conditionals
// (dropdown-6..22, buttons-5..13).
function _gstIsElementActive(el) {
  if (!el || el.disabled) return false;
  var node = el.parentElement;
  while (node && node !== document.documentElement) {
    if (node.hidden) return false;
    if (node.getAttribute && node.getAttribute('aria-hidden') === 'true') return false;
    try {
      var style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
    } catch (e) { break; }
    node = node.parentElement;
  }
  return true;
}

document.addEventListener('change', function (e) {
  var el = e.target;
  if (!el || !el.getAttribute) return;
  if (!_gstIsElementActive(el)) return;
  var handle = _gstStr(el.getAttribute('gpo-data-product-handle'));
  if (!handle && el.tagName === 'SELECT' && el.selectedOptions && el.selectedOptions.length) {
    handle = _gstStr(el.selectedOptions[0].getAttribute('gpo-data-product-handle'));
  }
  if (!handle) return;
  var rawName = el.getAttribute('data-name') || el.name || '';
  var m = rawName.match(_gstZoneFieldRe);
  if (!m) return;
  var kind = _gstClassifyOptionName(m[1]);
  if (kind === 'clinic' || kind === 'sessions') return;
  var label = _gstStr(el.value);
  if (!label) return;
  var vid = _gstStr(el.getAttribute('gpo-data-variant-id'));
  if (!vid && el.tagName === 'SELECT' && el.selectedOptions && el.selectedOptions.length) {
    vid = _gstStr(el.selectedOptions[0].getAttribute('gpo-data-variant-id'));
  }
  _gstSelectedZoneGpo[label] = { handle: handle, variantId: vid };
});

// Clear a previously shown "required options" error as soon as the user makes
// any GPO/product-form selection, so a stale message doesn't linger after the
// missing option is filled in. Scoped to bookable PDPs so it never clears the
// theme's own native product-form errors on nobooking products.
document.addEventListener('change', function (e) {
  if (typeof window === 'undefined' || !window.__GST_BOOKABLE_PDP__) return;
  var el = e.target;
  if (!el || !el.closest) return;
  if (!el.closest('.gpo-container, .gpo-app, form[is="product-form"]')) return;
  _gstClearBookableError(el.closest('form'));
});

// Read the quantity chosen for a zone option. GPO renders a per-option
// quantity input (`.gpo-quantity__input`) whose `data-parent-field-name`
// matches the zone's field name (e.g. "Zona1x1"); the custom +/- stepper
// (snippets/gpo-quantity-stepper.liquid) writes the chosen value there.
// Defaults to 1 when no matching, active input is found.
function _gstReadZoneQuantity(fieldName) {
  var fn = _gstStr(fieldName);
  if (!fn) return 1;
  try {
    var inputs = document.querySelectorAll('.gpo-quantity__input[data-parent-field-name]');
    for (var i = 0; i < inputs.length; i++) {
      var inp = inputs[i];
      if (_gstStr(inp.getAttribute('data-parent-field-name')) !== fn) continue;
      if (!_gstIsElementActive(inp)) continue;
      var n = parseInt(inp.value, 10);
      if (!isNaN(n) && n > 0) return _gstClampQuantity(n);
      break;
    }
  } catch (e) {}
  return 1;
}

// Enforce the per-product session cap (window.__GST_MAX_SESSIONS__, injected by
// layout/theme.liquid) when reading a chosen quantity. The UI stepper already
// clamps, but this guarantees the value sent to the booking API never exceeds
// the cap even if the DOM is tampered with. 0/absent means "no cap".
function _gstClampQuantity(n) {
  try {
    var max = parseInt(typeof window !== 'undefined' ? window.__GST_MAX_SESSIONS__ : 0, 10);
    if (!isNaN(max) && max > 0 && n > max) return max;
  } catch (e) {}
  return n;
}

function _gstReadBookableForm(form) {
  var data = {
    variantId:   '',
    sessions:    '',
    zones:       [],
    zoneEntries: [],   // [{ label, variantId, handle, fieldName, groupKey, quantity, sessions }]
    // Per-zone-group session count keyed by the group's trailing index (e.g.
    // "Sesiones1" -> "1"), so each zone entry can later be paired with its OWN
    // "Número de sesiones" pick (Zona1 <-> Sesiones1) instead of every cart line
    // inheriting the same combined `data.sessions` string.
    sessionsByGroup: {},
  };
  var seenZones    = {};
  var seenSessions = {};
  var fieldRe      = /^(?:options|properties)\[(.+)\]$/;

  function assign(fieldName, value, gpo) {
    var v = _gstStr(value);
    if (!v) return;
    var vid = _gstStr(gpo && gpo.variantId);
    var hnd = _gstStr(gpo && gpo.handle);
    var kind = _gstClassifyOptionName(fieldName);
    if (kind === 'other' && hnd) kind = 'zone';
    // Clinic is the native Shopify variant — its id flows via form.id and is
    // resolved against variantClinicMap, not by reading the option label here.
    if (kind === 'clinic') return;
    switch (kind) {
      case 'sessions':
        // GPO renders one "NÚMERO DE SESIONES" per zone group (per body
        // part). Record this group's own value keyed by its trailing index so
        // it can be attached to the matching zone entry later (Zona1 <->
        // Sesiones1). This is what lets each cart line carry its own session
        // count instead of all lines sharing the combined string.
        var sKey = _gstOptionGroupKey(fieldName);
        if (data.sessionsByGroup[sKey] == null) data.sessionsByGroup[sKey] = v;
        // Collect every distinct value so the calendar header and the combined
        // `data.sessions` summary reflect all picks, not just the first one.
        // Order matches the user's selection order.
        if (seenSessions[v]) break;
        seenSessions[v] = true;
        data.sessions = data.sessions ? (data.sessions + ', ' + v) : v;
        break;
      case 'zone':
        if (seenZones[v] !== undefined) {
          var existing = data.zoneEntries[seenZones[v]] || null;
          if (existing) {
            // Same display label can appear on different Shopify products (e.g.
            // Colectivos "Areolas" PROMO vs pack). Only merge when the handle
            // matches or is still empty; otherwise keep both entries.
            if (hnd && existing.handle && hnd !== existing.handle) {
              // fall through and add a new zone entry below
            } else {
              if (vid && !existing.variantId) existing.variantId = vid;
              if (hnd && !existing.handle)    existing.handle    = hnd;
              return;
            }
          } else {
            return;
          }
        }
        // GPO emits a single zone selection as multiple sibling fields
        // (e.g. "XS Base Barba | 1" and "XS Base Barba") that share the
        // same gpo-data-product-handle. Dedup by handle so the user sees
        // one entry per Shopify product. Different non-empty variantIds
        // on the same handle ARE kept separate (legitimate different
        // session-count variants of the same product). Prefer the cleaner
        // label without the " | N" Shopify variant suffix.
        if (hnd) {
          for (var zi = 0; zi < data.zoneEntries.length; zi++) {
            var prior = data.zoneEntries[zi];
            if (!prior.handle || prior.handle !== hnd) continue;
            if (prior.variantId && vid && prior.variantId !== vid) continue;
            if (vid && !prior.variantId) prior.variantId = vid;
            if (prior.label.indexOf('|') !== -1 && v.indexOf('|') === -1) {
              prior.label   = v;
              data.zones[zi] = v;
              seenZones[v]  = zi;
            }
            return;
          }
        }
        seenZones[v] = data.zoneEntries.length;
        data.zones.push(v);
        data.zoneEntries.push({
          label: v,
          variantId: vid,
          handle: hnd,
          fieldName: fieldName,
          groupKey: _gstOptionGroupKey(fieldName),
          quantity: _gstReadZoneQuantity(fieldName)
        });
        break;
    }
  }

  function gpoEntryFromEl(el) {
    if (!el) return { variantId: '', handle: '' };
    var read = function (node, attr) {
      var raw = node && node.getAttribute ? node.getAttribute(attr) : '';
      return raw ? String(raw).trim() : '';
    };
    var vid = read(el, 'gpo-data-variant-id')    || read(el, 'data-variant-id');
    var hnd = read(el, 'gpo-data-product-handle') || read(el, 'data-product-handle');
    if ((!vid || !hnd) && el.tagName === 'SELECT' && el.selectedOptions && el.selectedOptions.length) {
      var opt = el.selectedOptions[0];
      if (!vid) vid = read(opt, 'gpo-data-variant-id')    || read(opt, 'data-variant-id');
      if (!hnd) hnd = read(opt, 'gpo-data-product-handle') || read(opt, 'data-product-handle');
    }
    return { variantId: vid, handle: hnd };
  }

  function assignFromEl(el) {
    if (!_gstIsElementActive(el)) return;
    // GPO mirrors every selection into a hidden <input> inside
    // .gpo-hidden-inputs for cart-add submission. Those mirrors carry no
    // gpo-data-* attributes and the label is suffixed with the variant
    // session count (e.g. "XS Barbilla | 1"). Reading them would produce
    // phantom duplicate zones alongside the real radio-driven entries.
    if (el.closest && el.closest('.gpo-hidden-inputs')) return;
    var rawName = el.getAttribute('data-name') || el.name || '';
    var m = rawName.match(fieldRe);
    if (!m) return;
    var t = (el.type || '').toLowerCase();
    if ((t === 'radio' || t === 'checkbox') && !el.checked) return;
    assign(m[1], el.value, gpoEntryFromEl(el));
  }

  if (form) {
    var idInput = form.querySelector('input[name="id"]');
    data.variantId = idInput ? String(idInput.value || '') : '';
    form.querySelectorAll('input, select, textarea').forEach(assignFromEl);
  }

  document.querySelectorAll(_gstGpoFieldSelector).forEach(assignFromEl);

  // Backfill zone entries from live GPO change events
  data.zoneEntries.forEach(function (entry) {
    if (entry.handle) return;
    var stored = _gstSelectedZoneGpo[entry.label];
    if (!stored) return;
    entry.handle    = stored.handle;
    entry.variantId = entry.variantId || stored.variantId;
  });

  // Attach each zone's OWN session count so every cart line can reflect its
  // real value instead of the combined "1, 3, 6" string. Sources, in order of
  // reliability:
  //   1. The session count baked into the selected zone's own field name /
  //      handle. The laser products encode it as a suffix/prefix, one distinct
  //      product per pack: e.g. field "zona2x3" + handle "x3-dl-xs-barbilla" =
  //      3 sessions, "zona6x6"/"x6-…" = 6. This is authoritative because the
  //      "Número de sesiones" control (sesiones1, buttons-5, buttons-8, …) is
  //      only a switch that reveals the matching zone group.
  //   2. The matching session group, paired by the zone's trailing index
  //      (Zona1 <-> Sesiones1) — covers the x1 group whose field/handle carry
  //      no explicit suffix, and simpler products with a plain sessions radio.
  //   3. The single shared session value when only one session group exists.
  // Combo products expose none of these — their per-zone count is the quantity
  // stepper and is resolved in _addToCart.
  var uniqueSessions = Object.keys(data.sessionsByGroup)
    .map(function (k) { return data.sessionsByGroup[k]; })
    .filter(function (v, i, arr) { return arr.indexOf(v) === i; });
  data.zoneEntries.forEach(function (entry) {
    var own = _gstZoneSessionsFromEntry(entry);
    if (!own) own = data.sessionsByGroup[entry.groupKey];
    if (own == null && uniqueSessions.length === 1) own = uniqueSessions[0];
    entry.sessions = _gstStr(own);
  });

  return data;
}

// Extract the session-pack size a zone selection encodes in its own field name
// or linked product handle. The laser products model each pack as a distinct
// product: the zone field is suffixed "…xN" (e.g. "zona2x3") and/or the handle
// is prefixed "xN-" (e.g. "x3-dl-xs-barbilla"), where N is the session count.
// Returns '' when no such marker is present (e.g. the base x1 group "Zona1" or
// a simpler product with a separate sessions radio), leaving the caller to fall
// back to the paired session group.
function _gstZoneSessionsFromEntry(entry) {
  if (!entry) return '';
  var m = String(entry.fieldName || '').match(/x\s*(\d+)\s*$/i);
  if (m) return m[1];
  var h = String(entry.handle || '').match(/^x(\d+)[-_]/i);
  if (h) return h[1];
  return '';
}

// === Required-option validation (pre-handoff) =================================
//
// The booking interceptors run in the capture phase and stopImmediatePropagation,
// which preempts the Globo Product Options (GPO) app's own required-field
// validation. Without the checks below, clicking ATC would start the booking
// flow even when Zona/Sesiones are unselected — and a missing zone resolves to
// an empty Flowww product_id, leaving the calendar in a broken "Falta producto"
// state. We therefore re-validate the booking-relevant options ourselves before
// handing off, and surface a message instead of redirecting.

// Read the label of a GPO option group (used only for the user-facing message).
// Only trust GPO's own label classes — falling back to a generic <label> would
// pick up an option's text and produce a misleading message.
function _gstGpoGroupLabel(group) {
  var sels = ['.gpo-element-label', '.gpo-element__label', '.gpo-label', '.gpo-title'];
  for (var i = 0; i < sels.length; i++) {
    var el = group.querySelector(sels[i]);
    if (!el) continue;
    var t = _gstStr(el.textContent).replace(/[*:\s]+$/, '').replace(/\s+/g, ' ').trim();
    if (t) return t.length > 40 ? t.slice(0, 39) + '…' : t;
  }
  return '';
}

// Is a GPO option group visible to the user? GPO collapses conditional
// zone/session groups via display:none on the `.gpo-element` wrapper itself, so
// (unlike _gstIsElementActive, which only walks ancestors of an input) this
// check must START from the group element. Decoupled from per-input `disabled`
// state so a single sold-out option can't hide the whole group.
function _gstGpoGroupVisible(group) {
  var node = group;
  while (node && node !== document.documentElement) {
    if (node.hidden) return false;
    if (node.getAttribute && node.getAttribute('aria-hidden') === 'true') return false;
    try {
      var st = window.getComputedStyle(node);
      if (st && (st.display === 'none' || st.visibility === 'hidden')) return false;
    } catch (e) { break; }
    node = node.parentElement;
  }
  return true;
}

// Active GPO option groups (zona/sesiones) that drive a product/variant
// selection but have nothing chosen yet. Checkbox-only and free-text groups are
// treated as optional add-ons and skipped; the clinic selector is a native
// Shopify variant (not a .gpo-element) and is intentionally not considered here.
function _gstActiveGpoGroupsUnfilled() {
  var host = document.querySelector('.gpo-container, .gpo-app');
  if (!host) return [];
  var out = [];
  host.querySelectorAll('.gpo-element[data-element-id]').forEach(function (group) {
    // Only validate groups that actually drive a priced product/variant pick.
    if (!group.querySelector('[gpo-data-product-handle], [gpo-data-variant-id]')) return;

    var radios = group.querySelectorAll('input[type="radio"]');
    var hasSelection;
    if (radios.length) {
      hasSelection = !!group.querySelector('input[type="radio"]:checked');
    } else {
      var sel = group.querySelector('select');
      if (!sel) return; // checkbox/text only → optional add-on
      hasSelection = _gstStr(sel.value) !== '';
    }
    // Skip collapsed conditional groups (e.g. session counts for zones the user
    // hasn't picked) — only block on options the user can actually see.
    if (!_gstGpoGroupVisible(group)) return;
    if (!hasSelection) out.push(_gstGpoGroupLabel(group));
  });
  return out;
}

// Visible GPO groups that GPO's own field validation has flagged invalid. GPO
// marks the offending `.gpo-element` with `has-error` and renders a
// `.help-block.error` message inside it (e.g. "Seleccione un máximo de 2
// opciones" when more zonas than allowed are checked). Because the capture-phase
// interceptor stopImmediatePropagation()s GPO's own submit handler, that error
// state would otherwise be ignored and the over-limit selection would still hand
// off to the booking flow. Returns each group's label plus GPO's verbatim
// message so we can block and echo it.
function _gstGpoGroupsWithError() {
  var host = document.querySelector('.gpo-container, .gpo-app');
  if (!host) return [];
  var out = [];
  host.querySelectorAll('.gpo-element[data-element-id]').forEach(function (group) {
    if (!_gstGpoGroupVisible(group)) return;

    // Primary signal: GPO toggles `has-error` on the group reactively (verified
    // for min/max/required). Use a visible error-specific message element as a
    // secondary signal so a future GPO markup change that drops the class —
    // but still renders the message — doesn't silently fail open. `.help-block`
    // on its own is intentionally NOT a trigger: GPO also uses it for neutral
    // helper text, so we only treat the error-qualified variants as a signal.
    var hasErrClass = /(^|\s)has-error(\s|$)/.test(group.className || '');
    var msgEl = group.querySelector('.help-block.error, .gpo-error, .gpo-element__error');
    var visibleMsg = msgEl && _gstGpoGroupVisible(msgEl) &&
        _gstStr(msgEl.textContent).trim() !== '';
    if (!hasErrClass && !visibleMsg) return;

    var anyMsg = msgEl || group.querySelector('.help-block');
    var message = anyMsg ? _gstStr(anyMsg.textContent).replace(/\s+/g, ' ').trim() : '';
    out.push({ label: _gstGpoGroupLabel(group), message: message });
  });
  return out;
}

// Per-product total-session limits (across all selected options), injected by
// layout/theme.liquid from custom.min_sessions_total / custom.max_sessions_total
// (with a handle fallback for the Combo Joven). 0 means "no limit".
function _gstSessionTotalLimits() {
  function num(v) { var n = parseInt(v, 10); return (!isNaN(n) && n > 0) ? n : 0; }
  var min = 0, max = 0;
  try {
    if (typeof window !== 'undefined') {
      min = num(window.__GST_MIN_SESSIONS_TOTAL__);
      max = num(window.__GST_MAX_SESSIONS_TOTAL__);
    }
  } catch (e) {}
  return { min: min, max: max };
}

// Total sessions chosen across every selected zone/option. Each zone contributes
// its per-option quantity (default 1 when no quantity control is present).
function _gstCountTotalSessions(data) {
  var list = (data && Array.isArray(data.zoneEntries)) ? data.zoneEntries : [];
  var total = 0;
  list.forEach(function (z) {
    if (!z || !_gstStr(z.handle)) return;
    var q = parseInt(z.quantity, 10);
    if (isNaN(q) || q < 1) q = 1;
    total += q;
  });
  return total;
}

// GPO option groups can require an exact / minimum / maximum number of selected
// options via per-input data-min-selection / data-max-selection attributes
// (e.g. the "3 por 75€" combo: exactly 3 zones). Because the booking interceptor
// runs in the capture phase and stopImmediatePropagation()s GPO's own submit
// validation — and GPO only flags the MAX reactively, never the MIN — we must
// re-check these limits here before handing off. Returns a user-facing message
// when a visible group is outside its configured range, or '' when all pass.
function _gstSelectionLimitError() {
  var host = document.querySelector('.gpo-container, .gpo-app');
  if (!host) return '';
  var groups = host.querySelectorAll('.gpo-element[data-element-id]');
  for (var g = 0; g < groups.length; g++) {
    var group = groups[g];
    if (!_gstGpoGroupVisible(group)) continue;

    var limited = group.querySelector('input[data-min-selection], input[data-max-selection]');
    if (!limited) continue;

    var min = parseInt(limited.getAttribute('data-min-selection'), 10);
    var max = parseInt(limited.getAttribute('data-max-selection'), 10);
    if (isNaN(min)) min = 0;
    if (isNaN(max)) max = 0;
    if (min <= 0 && max <= 0) continue;

    // Count real, user-visible selections only — skip GPO's hidden mirror
    // inputs (in .gpo-hidden-inputs) which would otherwise double the count.
    var inputs = group.querySelectorAll('input[type="checkbox"], input[type="radio"]');
    var checked = 0;
    for (var i = 0; i < inputs.length; i++) {
      var inp = inputs[i];
      if (!inp.checked) continue;
      if (inp.closest && inp.closest('.gpo-hidden-inputs')) continue;
      checked++;
    }

    if (max > 0 && checked > max) {
      return (min === max)
          ? 'Selecciona exactamente ' + max + ' opciones para continuar.'
          : 'Selecciona como máximo ' + max + ' opciones para continuar.';
    }
    if (min > 0 && checked < min) {
      return (min === max)
          ? 'Selecciona exactamente ' + min + ' opciones para continuar.'
          : 'Selecciona al menos ' + min + ' opciones para continuar.';
    }
  }
  return '';
}

function _gstGpoOptionMax() {
  var n = parseInt(typeof window !== 'undefined' ? window.__GST_GPO_OPTION_MAX__ : 0, 10);
  return (!isNaN(n) && n > 0) ? n : 0;
}

// When GPO renders priced checkbox groups as multi-select but the promo only
// allows one choice (e.g. Black Summer MD VIALES), patch data-max-selection
// from theme.liquid and enforce radio-like behaviour on check.
function _gstApplyGpoOptionLimits() {
  var max = _gstGpoOptionMax();
  if (!max) return;
  var host = document.querySelector('.gpo-container, .gpo-app');
  if (!host) return;
  host.querySelectorAll('.gpo-element[data-element-id]').forEach(function (group) {
    if (!_gstGpoGroupVisible(group)) return;
    var inputs = group.querySelectorAll('input[type="checkbox"]');
    if (!inputs.length) return;
    inputs.forEach(function (inp) {
      if (inp.closest && inp.closest('.gpo-hidden-inputs')) return;
      inp.setAttribute('data-max-selection', String(max));
      if (!inp.getAttribute('data-min-selection')) inp.setAttribute('data-min-selection', '1');
    });
    if (max === 1 && !group.__gstSingleSelectBound) {
      group.__gstSingleSelectBound = true;
      group.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || t.type !== 'checkbox' || !t.checked) return;
        group.querySelectorAll('input[type="checkbox"]').forEach(function (cb) {
          if (cb === t || (cb.closest && cb.closest('.gpo-hidden-inputs'))) return;
          if (cb.checked) {
            cb.checked = false;
            cb.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }
  });
}

// Shared GPO min/max/required validation for both bookable handoff and native
// add-to-cart on `nobooking` products (whose submit bypasses the intercept).
function _gstValidateGpoForm(form) {
  var gpoErrors = _gstGpoGroupsWithError();
  if (gpoErrors.length) {
    var msgs = gpoErrors.map(function (e) { return e.message; }).filter(Boolean);
    return {
      ok: false,
      message: msgs.length ? msgs.join(' ') : _gstRequiredOptionsMessage([])
    };
  }

  var selectionError = _gstSelectionLimitError();
  if (selectionError) return { ok: false, message: selectionError };

  var host = document.querySelector('.gpo-container, .gpo-app');
  if (host) {
    var unfilled = [];
    host.querySelectorAll('.gpo-element[data-element-id]').forEach(function (group) {
      if (!_gstGpoGroupVisible(group)) return;
      if (!group.querySelector('[gpo-data-product-handle], [gpo-data-variant-id]')) return;
      var cbs = group.querySelectorAll('input[type="checkbox"]');
      if (!cbs.length) return;
      var checked = 0;
      for (var i = 0; i < cbs.length; i++) {
        var cb = cbs[i];
        if (!cb.checked) continue;
        if (cb.closest && cb.closest('.gpo-hidden-inputs')) continue;
        checked++;
      }
      var min = parseInt(cbs[0].getAttribute('data-min-selection'), 10);
      if (isNaN(min)) min = 0;
      if (min > 0 && checked < min) unfilled.push(_gstGpoGroupLabel(group));
    });
    if (unfilled.length) {
      return { ok: false, message: _gstRequiredOptionsMessage(unfilled) };
    }
  }

  return { ok: true, message: '' };
}

document.addEventListener('gpoRenderCompleted', _gstApplyGpoOptionLimits);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _gstApplyGpoOptionLimits);
} else {
  _gstApplyGpoOptionLimits();
}

// Returns { ok, missing[], message }. `bookingonly` products resolve product_id
// from the V1 metafield and need no GPO options, so they always pass. EA
// products are exempt from this bypass: their main CTA resolves the service id
// from the options app, so they must be validated like any options product.
function _gstValidateBookableSelection(pdp, data) {
  var isEa = !!(pdp && pdp.ea);
  var bookingOnly = !isEa && !!(pdp && pdp.bookingOnly);
  var v1 = pdp ? _gstStr(pdp.v1) : '';
  if (bookingOnly && v1) return { ok: true, missing: [], message: '' };

  // Colectivos promo: exactly 1 discounted "Zona XS/S PROMO" body part + at
  // least 1 pack body part from XS/S or M/L (with free session quantities).
  var colectivos = false;
  try { colectivos = !!window.__GST_COMBO_COLECTIVOS__; } catch (e) { colectivos = false; }
  if (colectivos) {
    var entries = (data && Array.isArray(data.zoneEntries)) ? data.zoneEntries : [];
    var promo = 0;
    var pack = 0;
    entries.forEach(function (z) {
      var h = _gstStr(z && z.handle);
      if (!h) return;
      if (/promocolectivosdiscounted/i.test(h)) promo++;
      else pack++;
    });
    if (promo < 1) {
      return {
        ok: false,
        missing: ['Zona XS/S PROMO'],
        message: 'Elige 1 zona en "Zona XS/S PROMO" para continuar.'
      };
    }
    if (promo > 1) {
      return {
        ok: false,
        missing: ['Zona XS/S PROMO'],
        message: 'Solo puedes elegir 1 zona en "Zona XS/S PROMO".'
      };
    }
    if (pack < 1) {
      return {
        ok: false,
        missing: ['Zona XS/S', 'Zona M/L'],
        message: 'A\u00f1ade al menos 1 zona en "Zona XS/S" o "Zona M/L" y elige el n\u00famero de sesiones.'
      };
    }
    return { ok: true, missing: [], message: '' };
  }

  // "eligeuno" (tag): varios desplegables de tratamiento condicionados por
  // centro; hay que elegir exactamente UNO. Sustituye a la regla general de
  // "todos los grupos visibles rellenos", que aquí obligaría a elegir los dos.
  var eligeUno = false;
  try { eligeUno = !!window.__GST_ELIGE_UNO__; } catch (e) { eligeUno = false; }
  if (eligeUno) return _gstValidateEligeUno();

  // Honour GPO's own validation errors first (e.g. the max-options limit) and
  // surface GPO's message verbatim so the user sees the same guidance GPO shows.
  var gpoErrors = _gstGpoGroupsWithError();
  if (gpoErrors.length) {
    var msgs = gpoErrors.map(function (e) { return e.message; }).filter(Boolean);
    return {
      ok: false,
      missing: gpoErrors.map(function (e) { return e.label; }),
      message: msgs.length ? msgs.join(' ') : _gstRequiredOptionsMessage([])
    };
  }

  // Enforce GPO's configured exact/min/max selection counts (e.g. the
  // "3 por 75€" combo requires exactly 3 zones). GPO only flags the max
  // reactively, so this also covers the "too few" case our interceptor hides.
  var selectionError = _gstSelectionLimitError();
  if (selectionError) {
    return { ok: false, missing: [], message: selectionError };
  }

  // Combo products (e.g. the "menores de 26" Combo Joven) let the customer pick
  // any number of zones, each independently optional, constrained only by a
  // total number of sessions across all picks. For these we must NOT require
  // every zone group to be filled — instead enforce the total min/max.
  var totalLimits = _gstSessionTotalLimits();
  if (totalLimits.min > 0 || totalLimits.max > 0) {
    var total = _gstCountTotalSessions(data);
    if (total < 1) {
      return { ok: false, missing: [], message: 'Selecciona al menos una zona para continuar.' };
    }
    if (totalLimits.max > 0 && total > totalLimits.max) {
      return { ok: false, missing: [], message: 'Puedes reservar un máximo de ' + totalLimits.max + ' sesiones en total.' };
    }
    if (totalLimits.min > 0 && total < totalLimits.min) {
      return { ok: false, missing: [], message: 'Debes reservar un mínimo de ' + totalLimits.min + ' sesiones en total.' };
    }
    return { ok: true, missing: [], message: '' };
  }

  var missing = _gstActiveGpoGroupsUnfilled();
  if (missing.length) {
    return { ok: false, missing: missing, message: _gstRequiredOptionsMessage(missing) };
  }

  // Backstop: an option-based product must resolve a Flowww product_id from a
  // selected zone, so block when GPO options exist but none resolved to a zone
  // (e.g. unexpected markup the group scan missed). Products with no GPO options
  // at all are intentionally left to the legacy pass-through behaviour.
  var hasGpo  = !!document.querySelector('.gpo-container, .gpo-app');
  var hasZone = Array.isArray(data.zoneEntries) &&
      data.zoneEntries.some(function (z) { return z && _gstStr(z.handle); });
  if (hasGpo && !hasZone) {
    return { ok: false, missing: [''], message: _gstRequiredOptionsMessage(['']) };
  }
  return { ok: true, missing: [], message: '' };
}

// Grupos GPO visibles que determinan un tratamiento (llevan producto/variante),
// con su estado de selección. Es el mismo criterio que _gstActiveGpoGroupsUnfilled.
function _gstEligeUnoGroups() {
  var host = document.querySelector('.gpo-container, .gpo-app');
  if (!host) return [];
  var out = [];
  host.querySelectorAll('.gpo-element[data-element-id]').forEach(function (group) {
    if (!group.querySelector('[gpo-data-product-handle], [gpo-data-variant-id]')) return;
    if (!_gstGpoGroupVisible(group)) return;
    var selected;
    if (group.querySelector('input[type="radio"]')) {
      selected = !!group.querySelector('input[type="radio"]:checked');
    } else {
      var sel = group.querySelector('select');
      if (!sel) return;
      selected = _gstStr(sel.value) !== '';
    }
    out.push({ group: group, selected: selected, label: _gstGpoGroupLabel(group) });
  });
  return out;
}

// Marca (o limpia) en rojo los desplegables implicados; la marca se quita sola
// en cuanto el usuario cambia cualquier opción.
function _gstEligeUnoMark(groups) {
  if (!document.getElementById('gst-elige-uno-css')) {
    var css = document.createElement('style');
    css.id = 'gst-elige-uno-css';
    // GPO pinta el desplegable como .dropdown-button (con radios dentro de la lista).
    css.textContent = '.gpo-element.gst-elige-uno-error .dropdown-button,.gpo-element.gst-elige-uno-error select{border:2px solid #c0392b!important;box-shadow:0 0 0 3px rgba(192,57,43,.15)!important}';
    document.head.appendChild(css);
  }
  document.querySelectorAll('.gpo-element.gst-elige-uno-error').forEach(function (g) {
    g.classList.remove('gst-elige-uno-error');
  });
  (groups || []).forEach(function (g) { g.group.classList.add('gst-elige-uno-error'); });
  var host = document.querySelector('.gpo-container, .gpo-app');
  if (host && !host.__gstEligeUnoBound) {
    host.__gstEligeUnoBound = true;
    host.addEventListener('change', function () { _gstEligeUnoMark([]); }, true);
  }
}

// Exclusión automática: al elegir un tratamiento en un desplegable, se vacían
// los demás (GPO no deja "deseleccionar" a mano: sus desplegables no tienen
// opción vacía). Desmarcar el radio y avisar con input/change basta para que
// GPO vuelva a "-- Por favor, elija uno --" y actualice sus campos ocultos.
var _gstEligeUnoLimpiando = false;
document.addEventListener('change', function (ev) {
  var activo = false;
  try { activo = !!window.__GST_ELIGE_UNO__; } catch (e) { activo = false; }
  if (!activo || _gstEligeUnoLimpiando) return;
  var inp = ev.target;
  if (!inp || inp.type !== 'radio' || !inp.checked) return;
  if (inp.closest && inp.closest('.gpo-hidden-inputs')) return;
  var propio = inp.closest && inp.closest('.gpo-element[data-element-id]');
  if (!propio || !propio.querySelector('[gpo-data-product-handle], [gpo-data-variant-id]')) return;

  _gstEligeUnoLimpiando = true;
  try {
    _gstEligeUnoGroups().forEach(function (g) {
      if (g.group === propio || !g.selected) return;
      g.group.querySelectorAll('input[type="radio"]:checked').forEach(function (r) {
        if (r.closest && r.closest('.gpo-hidden-inputs')) return;
        r.checked = false;
        r.dispatchEvent(new Event('input', { bubbles: true }));
        r.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    _gstEligeUnoMark([]);
  } finally {
    _gstEligeUnoLimpiando = false;
  }
}, true);

function _gstValidateEligeUno() {
  var groups = _gstEligeUnoGroups();
  if (!groups.length) {
    // Sin desplegables de tratamiento visibles: que decida la regla general.
    return { ok: true, missing: [], message: '' };
  }
  var chosen = groups.filter(function (g) { return g.selected; });
  if (chosen.length === 1) {
    _gstEligeUnoMark([]);
    return { ok: true, missing: [], message: '' };
  }
  if (chosen.length === 0) {
    _gstEligeUnoMark(groups);
    return {
      ok: false,
      missing: groups.map(function (g) { return g.label; }),
      message: groups.length > 1
          ? 'Elige un tratamiento (solo uno de los desplegables) para continuar.'
          : 'Elige tu tratamiento para continuar.'
    };
  }
  _gstEligeUnoMark(chosen);
  return {
    ok: false,
    missing: chosen.map(function (g) { return g.label; }),
    message: 'Solo puedes elegir un tratamiento: deja el otro desplegable en blanco.'
  };
}

function _gstRequiredOptionsMessage(missing) {
  var labels = (missing || []).filter(Boolean);
  if (labels.length && labels.length <= 3) {
    return 'Antes de continuar, selecciona: ' + labels.join(', ') + '.';
  }
  return 'Selecciona todas las opciones del tratamiento antes de continuar con la reserva.';
}

function _gstBookableErrorScope(form) {
  if (form && form.closest) {
    var s = form.closest('.product-form');
    if (s) return s;
  }
  return document.querySelector('.product-form') || document;
}

function _gstShowBookableError(form, message) {
  var scope = _gstBookableErrorScope(form);
  if (!scope || !scope.querySelector) { try { window.alert(message); } catch (e) {} return; }

  // Prefer the wrapper's inner message (it has the error icon); fall back to the
  // standalone message element. Show in ONE place so the message isn't doubled.
  var wrapper = scope.querySelector('.product-form__error-message-wrapper');
  var target = wrapper
      ? wrapper.querySelector('.product-form__error-message')
      : scope.querySelector('.product-form__error-message');

  if (wrapper) wrapper.hidden = false;
  if (target) {
    target.hidden = false;
    target.textContent = message;
  } else {
    try { window.alert(message); } catch (e) {}
  }

  var focusTarget = document.querySelector('.gpo-container, .gpo-app') || wrapper || target;
  if (focusTarget && focusTarget.scrollIntoView) {
    focusTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

function _gstClearBookableError(form) {
  var scope = _gstBookableErrorScope(form);
  if (!scope.querySelectorAll) return;
  var wrapper = scope.querySelector('.product-form__error-message-wrapper');
  if (wrapper) wrapper.hidden = true;
  Array.prototype.forEach.call(scope.querySelectorAll('.product-form__error-message'), function (el) {
    el.hidden = true;
    el.textContent = '';
  });
}

function _gstHandoffToBookingFlow(opts) {
  var pdp     = opts.pdp || {};
  var data    = opts.formData || _gstReadBookableForm(null);
  _gstApplyDefaultSessions(data);
  var productId = _gstStr(opts.productId);
  var clin    = opts.clin;

  _gstDebugLog('handoff: booking flow', {
    variantId:     _gstStr(opts.variantId),
    parentProductId: _gstStr(pdp.id),
    productHandle: _gstStr(opts.handle),
    productTitle:  _gstStr(opts.title),
    productId:     productId || 'MISSING',
    bookingOnly:   !!pdp.bookingOnly,
    v1:            _gstStr(pdp.v1) || 'MISSING (custom.V1)',
    clinic:        clin || 'UNRESOLVED',
    sessions:      data.sessions || '',
    zones:         data.zones || [],
    zoneEntries:   data.zoneEntries || []
  });

  GstBookingState.clear();
  if (opts.title)  GstBookingState.set('productTitle',  opts.title);
  if (opts.handle) GstBookingState.set('productHandle', opts.handle);
  if (pdp.id)      GstBookingState.set('parentProductId', String(pdp.id));
  if (opts.variantId) {
    GstBookingState.set('variantId',       String(opts.variantId));
    GstBookingState.set('parentVariantId', String(opts.variantId));
  }
  if (productId) GstBookingState.set('productId', productId);
  // Persisted for the debug panel so the bookingonly tag + V1 metafield are
  // visible on the calendar page (where window.__GST_BOOKABLE_PRODUCT__ is gone).
  GstBookingState.set('bookingOnly', pdp.bookingOnly ? '1' : '0');
  GstBookingState.set('me', pdp.me ? '1' : '0');
  if (_gstStr(pdp.v1)) GstBookingState.set('v1', _gstStr(pdp.v1));
  if (data.sessions) {
    GstBookingState.set('sessionsCount', data.sessions);
    GstBookingState.set('sessionsLabel', data.sessions);
  }
  if (data.zones && data.zones.length) {
    GstBookingState.setJson('zones', data.zones);
  } else if (pdp.id) {
    _gstDebugLog('handoff: no zones detected', { sessions: data.sessions || '', zoneEntries: data.zoneEntries });
  }
  // Persist the full zone entry list (label + handle + variantId per body
  // part) so `_addToCart` can add each linked Shopify product as its own
  // cart line. The clinic-chooser variant is GRATIS by design; the price
  // lives on the body-part products linked via GPO.
  if (Array.isArray(data.zoneEntries) && data.zoneEntries.length) {
    GstBookingState.setJson('zoneEntries', data.zoneEntries);
  }

  // Snapshot the PDP price so the confirm step can show it. Captured here (on
  // the product page) because window.__GST_BOOKABLE_PRODUCT__ / the price block
  // are gone by the calendar/confirm pages. Free flows (consulta gratuita) skip
  // this so the confirm step never shows a price.
  if (!opts.free) {
    var priceLabel = _gstReadPdpPriceLabel();
    if (priceLabel) GstBookingState.set('priceLabel', priceLabel);
  }

  if (clin && clin.clinicId) {
    GstBookingState.set('clinicId',      clin.clinicId);
    GstBookingState.set('calendarId',    clin.calendarId || clin.clinicId);
    GstBookingState.set('clinicName',    clin.clinicName || clin.clinicId);
    GstBookingState.set('clinicAddress', clin.clinicAddress || '');
  } else {
    // Reaching here means the bookable variant has neither SKU nor metafield
    // — a data integrity problem with the product. Surface it so it's caught
    // in the debug log instead of silently dropping the user into a broken
    // calendar step.
    _gstDebugLog('handoff: clinic unresolved (missing variant SKU and metafield)');
  }
  _gstSelectedZoneGpo = {};
  var mainpid = document.getElementById('mainpid').value;
  console.log(productId);
  if(mainpid == 15632407986524){
    console.log("product id is "+ mainpid);
    var ageGateDiv = document.getElementById('ageGate');
    ageGateDiv.classList.add('show');
    ageGateDiv.classList.remove('hidden');
  }else{
    window.location.href = '/pages/reservar-fecha';
  }
  //window.location.href = '/pages/reservar-fecha';
}

// === clinic_id line item property (nobooking) ================================
//
// Products tagged `nobooking` add to cart through Shopify's native form, so the
// booking flow — which is what normally records the centro — never runs. The
// Liquid forms render properties[_clinic_id] seeded from the variant on page
// load, but a variant change does not re-render them: product-info.js only
// swaps price/SKU/badges and rewrites input[name="id"]. Without the sync below
// the property would keep the first centro's id after the customer switches.
//
// Each form is resolved independently because the sticky bar carries its own
// variant <select>, which can differ from the main form's selection.

// Returns the clinic id for a variant, '' when the variant maps to no usable
// clinic, or null when the map itself is unavailable — the caller treats null
// as "leave the server-rendered value alone" so a missing map can never blank
// a correct one.
//
// Resolution order matches _gstResolveClinicIdFromVariant used by the booking
// flow: variant SKU is the Flowww clinic ID on every clinic variant; the
// flowww_clinic_id metafield is only an explicit override. On microneedling
// (and most nobooking products) the metafield is empty and the SKU holds the
// id — reading only clinicId left _clinic_id blank and Flowww fell back to
// FLOWWW_NO_BOOKING_CLINIC_ID.
function _gstClinicIdForVariant(variantId) {
  var pdp = (typeof window !== 'undefined' && window.__GST_BOOKABLE_PRODUCT__) || null;
  if (!pdp || !pdp.variantClinicMap) return null;
  if (!variantId) return '';
  var entry = pdp.variantClinicMap[String(variantId)];
  if (!entry) return '';
  var id = _gstResolveClinicIdFromVariant(entry);
  // Guard against the "Elige tu centro" placeholder and unmapped variants: an
  // empty value makes Shopify drop the property rather than send clinic_id=0.
  return /^[0-9]+$/.test(id) && id !== '0' ? id : '';
}

function _gstSyncClinicIdProperty(scope) {
  var root = scope && scope.querySelectorAll ? scope : document;
  var fields = root.querySelectorAll('[data-gst-clinic-id-property]');
  Array.prototype.forEach.call(fields, function (field) {
    var form = field.form || (field.closest ? field.closest('form') : null);
    if (!form) return;
    var idInput = form.querySelector('[name="id"]');
    var variantId = idInput ? _gstStr(idInput.value) : '';
    var clinicId = _gstClinicIdForVariant(variantId);
    if (clinicId === null) return;
    field.value = clinicId;
  });
}

// updateVariantInputs() in product-info.js dispatches a bubbling `change` on
// input[name="id"], and the sticky bar's <select name="id"> fires one natively,
// so a single delegated listener covers both add-to-cart forms.
document.addEventListener('change', function (e) {
  var el = e.target;
  if (!el || !el.name || el.name !== 'id') return;
  _gstSyncClinicIdProperty(el.form || document);
});

document.addEventListener('variant:changed', function () {
  _gstSyncClinicIdProperty(document);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () { _gstSyncClinicIdProperty(document); });
} else {
  _gstSyncClinicIdProperty(document);
}

// === First-booking-only gate (firstbookonly) =================================
//
// Products tagged `firstbookonly` only allow booking / add-to-cart for
// customers who have never had an appointment in Flowww. Eligibility is checked
// via GET /v1/customer/has-appointment?email=&clinic_id= right before the
// handoff to the booking flow. Logged-in customers are checked with their
// account email (window.__GST_BOOKING__.customer.email); guests are asked for
// an email in a lightweight modal — the email is only used for this check, no
// registration happens here.

function _gstIsFirstBookOnly() {
  try { return !!window.__GST_FIRST_BOOK_ONLY__; } catch (e) { return false; }
}

function _gstIsPurchaseGated() {
  return _gstIsFirstBookOnly() || _gstIsTripleCheck();
}

function _gstCustomerEmail() {
  var c = _gstBookingCfg().customer;
  return c && typeof c.email === 'string' ? c.email.trim() : '';
}

function _gstFirstBookMsg(key) {
  var defaults = {
    notForYou:        'Lo sentimos, esta promoción está destinada exclusivamente a personas que realizan su primera compra en Grupostop. Nuestros registros indican que ya has sido cliente de Grupostop, por lo que no es posible aplicar esta promoción. Si consideras que se trata de un error, contacta con nuestro Servicio de Atención al Cliente clientes@grupostop.eu o 900 828 410 para que podamos revisarlo contigo.',
    modalTitle:       'Reserva tu primera cita',
    modalIntro:       'Introduce tu correo electrónico para continuar.',
    emailLabel:       'Correo electrónico',
    emailPlaceholder: 'tu@email.com',
    submit:           'Continuar',
    invalidEmail:     'Introduce un correo electrónico válido.',
    selectClinic:     'Selecciona un centro para continuar.',
    error:            'No hemos podido verificar tu cuenta. Inténtalo de nuevo más tarde.',
    close:            'Cerrar'
  };
  var i18n = (typeof window !== 'undefined' && window.__GST_FIRST_BOOK_I18N__) || {};
  var v = i18n[key];
  if (typeof v === 'string' && v && v.indexOf('translation missing') === -1) return v;
  return defaults[key] || '';
}

function _gstIsValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());
}

// Resolves to { ok: true, hasAppointment: bool } on a successful API response,
// or { ok: false, status } when the request fails / is rate-limited (429) /
// errors (422, 502, network). Callers must NOT proceed to booking when ok is
// false.
function _gstCheckHasAppointment(email, clinicId) {
  var url = _gstApiPath('/customer/has-appointment') +
      '?email=' + encodeURIComponent(email) +
      '&clinic_id=' + encodeURIComponent(clinicId);
  return _gstFetchGet(url).then(function (res) {
    if (!res.ok) return { ok: false, status: res.status };
    return res.json().then(function (json) {
      return { ok: true, hasAppointment: !!(json && json.has_appointment) };
    }).catch(function () { return { ok: false, status: res.status }; });
  }).catch(function () {
    return { ok: false, status: 0 };
  });
}

function _gstSetFirstBookCtaBusy(form, busy) {
  try {
    var btns = [];
    if (form && form.querySelectorAll) {
      btns = Array.prototype.slice.call(form.querySelectorAll('[name="add"], button[type="submit"]'));
    }
    var cta = _el('gst-book-now-btn');
    if (cta && btns.indexOf(cta) === -1) btns.push(cta);
    btns.forEach(function (b) {
      b.disabled = !!busy;
      b.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (b.classList) b.classList.toggle('loading', !!busy);
    });
  } catch (e) {}
}

var _gstFirstBookModalEl = null;

function _gstInjectFirstBookStyles() {
  if (document.getElementById('gst-fb-styles')) return;
  var css = [
    '.gst-fb-overlay{position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.5);padding:16px;}',
    '.gst-fb-dialog{position:relative;background:#fff;max-width:420px;width:100%;border-radius:8px;padding:28px 24px;box-shadow:0 10px 40px rgba(0,0,0,.25);font-family:inherit;}',
    '.gst-fb-close{position:absolute;top:8px;right:12px;border:0;background:transparent;font-size:26px;line-height:1;cursor:pointer;color:#333;}',
    '.gst-fb-title{margin:0 0 8px;font-size:20px;}',
    '.gst-fb-intro{margin:0 0 16px;font-size:14px;color:#555;}',
    '.gst-fb-label{display:block;margin:0 0 6px;font-size:13px;font-weight:600;}',
    '.gst-fb-input{width:100%;box-sizing:border-box;padding:10px 12px;border:1px solid #ccc;border-radius:6px;font-size:15px;margin:0 0 12px;}',
    '.gst-fb-message{margin:0 0 12px;font-size:13px;color:#c0392b;}',
    '.gst-fb-submit{width:100%;}',
    '.gst-fb-submit[disabled]{opacity:.6;cursor:default;}'
  ].join('');
  var style = document.createElement('style');
  style.id = 'gst-fb-styles';
  style.textContent = css;
  document.head.appendChild(style);
}

function _gstFirstBookKeydown(e) {
  if (e.key === 'Escape' || e.keyCode === 27) _gstCloseFirstBookModal();
}

function _gstCloseFirstBookModal() {
  if (_gstFirstBookModalEl && _gstFirstBookModalEl.parentNode) {
    _gstFirstBookModalEl.parentNode.removeChild(_gstFirstBookModalEl);
  }
  _gstFirstBookModalEl = null;
  document.removeEventListener('keydown', _gstFirstBookKeydown, true);
}

function _gstOpenFirstBookEmailModal(clinicId, onEligible) {
  _gstCloseFirstBookModal();
  _gstInjectFirstBookStyles();

  var overlay = document.createElement('div');
  overlay.className = 'gst-fb-overlay';
  overlay.innerHTML =
      '<div class="gst-fb-dialog" role="dialog" aria-modal="true" aria-label="' + _esc(_gstFirstBookMsg('modalTitle')) + '">' +
      '<button type="button" class="gst-fb-close" aria-label="' + _esc(_gstFirstBookMsg('close')) + '">&times;</button>' +
      '<h2 class="gst-fb-title">' + _esc(_gstFirstBookMsg('modalTitle')) + '</h2>' +
      '<p class="gst-fb-intro">' + _esc(_gstFirstBookMsg('modalIntro')) + '</p>' +
      '<form class="gst-fb-form" novalidate>' +
      '<label class="gst-fb-label" for="gst-fb-email">' + _esc(_gstFirstBookMsg('emailLabel')) + '</label>' +
      '<input id="gst-fb-email" class="gst-fb-input" type="email" autocomplete="email" inputmode="email" placeholder="' + _esc(_gstFirstBookMsg('emailPlaceholder')) + '" />' +
      '<p class="gst-fb-message" role="alert" hidden></p>' +
      '<button type="submit" class="gst-fb-submit btn btn--primary">' + _esc(_gstFirstBookMsg('submit')) + '</button>' +
      '</form>' +
      '</div>';

  var dialog  = overlay.querySelector('.gst-fb-dialog');
  var form    = overlay.querySelector('.gst-fb-form');
  var input   = overlay.querySelector('#gst-fb-email');
  var message = overlay.querySelector('.gst-fb-message');
  var submit  = overlay.querySelector('.gst-fb-submit');
  var closeBtn = overlay.querySelector('.gst-fb-close');

  function showMessage(text) {
    message.textContent = text || '';
    message.hidden = !text;
  }
  function setBusy(busy) {
    submit.disabled = !!busy;
    submit.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  overlay.addEventListener('click', function (e) {
    if (e.target === overlay) _gstCloseFirstBookModal();
  });
  if (closeBtn) closeBtn.addEventListener('click', _gstCloseFirstBookModal);
  if (dialog) dialog.addEventListener('click', function (e) { e.stopPropagation(); });

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    var email = _gstStr(input.value);
    if (!_gstIsValidEmail(email)) {
      showMessage(_gstFirstBookMsg('invalidEmail'));
      input.focus();
      return;
    }
    showMessage('');
    setBusy(true);
    _gstDebugLog('firstbook: guest eligibility check', { clinicId: clinicId });
    _gstCheckHasAppointment(email, clinicId).then(function (res) {
      setBusy(false);
      if (!res.ok) {
        _gstDebugLog('firstbook: guest check failed', { status: res.status });
        showMessage(_gstFirstBookMsg('error'));
        return;
      }
      if (res.hasAppointment) {
        _gstDebugLog('firstbook: guest has appointments, blocked');
        showMessage(_gstFirstBookMsg('notForYou'));
        return;
      }
      _gstDebugLog('firstbook: guest eligible, proceeding');
      _gstCloseFirstBookModal();
      if (typeof onEligible === 'function') onEligible();
    });
  });

  document.body.appendChild(overlay);
  _gstFirstBookModalEl = overlay;
  document.addEventListener('keydown', _gstFirstBookKeydown, true);
  try { input.focus(); } catch (e) {}
}

// === One-per-customer promo (custom.triple_check) ===========================
//
// A product metafield, independent of the firstbookonly tag. Before the promo
// can be booked or added to the cart the customer gives an email, a Spanish
// phone and a DNI/NIE (a passport is accepted too). The API blocks the sale
// when any of the three has already paid for this Shopify product: email, then
// phone, then the document. A free consultation (opts.free) is not a purchase
// of the promo and skips this gate. Logged-in customers with all three values
// already on the account are checked without the popup.

function _gstIsTripleCheck() {
  try { return !!window.__GST_TRIPLE_CHECK__; } catch (e) { return false; }
}

function _gstTripleCheckMsg(key) {
  var defaults = {
    notForYou:        'Lo sentimos, esta promoción solo puede adquirirse una vez por cliente y nuestros registros indican que ya la has adquirido anteriormente. Si consideras que se trata de un error, contacta con nuestro Servicio de Atención al Cliente clientes@grupostop.eu o 900 828 410 para que podamos revisarlo contigo.',
    modalTitle:       'Verifica tus datos',
    modalIntro:       'Esta promoción solo puede adquirirse una vez. Introduce tu correo, teléfono y documento para continuar. Solo los usaremos para comprobar que no la has adquirido antes.',
    emailLabel:       'Correo electrónico',
    emailPlaceholder: 'tu@email.com',
    phoneLabel:       'Teléfono',
    phonePlaceholder: '600 000 000',
    nifLabel:         'DNI / NIE / Pasaporte',
    nifPlaceholder:   '12345678Z',
    submit:           'Continuar',
    invalidEmail:     'Introduce un correo electrónico válido.',
    invalidPhone:     'Introduce un teléfono válido (si no es español, incluye el prefijo, p. ej. +376).',
    invalidNif:       'Introduce un DNI, NIE o pasaporte válido.',
    error:            'No hemos podido verificar tus datos. Inténtalo de nuevo más tarde.',
    close:            'Cerrar'
  };
  var i18n = (typeof window !== 'undefined' && window.__GST_TRIPLE_CHECK_I18N__) || {};
  var v = i18n[key];
  if (typeof v === 'string' && v && v.indexOf('translation missing') === -1) return v;
  return defaults[key] || '';
}

function _gstCustomerField(key) {
  var c = _gstBookingCfg().customer;
  return c && typeof c[key] === 'string' ? c[key].trim() : '';
}

function _gstTripleCheckProductId() {
  var pdp = (typeof window !== 'undefined' && window.__GST_BOOKABLE_PRODUCT__) || {};
  var id = parseInt(pdp.id, 10);
  return id > 0 ? id : 0;
}

function _gstTcKey(kind) {
  return 'gst-tc-' + kind + '-' + _gstTripleCheckProductId();
}

function _gstTcFlag(kind) {
  try { return sessionStorage.getItem(_gstTcKey(kind)) === '1'; } catch (e) { return false; }
}

function _gstTcSetFlag(kind, on) {
  try {
    if (on) sessionStorage.setItem(_gstTcKey(kind), '1');
    else sessionStorage.removeItem(_gstTcKey(kind));
  } catch (e) {}
}

// Same shape the API stores: national nine digits for Spain, otherwise the full
// number with its country code. A non-Spanish number must be typed with + or 00
// so a short local number is never mistaken for a foreign one.
function _gstNormalizePhone(value) {
  var raw = String(value || '').trim();
  var international = /^(\+|00)/.test(raw);
  var digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.indexOf('34') === 0) digits = digits.slice(2);
  else if (digits.length === 13 && digits.indexOf('0034') === 0) digits = digits.slice(4);
  else if (digits.indexOf('00') === 0) digits = digits.slice(2);
  if (digits.length === 9) return digits;
  if (international && digits.length >= 8 && digits.length <= 15) return digits;
  return '';
}

function _gstNormalizeNif(value) {
  return String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function _gstIsValidDocument(value) {
  var v = _gstNormalizeNif(value);
  var letters = 'TRWAGMYFPDXBNJZSQVHLCKE';
  var dni = v.match(/^(\d{8})([A-Z])$/);
  if (dni) return letters[parseInt(dni[1], 10) % 23] === dni[2];
  var nie = v.match(/^([XYZ])(\d{7})([A-Z])$/);
  if (nie) {
    var prefix = { X: '0', Y: '1', Z: '2' }[nie[1]];
    return letters[parseInt(prefix + nie[2], 10) % 23] === nie[3];
  }
  return /^[A-Z0-9]{5,20}$/.test(v) && /[A-Z]/.test(v) && /\d/.test(v);
}

function _gstTripleCheckKnownIdentity() {
  var email = _gstCustomerEmail();
  var phone = _gstNormalizePhone(_gstCustomerField('phone'));
  var nif = _gstNormalizeNif(_gstCustomerField('nif'));
  if (!_gstIsValidEmail(email) || !phone || !_gstIsValidDocument(nif)) return null;
  return { email: email, phone: phone, nif: nif };
}

function _gstTripleCheckCtas() {
  var list = [];
  function add(el) {
    if (el && list.indexOf(el) === -1) list.push(el);
  }
  Array.prototype.forEach.call(document.querySelectorAll('form[is="product-form"]'), function (form) {
    Array.prototype.forEach.call(form.querySelectorAll('[name="add"], button[type="submit"]'), add);
  });
  add(document.getElementById('gst-book-now-btn'));
  add(document.getElementById('res_tarde_button'));
  return list;
}

// Remembers each button's own disabled state (sold-out variant, etc.) so the
// busy toggle never re-enables a button the theme had switched off.
function _gstSetTripleCheckCtasBusy(busy) {
  _gstTripleCheckCtas().forEach(function (b) {
    if (b.getAttribute('data-gst-tc-blocked') === '1') return;
    if (busy) {
      if (b.getAttribute('data-gst-tc-busy') === '1') return;
      b.setAttribute('data-gst-tc-busy', '1');
      b.setAttribute('data-gst-tc-was-disabled', b.disabled ? '1' : '0');
      b.disabled = true;
      b.setAttribute('aria-busy', 'true');
      return;
    }
    if (b.getAttribute('data-gst-tc-busy') !== '1') return;
    b.disabled = b.getAttribute('data-gst-tc-was-disabled') === '1';
    b.removeAttribute('data-gst-tc-busy');
    b.removeAttribute('data-gst-tc-was-disabled');
    b.setAttribute('aria-busy', 'false');
  });
}

function _gstSetTripleCheckCtasDisabled(disabled) {
  _gstTripleCheckCtas().forEach(function (b) {
    b.disabled = !!disabled;
    if (disabled) b.setAttribute('data-gst-tc-blocked', '1');
  });
}

function _gstTripleCheckBoot() {
  if (_gstIsTripleCheck() && _gstTcFlag('block')) _gstSetTripleCheckCtasDisabled(true);
}

// The theme re-enables the add button on every variant change.
document.addEventListener('variant:changed', function () {
  setTimeout(_gstTripleCheckBoot, 0);
});

function _gstCheckPromoEligibility(identity) {
  return fetch(_gstApiPath('/customer/promo-eligibility'), {
    method: 'POST',
    credentials: 'omit',
    headers: _gstJsonPostHeaders(),
    body: JSON.stringify({
      product_id: _gstTripleCheckProductId(),
      email: identity.email,
      phone: identity.phone,
      nif: identity.nif
    })
  }).then(function (res) {
    if (!res.ok) return { ok: false, status: res.status };
    return res.json().then(function (json) {
      return { ok: true, eligible: !!(json && json.eligible) };
    }).catch(function () { return { ok: false, status: res.status }; });
  }).catch(function () {
    return { ok: false, status: 0 };
  });
}

// The checkout falls back to these attributes when Shopify hides the buyer's
// email/phone from the extension, so the hand-off waits for the write.
function _gstStoreTripleCheckAttributes(identity) {
  try {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        attributes: {
          _gst_tc_email: identity.email,
          _gst_tc_phone: identity.phone,
          _gst_tc_nif: identity.nif
        }
      })
    }).catch(function () {});
  } catch (e) {
    return Promise.resolve();
  }
}

function _gstRejectTripleCheck(form, message, permanent) {
  if (permanent) {
    _gstTcSetFlag('pass', false);
    _gstTcSetFlag('block', true);
    _gstSetTripleCheckCtasDisabled(true);
  }
  _gstShowBookableError(form, message);
}

function _gstAcceptTripleCheck(form, identity, onPass) {
  _gstTcSetFlag('block', false);
  _gstTcSetFlag('pass', true);
  _gstClearBookableError(form);
  var proceeded = false;
  function proceed() {
    if (proceeded) return;
    proceeded = true;
    _gstSetTripleCheckCtasBusy(false);
    if (typeof onPass === 'function') onPass();
  }
  _gstSetTripleCheckCtasBusy(true);
  setTimeout(proceed, 2000);
  _gstStoreTripleCheckAttributes(identity).then(proceed, proceed);
}

function _gstRunTripleCheck(form, identity, onPass, showMessage) {
  _gstSetTripleCheckCtasBusy(true);
  _gstDebugLog('triplecheck: eligibility check', { productId: _gstTripleCheckProductId() });
  _gstCheckPromoEligibility(identity).then(function (res) {
    _gstSetTripleCheckCtasBusy(false);
    if (!res.ok) {
      _gstDebugLog('triplecheck: check failed', { status: res.status });
      if (typeof showMessage === 'function') showMessage(_gstTripleCheckMsg('error'));
      else _gstShowBookableError(form, _gstTripleCheckMsg('error'));
      return;
    }
    if (!res.eligible) {
      _gstDebugLog('triplecheck: already purchased, blocked');
      _gstRejectTripleCheck(form, _gstTripleCheckMsg('notForYou'), true);
      if (typeof showMessage === 'function') showMessage(_gstTripleCheckMsg('notForYou'));
      return;
    }
    _gstDebugLog('triplecheck: eligible, proceeding');
    _gstCloseTripleCheckModal();
    _gstAcceptTripleCheck(form, identity, onPass);
  });
}

var _gstTripleCheckModalEl = null;

function _gstInjectTripleCheckStyles() {
  if (document.getElementById('gst-tc-styles')) return;
  var css = [
    '.gst-tc-overlay{position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.5);padding:16px;}',
    '.gst-tc-dialog{position:relative;background:#fff;max-width:440px;width:100%;max-height:90vh;overflow:auto;border-radius:8px;padding:28px 24px;box-shadow:0 10px 40px rgba(0,0,0,.25);font-family:inherit;}',
    '.gst-tc-close{position:absolute;top:8px;right:12px;border:0;background:transparent;font-size:26px;line-height:1;cursor:pointer;color:#333;}',
    '.gst-tc-title{margin:0 0 8px;font-size:20px;}',
    '.gst-tc-intro{margin:0 0 16px;font-size:14px;color:#555;}',
    '.gst-tc-label{display:block;margin:0 0 6px;font-size:13px;font-weight:600;}',
    '.gst-tc-input{width:100%;box-sizing:border-box;padding:10px 12px;border:1px solid #ccc;border-radius:6px;font-size:15px;margin:0 0 12px;}',
    '.gst-tc-message{margin:0 0 12px;font-size:13px;color:#c0392b;}',
    '.gst-tc-submit{width:100%;}',
    '.gst-tc-submit[disabled]{opacity:.6;cursor:default;}'
  ].join('');
  var style = document.createElement('style');
  style.id = 'gst-tc-styles';
  style.textContent = css;
  document.head.appendChild(style);
}

function _gstTripleCheckKeydown(e) {
  if (e.key === 'Escape' || e.keyCode === 27) _gstCloseTripleCheckModal();
}

function _gstCloseTripleCheckModal() {
  if (_gstTripleCheckModalEl && _gstTripleCheckModalEl.parentNode) {
    _gstTripleCheckModalEl.parentNode.removeChild(_gstTripleCheckModalEl);
  }
  _gstTripleCheckModalEl = null;
  document.removeEventListener('keydown', _gstTripleCheckKeydown, true);
}

function _gstOpenTripleCheckModal(form, onPass) {
  _gstCloseTripleCheckModal();
  _gstInjectTripleCheckStyles();
  var prefill = {
    email: _gstCustomerEmail(),
    phone: _gstCustomerField('phone'),
    nif: _gstCustomerField('nif')
  };

  var overlay = document.createElement('div');
  overlay.className = 'gst-tc-overlay';
  overlay.innerHTML =
      '<div class="gst-tc-dialog" role="dialog" aria-modal="true" aria-label="' + _esc(_gstTripleCheckMsg('modalTitle')) + '">' +
      '<button type="button" class="gst-tc-close" aria-label="' + _esc(_gstTripleCheckMsg('close')) + '">&times;</button>' +
      '<h2 class="gst-tc-title">' + _esc(_gstTripleCheckMsg('modalTitle')) + '</h2>' +
      '<p class="gst-tc-intro">' + _esc(_gstTripleCheckMsg('modalIntro')) + '</p>' +
      '<form class="gst-tc-form" novalidate>' +
      '<label class="gst-tc-label" for="gst-tc-email">' + _esc(_gstTripleCheckMsg('emailLabel')) + '</label>' +
      '<input id="gst-tc-email" class="gst-tc-input" type="email" autocomplete="email" inputmode="email" placeholder="' + _esc(_gstTripleCheckMsg('emailPlaceholder')) + '" />' +
      '<label class="gst-tc-label" for="gst-tc-phone">' + _esc(_gstTripleCheckMsg('phoneLabel')) + '</label>' +
      '<input id="gst-tc-phone" class="gst-tc-input" type="tel" autocomplete="tel" inputmode="tel" placeholder="' + _esc(_gstTripleCheckMsg('phonePlaceholder')) + '" />' +
      '<label class="gst-tc-label" for="gst-tc-nif">' + _esc(_gstTripleCheckMsg('nifLabel')) + '</label>' +
      '<input id="gst-tc-nif" class="gst-tc-input" type="text" autocomplete="off" placeholder="' + _esc(_gstTripleCheckMsg('nifPlaceholder')) + '" />' +
      '<p class="gst-tc-message" role="alert" hidden></p>' +
      '<button type="submit" class="gst-tc-submit btn btn--primary">' + _esc(_gstTripleCheckMsg('submit')) + '</button>' +
      '</form>' +
      '</div>';

  var dialog = overlay.querySelector('.gst-tc-dialog');
  var modalForm = overlay.querySelector('.gst-tc-form');
  var emailInput = overlay.querySelector('#gst-tc-email');
  var phoneInput = overlay.querySelector('#gst-tc-phone');
  var nifInput = overlay.querySelector('#gst-tc-nif');
  var message = overlay.querySelector('.gst-tc-message');
  var submit = overlay.querySelector('.gst-tc-submit');
  var closeBtn = overlay.querySelector('.gst-tc-close');

  emailInput.value = prefill.email;
  phoneInput.value = prefill.phone;
  nifInput.value = prefill.nif;

  function showMessage(text) {
    message.textContent = text || '';
    message.hidden = !text;
  }
  function setBusy(busy) {
    submit.disabled = !!busy;
    submit.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  overlay.addEventListener('click', function (e) {
    if (e.target === overlay) _gstCloseTripleCheckModal();
  });
  if (closeBtn) closeBtn.addEventListener('click', _gstCloseTripleCheckModal);
  if (dialog) dialog.addEventListener('click', function (e) { e.stopPropagation(); });

  modalForm.addEventListener('submit', function (e) {
    e.preventDefault();
    var email = _gstStr(emailInput.value);
    var phone = _gstNormalizePhone(phoneInput.value);
    var nif = _gstNormalizeNif(nifInput.value);
    if (!_gstIsValidEmail(email)) {
      showMessage(_gstTripleCheckMsg('invalidEmail'));
      emailInput.focus();
      return;
    }
    if (!phone) {
      showMessage(_gstTripleCheckMsg('invalidPhone'));
      phoneInput.focus();
      return;
    }
    if (!_gstIsValidDocument(nif)) {
      showMessage(_gstTripleCheckMsg('invalidNif'));
      nifInput.focus();
      return;
    }
    showMessage('');
    setBusy(true);
    _gstRunTripleCheck(form, { email: email, phone: phone, nif: nif }, onPass, function (text) {
      setBusy(false);
      showMessage(text);
      if (_gstTcFlag('block')) setBusy(true);
    });
  });

  document.body.appendChild(overlay);
  _gstTripleCheckModalEl = overlay;
  document.addEventListener('keydown', _gstTripleCheckKeydown, true);
  try {
    (emailInput.value ? (phoneInput.value ? nifInput : phoneInput) : emailInput).focus();
  } catch (e) {}
}

function _gstEnsureTripleCheck(form, onPass) {
  if (!_gstIsTripleCheck()) {
    if (typeof onPass === 'function') onPass();
    return;
  }
  if (!_gstTripleCheckProductId()) {
    _gstShowBookableError(form, _gstTripleCheckMsg('error'));
    return;
  }
  if (_gstTcFlag('block')) {
    _gstSetTripleCheckCtasDisabled(true);
    _gstShowBookableError(form, _gstTripleCheckMsg('notForYou'));
    return;
  }
  if (_gstTcFlag('pass')) {
    if (typeof onPass === 'function') onPass();
    return;
  }
  var known = _gstTripleCheckKnownIdentity();
  if (known) {
    _gstDebugLog('triplecheck: account identity, checking without popup');
    _gstRunTripleCheck(form, known, onPass);
    return;
  }
  _gstDebugLog('triplecheck: opening verification popup');
  _gstOpenTripleCheckModal(form, onPass);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _gstTripleCheckBoot);
} else {
  _gstTripleCheckBoot();
}

// Single choke point for every booking entry on a firstbookonly PDP. For other
// products it simply hands off immediately. A triple-check promo is verified
// first and, when both gates are set, the triple check is the one that runs.
function _gstHandoffWithFirstBookGate(opts) {
  opts = opts || {};
  if (_gstIsTripleCheck() && !opts.free) {
    _gstEnsureTripleCheck(opts.form, function () {
      _gstHandoffToBookingFlow(opts);
    });
    return;
  }
  if (!_gstIsFirstBookOnly()) {
    _gstHandoffToBookingFlow(opts);
    return;
  }

  var form = opts.form || null;
  var clinicId = (opts.clin && opts.clin.clinicId) ? parseInt(opts.clin.clinicId, 10) : 0;

  // clinic_id is required by the API (>= 1) and resolved from the selected
  // clinic ("Elige tu centro"). Without it we cannot run the check.
  if (!clinicId || clinicId < 1) {
    _gstDebugLog('firstbook: clinic unresolved, cannot run eligibility check');
    _gstShowBookableError(form, _gstFirstBookMsg('selectClinic'));
    return;
  }

  var email = _gstCustomerEmail();

  if (email) {
    _gstDebugLog('firstbook: logged-in eligibility check', { clinicId: clinicId });
    _gstSetFirstBookCtaBusy(form, true);
    _gstCheckHasAppointment(email, clinicId).then(function (res) {
      _gstSetFirstBookCtaBusy(form, false);
      if (!res.ok) {
        _gstDebugLog('firstbook: logged-in check failed', { status: res.status });
        _gstShowBookableError(form, _gstFirstBookMsg('error'));
        return;
      }
      if (res.hasAppointment) {
        _gstDebugLog('firstbook: logged-in customer has appointments, blocked');
        _gstShowBookableError(form, _gstFirstBookMsg('notForYou'));
        return;
      }
      _gstClearBookableError(form);
      _gstHandoffToBookingFlow(opts);
    });
  } else {
    _gstDebugLog('firstbook: guest, opening email modal', { clinicId: clinicId });
    _gstOpenFirstBookEmailModal(clinicId, function () {
      _gstHandoffToBookingFlow(opts);
    });
  }
}

window.GstBookingCtaInit = function (opts, btnArg) {
  opts = opts || {};
  var btn = btnArg || _el('gst-book-now-btn');
  if (!btn) return;

  function applyVariantDetail(detail) {
    if (!detail || !detail.variant || !detail.variant.id) return;
    var vid = String(detail.variant.id);
    var pdp = window.__GST_BOOKABLE_PRODUCT__ || {};
    var vc  = (pdp.variantClinicMap || {})[vid] || null;

    btn.dataset.variantId = vid;
    if (vc) {
      var rClinic = _gstResolveClinicIdFromVariant(vc);
      btn.dataset.clinicId      = rClinic;
      btn.dataset.calendarId    = _gstResolveCalendarForVariant(vc, rClinic);
      if (vc.clinicName)    btn.dataset.clinicName    = String(vc.clinicName);
      if (vc.clinicAddress) btn.dataset.clinicAddress = String(vc.clinicAddress);
    }
  }

  btn.addEventListener('click', function () {
    var pdp    = window.__GST_BOOKABLE_PRODUCT__ || {};
    var formEl = btn.closest('form[is="product-form"]') || document.querySelector('form[is="product-form"]');
    var data   = _gstReadBookableForm(formEl);
    var variantId = data.variantId || btn.dataset.variantId || opts.variantId || '';
    if (!variantId) {
      // No variant means no clinic to check against; on firstbookonly PDPs the
      // gate must run first, so don't shortcut to the calendar.
      if (_gstIsPurchaseGated()) {
        _gstShowBookableError(formEl, _gstFirstBookMsg('selectClinic'));
        return;
      }
      window.location.href = '/pages/reservar-fecha';
      return;
    }
    var vc = (pdp.variantClinicMap || {})[variantId] || null;

    // Explicit Flowww product id (e.g. the hero "Reservar Ahora" CTA passes the
    // product's `V1` metafield via data-product-id). When present, use it
    // directly and skip GPO zone resolution, which the hero has no form for.
    var explicitProductId = _gstStr(btn.dataset.productId) || _gstStr(opts.productId);

    // Only zone-based bookings need GPO option validation; an explicit product
    // id (hero/consulta CTA) drives the booking directly without a GPO form.
    if (!explicitProductId) {
      var validation = _gstValidateBookableSelection(pdp, data);
      if (!validation.ok) {
        _gstDebugLog('cta blocked: required options missing', { missing: validation.missing });
        _gstShowBookableError(formEl, validation.message || _gstRequiredOptionsMessage(validation.missing));
        return;
      }
      _gstClearBookableError(formEl);
    }

    var productIdPromise = explicitProductId
        ? Promise.resolve(explicitProductId)
        : _gstResolvePdpProductId(pdp, data);

    productIdPromise
        .then(function (productId) {
          _gstHandoffWithFirstBookGate({
            pdp:       pdp,
            variantId: variantId,
            title:     btn.dataset.productTitle  || opts.productTitle  || pdp.title  || '',
            handle:    btn.dataset.productHandle || opts.productHandle || pdp.handle || '',
            productId: productId,
            formData:  data,
            clin:      _gstClinicFromVariant(vc),
            form:      formEl
          });
        })
        .catch(function () {
          if (_gstIsPurchaseGated()) return;
          window.location.href = '/pages/reservar-fecha';
        });
  });

  document.addEventListener('variant:changed', function (e) { applyVariantDetail(e.detail); });
};

function _gstRedirectToBookingFlow(form, source) {
  var pdp  = window.__GST_BOOKABLE_PRODUCT__ || {};
  var data = _gstReadBookableForm(form);

  if (!data.variantId) {
    _gstDebugLog('intercept skip: no variant id (' + source + ')');
    return Promise.resolve(false);
  }

  var validation = _gstValidateBookableSelection(pdp, data);
  if (!validation.ok) {
    _gstDebugLog('intercept blocked: required options missing', {
      source:  source,
      missing: validation.missing
    });
    _gstShowBookableError(form, validation.message || _gstRequiredOptionsMessage(validation.missing));
    return Promise.resolve(false);
  }
  _gstClearBookableError(form);

  var vc = (pdp.variantClinicMap || {})[data.variantId] || null;

  return _gstResolvePdpProductId(pdp, data).then(function (productId) {
    _gstHandoffWithFirstBookGate({
      pdp:       pdp,
      variantId: data.variantId,
      title:     pdp.title  || '',
      handle:    pdp.handle || '',
      productId: productId,
      formData:  data,
      clin:      _gstClinicFromVariant(vc),
      form:      form
    });
    return true;
  });
}

// One-shot flag: set by #res_tarde_button click so the next product-form
// submit skips the booking intercept and carries properties[_bookLater]=true.
var _gstBookLaterPending = false;

function _gstShouldSkipPdpIntercept() {
  // "Compra ahora, reserva más tarde" — skip the calendar and use native ATC
  // with properties[_bookLater]=true so the API can invoice without a booking.
  if (_gstBookLaterPending) return true;
  // Products tagged `nobooking` set __GST_BOOKABLE_PDP__ to false in
  // theme.liquid, so this gate routes them to Shopify's native add-to-cart.
  if (!window.__GST_BOOKABLE_PDP__) return true;
  // We're on a bookable PDP, so the user clearly wants the booking flow.
  // Drop any stale slot/handoff state from a previous attempt before the
  // intercept runs — otherwise the previous attempt's slotTimecode would
  // make this function bail out and Shopify's native ATC would route to
  // /cart instead of /pages/reservar-datos.
  if (GstBookingState.get('slotTimecode')) {
    try { GstBookingState.clear(); } catch (e) {}
    _gstDebugLog('pdp: cleared stale slotTimecode before intercept');
  }
  console.log('cart skipped');
  return false;
}

document.addEventListener('submit', function (e) {
  let form = e.target;
  if (!form || form.tagName !== 'FORM') return;

  let isProductForm = !!(form.matches && form.matches('form[is="product-form"]'));
  let hasAddInput   = !!(form.querySelector && form.querySelector('[name="add"]'));

  if (isProductForm) {
    let inp = form.querySelector('input[name="id"]');
    _gstDebugLog('submit: product-form', {
      variantId: inp ? String(inp.value || '') : '',
      formId: form.id || '',
      bookablePdp: !!window.__GST_BOOKABLE_PDP__
    });
  }

  if (_gstShouldSkipPdpIntercept()) {
    if (isProductForm || hasAddInput) {
      _gstApplyGpoOptionLimits();
      // Por aquí pasan dos cosas distintas: "compra ahora, reserva más tarde"
      // (que debe validar igual que el botón de agendar) y los productos sin
      // reserva, a los que no se les puede exigir zonas.
      var validacion = _gstBookLaterPending
        ? _gstValidateBookableSelection(window.__GST_BOOKABLE_PRODUCT__ || {}, _gstReadBookableForm(form))
        : _gstValidateGpoForm(form);
      if (!validacion.ok) {
        e.preventDefault();
        e.stopImmediatePropagation();
        _gstBookLaterPending = false;
        _gstSetBookLaterOnProductForms(false);
        _gstShowBookableError(form, validacion.message || _gstRequiredOptionsMessage(validacion.missing));
        return;
      }
      _gstClearBookableError(form);
      // Native add-to-cart (nobooking promos, book-later) re-submits once the
      // triple check has passed; the pass flag lets the second submit through.
      if (_gstIsTripleCheck() && !_gstTcFlag('pass')) {
        e.preventDefault();
        e.stopImmediatePropagation();
        _gstEnsureTripleCheck(form, function () {
          var addBtn = form.querySelector('[name="add"]');
          if (typeof form.requestSubmit === 'function') form.requestSubmit(addBtn || undefined);
          else form.submit();
        });
        return;
      }
    }
    if (isProductForm) _gstDebugLog('intercept skip: not bookable or booking already chosen');
    // ProductForm builds FormData synchronously after capture listeners return.
    // Clear the one-shot book-later flag/property on the next tick so this
    // submit still carries properties[_bookLater]=true but the next ATC does not.
    if (_gstBookLaterPending) {
      setTimeout(function () {
        _gstBookLaterPending = false;
        _gstSetBookLaterOnProductForms(false);
      }, 0);
    }
    return;
  }
  if (!isProductForm && !hasAddInput) return;

  e.preventDefault();
  e.stopImmediatePropagation();
  _gstBookLaterPending = false;
  _gstSetBookLaterOnProductForms(false);
  _gstRedirectToBookingFlow(form, 'submit').catch(function (err) {
    _gstDebugLog('intercept failed', { err: String(err && err.message ? err.message : err) });
    // Never fall through to the calendar on a firstbookonly PDP: the
    // eligibility gate hasn't run, so silently fail instead of bypassing it.
    if (_gstIsPurchaseGated()) return;
    window.location.href = '/pages/reservar-fecha';
  });
}, true);

document.addEventListener('click', function (e) {
  if (_gstShouldSkipPdpIntercept()) return;

  let target = e.target;
  if (!target || !target.closest) return;
  let btn = target.closest('[name="add"], button[type="submit"][data-add-to-cart], [data-cart-add]');
  if (!btn) return;

  let form = btn.form || btn.closest('form');

  _gstDebugLog('click: ATC button', {
    btnTag: btn.tagName || '',
    btnName: btn.getAttribute('name') || '',
    hasForm: !!form,
    formIs: form ? (form.getAttribute('is') || '') : '',
    bookablePdp: !!window.__GST_BOOKABLE_PDP__
  });

  if (e.defaultPrevented) return;

  e.preventDefault();
  e.stopImmediatePropagation();
  _gstBookLaterPending = false;
  _gstSetBookLaterOnProductForms(false);
  _gstRedirectToBookingFlow(form, 'click').catch(function (err) {
    _gstDebugLog('intercept failed', { err: String(err && err.message ? err.message : err) });
    if (_gstIsPurchaseGated()) return;
    window.location.href = '/pages/reservar-fecha';
  });
}, true);

// === Consultation booking ("¿Tienes dudas? Reserva una consulta") ============
//
// The PDP renders a "Reserva ahora" button inside the consultation collapsible.
// The shared product-information-blocks snippet marks that collapsible's content
// container with `data-gst-consulta-booking` and the product's `V1` metafield in
// `data-gst-product-id` (with __GST_BOOKABLE_PRODUCT__.v1 as a global fallback).
// Any click on a button/link inside that container starts the booking flow with
// product_id = V1, independent of GPO zone resolution. Wiring lives in the shared
// renderer, so new product templates get this behaviour with no extra markup.

// Native Shopify variant picker for "Elige tu centro" ships with a disabled
// placeholder option while input[name="id"] still holds the first variant.
// Treat the booking as unselected until the customer picks a real centro.
function _gstClinicSelectEl(form) {
  function findIn(root) {
    if (!root || !root.querySelectorAll) return null;
    var selects = root.querySelectorAll('select[name^="options["]');
    for (var i = 0; i < selects.length; i++) {
      var name = (selects[i].getAttribute('name') || '').toLowerCase();
      if (/centro|cl[ií]nica|clinic|sede|ubicacion|localizacion|location/.test(name)) {
        return selects[i];
      }
    }
    return null;
  }
  if (form) {
    var inForm = findIn(form);
    if (inForm) return inForm;
  }
  return findIn(document);
}

function _gstIsClinicVariantSelected(form) {
  var sel = _gstClinicSelectEl(form);
  if (!sel) return true;
  var v = _gstStr(sel.value);
  if (!v) return false;
  var opt = sel.selectedOptions && sel.selectedOptions[0];
  if (opt && opt.disabled && v === '') return false;
  return true;
}

// Consultation bookings always use the V1 Flowww product id and must not carry
// GPO body-part / session picks into the calendar or cart.
function _gstConsultaFormData(variantId) {
  return {
    variantId:   _gstStr(variantId),
    sessions:    '',
    zones:       [],
    zoneEntries: [],
    sessionsByGroup: {}
  };
}

function _gstResolveCurrentBookableVariantId(pdp) {
  var formEl = document.querySelector('form[is="product-form"]');
  if (formEl) {
    var idInput = formEl.querySelector('input[name="id"]');
    var fromForm = idInput ? _gstStr(idInput.value) : '';
    if (fromForm) return fromForm;
  }
  // Fall back to the first variant in the clinic map so clinic data can still
  // resolve when no product form is present on the page.
  var map = (pdp && pdp.variantClinicMap) || {};
  var keys = Object.keys(map);
  return keys.length ? _gstStr(keys[0]) : '';
}

function _gstStartConsultaBooking(container) {
  var pdp = window.__GST_BOOKABLE_PRODUCT__ || {};
  var productId = _gstStr(container && container.dataset ? container.dataset.gstProductId : '') ||
      _gstStr(pdp.v1);

  var formEl = document.querySelector('form[is="product-form"]');

  if (!_gstIsClinicVariantSelected(formEl)) {
    _gstShowBookableError(formEl, _gstFirstBookMsg('selectClinic'));
    return;
  }

  if (!productId) {
    _gstShowBookableError(formEl, 'No pudimos identificar el tratamiento de consulta.');
    return;
  }

  var data = _gstReadBookableForm(formEl);
  var variantId = data.variantId || _gstResolveCurrentBookableVariantId(pdp);
  if (!variantId) {
    _gstShowBookableError(formEl, _gstFirstBookMsg('selectClinic'));
    return;
  }

  var vc = (pdp.variantClinicMap || {})[variantId] || null;

  _gstDebugLog('consulta: start booking', {
    productId:  productId || 'MISSING (custom.V1)',
    variantId:  variantId || 'MISSING',
    fromGlobal: !!pdp.v1,
    clinic:     _gstClinicFromVariant(vc)
  });

  _gstClearBookableError(formEl);

  _gstHandoffWithFirstBookGate({
    pdp:       pdp,
    variantId: variantId,
    title:     pdp.title  || '',
    handle:    pdp.handle || '',
    productId: productId,
    formData:  _gstConsultaFormData(variantId),
    clin:      _gstClinicFromVariant(vc),
    form:      formEl,
    // The "1ª visita de consulta gratuita" flow (res_ahora_button /
    // [data-gst-consulta-booking]) is always free: don't snapshot the PDP price,
    // so the confirm step shows no price.
    free:      true
  });
}

// The ID of a custom-liquid button that starts the consultation booking flow
// directly, without the merchant having to enable the row's "Trigger
// consultation booking" toggle. Lets a button pasted into any Custom liquid
// block work as long as it carries this id. The product_id resolves from the
// row's data-gst-product-id when present, otherwise from the global custom.V1
// metafield (__GST_BOOKABLE_PRODUCT__.v1).
var GST_CONSULTA_BUTTON_ID = 'res_ahora_button';

document.addEventListener('click', function (e) {
  if (!e.target || !e.target.closest) return;

  // Entry point 1: any a/button inside a row flagged for consulta booking.
  var trigger = e.target.closest('[data-gst-consulta-booking] a, [data-gst-consulta-booking] button');

  // Entry point 2: a button carrying the well-known id (or an <a> wrapping it,
  // which is common since custom liquid often nests <button> inside <a href="#">).
  if (!trigger) {
    trigger = e.target.closest('#' + GST_CONSULTA_BUTTON_ID);
    if (!trigger) {
      var anchor = e.target.closest('a');
      if (anchor && anchor.querySelector('#' + GST_CONSULTA_BUTTON_ID)) trigger = anchor;
    }
  }
  if (!trigger) return;

  // Container is optional: present for toggle-flagged rows (carries the V1
  // product id), absent for a bare id button (falls back to the global V1).
  var container = trigger.closest('[data-gst-consulta-booking]');
  document.getElementById('free_consultation').value = "Verdadero";
  var freeConsulation = document.getElementById('free_consultation').value;
  if(freeConsulation){
    GstBookingState.set('freeConsulation', freeConsulation);
  }
  
  e.preventDefault();
  e.stopImmediatePropagation();
  try {
    _gstStartConsultaBooking(container);
  } catch (err) {
    _gstDebugLog('consulta intercept failed', { err: String(err && err.message ? err.message : err) });
    if (_gstIsPurchaseGated()) return;
    window.location.href = '/pages/reservar-fecha';
  }
}, true);

// === Book later ("Compra ahora, reserva más tarde") ==========================
//
// Custom-liquid button #res_tarde_button on bookable PDPs. Skips the calendar
// and adds to cart with properties[_bookLater]=true so the API can treat the
// order as pay-now / book-later.
//
// IMPORTANT: Do NOT use native product-form requestSubmit() here. Parent clinic
// variants are €0 and the real price lives on the GPO-linked body-part
// products, so a native ATC reaches the drawer at 0,00 €.
//
// Globo's .gpo-clone-atc-button does post the priced variants, but it takes
// their quantity from its own option config -- the "| 6" in "Barbilla | 6" --
// so an x6 pack whose €132 already covers six sessions was charged 6 × €132.
// Hence _gstBookLaterAddViaZones, which reads the quantity from the visible
// stepper (1 when the PDP has none) and is the resolution the calendar flow
// already uses, so both ways of buying build the same line.
//
// _gstWatchBookLaterCartAdd below only tracked Globo's transport; it is unused.
function _gstStartBookLaterAdd(form, btn) {
  if (btn && btn.nodeType === 1) {
    btn.disabled = true;
    btn.textContent = 'Procesando...';
  }
  _gstSetBookLaterOnProductForms(true);
  _gstBookLaterPending = true;
  var clinicId = _gstBookLaterClinicId(form);
  _gstDebugLog('book-later: resolving zone items → checkout', {
    variantId: (form.querySelector('[name="id"]') || {}).value || '',
    clinicId: clinicId
  });
  _gstBookLaterAddViaZones(form, btn, _gstSetBookingJourney('book_later'));
}

var GST_BOOK_LATER_BUTTON_ID = 'res_tarde_button';
var GST_BOOK_LATER_LABEL = 'Compra ahora, reserva más tarde';

function _gstEnsureBookLaterProperty(form, enabled) {
  if (!form) return;
  var name = 'properties[_bookLater]';
  var field = form.querySelector('[name="' + name + '"]');
  if (enabled) {
    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = name;
      field.setAttribute('data-gst-book-later-property', '');
      form.appendChild(field);
    }
    field.value = 'true';
    return;
  }
  if (field && field.parentNode) field.parentNode.removeChild(field);
}

// Bookable PDPs do not render Liquid properties[_clinic_id] (that is nobooking-only
// in buy-buttons.liquid). Book-later still needs it for OrderClinicId / Flowww.
function _gstBookLaterClinicId(form) {
  var idInput = form && form.querySelector('[name="id"]');
  var variantId = idInput ? _gstStr(idInput.value) : '';
  var clinicId = _gstClinicIdForVariant(variantId);
  if (clinicId === null) {
    var existing = form && form.querySelector('[name="properties[_clinic_id]"]');
    return existing ? _gstStr(existing.value) : '';
  }
  return clinicId || '';
}

// Nombre y dirección del centro de la variante elegida (reservar más tarde).
function _gstBookLaterClinicInfo(variantId) {
  var pdp = (typeof window !== 'undefined' && window.__GST_BOOKABLE_PRODUCT__) || {};
  var vc = pdp.variantClinicMap && pdp.variantClinicMap[_gstStr(variantId)];
  if (!vc) return null;
  try { return _gstClinicFromVariant(vc); } catch (e) { return null; }
}

function _gstEnsureClinicIdProperty(form, enabled) {
  if (!form) return;
  var name = 'properties[_clinic_id]';
  var field = form.querySelector('[name="' + name + '"]');
  if (enabled) {
    var clinicId = _gstBookLaterClinicId(form);
    if (!clinicId) {
      // Do not send clinic_id=0 — Shopify should omit rather than bill the wrong centro.
      if (field && field.hasAttribute('data-gst-book-later-clinic')) {
        field.parentNode.removeChild(field);
      }
      return;
    }
    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = name;
      field.setAttribute('data-gst-book-later-clinic', '');
      form.appendChild(field);
    }
    field.value = clinicId;
    // Centro y dirección para el bloque "Tu cita" del checkout.
    var idInput = form.querySelector('[name="id"]');
    var info = _gstBookLaterClinicInfo(idInput ? idInput.value : '');
    var pdp = (typeof window !== 'undefined' && window.__GST_BOOKABLE_PRODUCT__) || {};
    _gstSetBookLaterHiddenProp(form, '_clinic', info && info.clinicName);
    _gstSetBookLaterHiddenProp(form, '_clinic_address', info && info.clinicAddress);
    _gstSetBookLaterHiddenProp(form, '_promo', pdp.title);
    return;
  }
  if (field && field.hasAttribute('data-gst-book-later-clinic') && field.parentNode) {
    field.parentNode.removeChild(field);
  }
  _gstSetBookLaterHiddenProp(form, '_clinic', '');
  _gstSetBookLaterHiddenProp(form, '_clinic_address', '');
  _gstSetBookLaterHiddenProp(form, '_promo', '');
}

// Crea/actualiza (o quita si no hay valor) un campo oculto properties[key]
// marcado como nuestro, sin tocar campos que haya puesto el theme u otra app.
function _gstSetBookLaterHiddenProp(form, key, value) {
  var name = 'properties[' + key + ']';
  var field = form.querySelector('[name="' + name + '"][data-gst-book-later-clinic]');
  var val = _gstStr(value);
  if (!val) {
    if (field && field.parentNode) field.parentNode.removeChild(field);
    return;
  }
  if (form.querySelector('[name="' + name + '"]:not([data-gst-book-later-clinic])')) return;
  if (!field) {
    field = document.createElement('input');
    field.type = 'hidden';
    field.name = name;
    field.setAttribute('data-gst-book-later-clinic', '');
    form.appendChild(field);
  }
  field.value = val;
}

function _gstSetBookLaterOnProductForms(enabled) {
  var forms = document.querySelectorAll('form[is="product-form"]');
  Array.prototype.forEach.call(forms, function (form) {
    _gstEnsureBookLaterProperty(form, enabled);
    _gstEnsureClinicIdProperty(form, enabled);
  });
}

function _gstResetBookLaterButton(btn) {
  if (!btn) return;
  btn.disabled = false;
  if (btn.getAttribute('data-gst-book-later-label')) {
    btn.textContent = btn.getAttribute('data-gst-book-later-label');
  } else {
    btn.textContent = GST_BOOK_LATER_LABEL;
  }
}

var _gstBookLaterNavDone = false;

function _gstFinishBookLater(ok, btn) {
  if (_gstBookLaterNavDone) return;
  _gstBookLaterPending = false;
  if (!ok) {
    _gstSetBookLaterOnProductForms(false);
    _gstResetBookLaterButton(btn);
    return;
  }
  _gstBookLaterNavDone = true;
  // Leave _bookLater on the form until navigation; clear pending so a later
  // ATC in this same page (if checkout nav fails) does not stay skipped forever.
  setTimeout(function () {
    _gstSetBookLaterOnProductForms(false);
  }, 0);
  // replace + short retries beat the theme's cart-drawer /cart navigation race.
  try { window.location.replace('/checkout'); } catch (e1) {
    window.location.href = '/checkout';
  }
  setTimeout(function () {
    if (String(window.location.href || '').indexOf('/checkout') === -1) {
      window.location.href = '/checkout';
    }
  }, 100);
  setTimeout(function () {
    if (String(window.location.href || '').indexOf('/checkout') === -1) {
      window.location.href = '/checkout';
    }
  }, 400);
}

function _gstIsCartAddUrl(url) {
  return String(url || '').indexOf('/cart/add') !== -1;
}

// Repair path only. The API reads both properties at order level
// (BookLaterLines::orderHasBookLaterLine / OrderClinicId::fromLineItems), so the
// parent line carrying them is enough and the normal flow skips this entirely —
// the extra /cart/change.js round trips lose the race against the theme's
// cart navigation. Used when the add somehow landed without them.
function _gstStampBookLaterOnCart(clinicId) {
  return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      var items = (cart && cart.items) || [];
      if (!items.length) return cart;

      var chain = Promise.resolve();
      items.forEach(function (item, idx) {
        chain = chain.then(function () {
          var props = {};
          var existing = item.properties || {};
          Object.keys(existing).forEach(function (k) {
            if (existing[k] != null && existing[k] !== '') props[k] = existing[k];
          });
          props._bookLater = 'true';
          if (clinicId) props._clinic_id = String(clinicId);
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              line: idx + 1,
              quantity: item.quantity,
              properties: props
            })
          }).then(function (res) {
            if (!res.ok) {
              _gstDebugLog('book-later: cart stamp failed', { line: idx + 1, status: res.status });
            }
          });
        });
      });
      return chain;
    });
}

// After GPO/theme ATC, go to checkout instead of the side-cart drawer.
// Covers fetch, XHR, and a cart.js poll (Globo/theme transport varies).
function _gstWatchBookLaterCartAdd(btn, clinicId) {
  _gstBookLaterNavDone = false;
  var themeFetch = window.fetch;
  var XHR = window.XMLHttpRequest;
  var restored = false;
  var sawAdd = false;
  var baselineCount = -1;
  var finishing = false;

  themeFetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
    .then(function (r) { return r.json(); })
    .then(function (cart) { baselineCount = cart && cart.item_count != null ? cart.item_count : 0; })
    .catch(function () { baselineCount = 0; });

  function restore() {
    if (restored) return;
    restored = true;
    window.fetch = themeFetch;
    window.XMLHttpRequest = XHR;
  }

  function onTransportOk() {
    sawAdd = true;
  }

  // `needsRepair` is false on the happy path: redirect immediately, because the
  // theme is racing us to /cart and every extra request hands it the win.
  function goCheckout(needsRepair) {
    if (finishing || _gstBookLaterNavDone) return;
    finishing = true;
    clearInterval(pollId);
    restore();
    if (!needsRepair) {
      _gstFinishBookLater(true, btn);
      return;
    }
    _gstStampBookLaterOnCart(clinicId)
      .catch(function () {})
      .then(function () {
        _gstFinishBookLater(true, btn);
      });
  }

  window.fetch = function () {
    var args = arguments;
    var url = args[0] instanceof Request ? args[0].url : String(args[0] || '');
    if (_gstIsCartAddUrl(url)) {
      sawAdd = true;
      return themeFetch.apply(this, args).then(function (response) {
        if (response.ok) onTransportOk();
        return response;
      }).catch(function (err) {
        throw err;
      });
    }
    return themeFetch.apply(this, args);
  };

  window.XMLHttpRequest = function () {
    var xhr = new XHR();
    var open = xhr.open;
    xhr.open = function (method, url) {
      xhr.__gstBookLaterUrl = url;
      return open.apply(xhr, arguments);
    };
    xhr.addEventListener('load', function () {
      if (_gstBookLaterNavDone) return;
      if (!_gstIsCartAddUrl(xhr.__gstBookLaterUrl)) return;
      if (xhr.status >= 200 && xhr.status < 300) onTransportOk();
    });
    return xhr;
  };
  window.XMLHttpRequest.prototype = XHR.prototype;
  try {
    ['UNSENT', 'OPENED', 'HEADERS_RECEIVED', 'LOADING', 'DONE'].forEach(function (k) {
      window.XMLHttpRequest[k] = XHR[k];
    });
  } catch (e2) {}

  // Poll cart until a priced line appears, stamp props, then checkout.
  var polls = 0;
  var pollId = setInterval(function () {
    if (_gstBookLaterNavDone || finishing) {
      clearInterval(pollId);
      restore();
      return;
    }
    polls += 1;
    themeFetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (_gstBookLaterNavDone || finishing) return;
        var items = (cart && cart.items) || [];
        var hasBookLater = items.some(function (i) {
          return i && i.properties && String(i.properties._bookLater) === 'true';
        });
        var hasClinic = !clinicId || items.some(function (i) {
          return i && i.properties && _gstStr(i.properties._clinic_id);
        });
        var hasPaid = items.some(function (i) {
          return i && Number(i.final_line_price || i.line_price || i.price || 0) > 0;
        });
        var grew = baselineCount >= 0 && (cart.item_count || 0) > baselineCount;
        // Checkout once this click produced a priced cart (not a stale one).
        if (hasPaid && (hasBookLater || sawAdd || grew)) {
          goCheckout(!(hasBookLater && hasClinic));
          return;
        }
        if (polls >= 24) {
          clearInterval(pollId);
          restore();
          if (hasBookLater || (hasPaid && (sawAdd || grew))) {
            goCheckout(!(hasBookLater && hasClinic));
          } else {
            _gstFinishBookLater(false, btn);
            _gstDebugLog('book-later: cart/add not confirmed', { sawAdd: sawAdd });
          }
        }
      })
      .catch(function () {});
  }, 250);
}

// Fallback when Globo's clone ATC is missing: resolve priced zone variants
// ourselves (same path as booking _addToCart) and post multi-item /cart/add.js.
// `journeyReady` resolves before /cart/add.js is posted, so the add → checkout
// redirect stays immediate; putting the journey write after the add would hand
// the theme's /cart navigation race the win.
function _gstBookLaterAddViaZones(form, btn, journeyReady) {
  var data = _gstReadBookableForm(form);
  _gstApplyDefaultSessions(data);
  var parentId = (form.querySelector('[name="id"]') || {}).value || '';
  var pdpForPack = (typeof window !== 'undefined' && window.__GST_BOOKABLE_PRODUCT__) || {};
  var bookLaterProps = { _bookLater: 'true' };
  var clinicId = _gstBookLaterClinicId(form);
  if (clinicId) bookLaterProps._clinic_id = clinicId;
  // Centro, dirección y tratamiento para el bloque "Tu cita" del checkout.
  var clinicInfo = _gstBookLaterClinicInfo(parentId);
  if (clinicInfo && clinicInfo.clinicName) bookLaterProps._clinic = clinicInfo.clinicName;
  if (clinicInfo && clinicInfo.clinicAddress) bookLaterProps._clinic_address = clinicInfo.clinicAddress;
  if (_gstStr(pdpForPack.title)) bookLaterProps._promo = _gstStr(pdpForPack.title);
  var zones = data.zones || [];
  var parentProps = Object.assign({}, bookLaterProps);
  if (zones.length) {
    parentProps.Zona = zones.join(', ');
    parentProps._zones = zones.join(', ');
  }
  if (data.sessions) {
    parentProps._sessions = data.sessions;
    parentProps._sessions_label = data.sessions;
  }
  // Pack de promo en checkout: misma marca en la promo y en sus zonas.
  var packGroup = (parentId && _gstPromoPackEnabledFor(pdpForPack.id)) ? String(Date.now()) : '';
  if (packGroup) parentProps._gst_group = packGroup;

  return Promise.all([
    _gstResolveZoneCartItems(data.zoneEntries || []),
    journeyReady || Promise.resolve(false)
  ]).then(function (results) {
    var resolved = results[0];
    var items = [];
    if (parentId) {
      items.push({ id: Number(parentId), quantity: 1, properties: parentProps });
    }
    resolved.forEach(function (entry) {
      var props = Object.assign({}, bookLaterProps);
      if (entry.label) {
        props.Zona = entry.label;
        props._zones = entry.label;
      }
      var qty = parseInt(entry.quantity, 10);
      if (isNaN(qty) || qty < 1) qty = 1;
      var lineSessions = _gstLineSessions(entry, qty);
      props._sessions = String(lineSessions.perUnit);
      props._sessions_label = String(lineSessions.total);
      if (packGroup) props._gst_group = packGroup;
      items.push({ id: Number(entry.variantId), quantity: qty, properties: props });
    });

    if (!items.length || items.length < 2 && !resolved.length) {
      _gstDebugLog('book-later: no priced zone items to add', {
        parentId: parentId,
        zones: data.zoneEntries
      });
      _gstFinishBookLater(false, btn);
      return;
    }

    _gstDebugLog('book-later: fallback multi-item ATC', {
      count: items.length,
      parentId: parentId,
      zones: resolved
    });

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items }),
      credentials: 'same-origin'
    }).then(function (res) {
      if (res.ok) _gstFinishBookLater(true, btn);
      else _gstFinishBookLater(false, btn);
    }).catch(function () {
      _gstFinishBookLater(false, btn);
    });
  });
}

document.addEventListener('click', function (e) {
  if (!e.target || !e.target.closest) return;

  var trigger = e.target.closest('#' + GST_BOOK_LATER_BUTTON_ID);
  if (!trigger) {
    var anchor = e.target.closest('a');
    if (anchor && anchor.querySelector('#' + GST_BOOK_LATER_BUTTON_ID)) trigger = anchor;
  }
  if (!trigger) return;

  // Own the click. A product-template script used to also capture this on
  // window (before document) and ATC without _bookLater/_clinic_id — removed.
  e.preventDefault();
  e.stopImmediatePropagation();

  var btn = trigger.id === GST_BOOK_LATER_BUTTON_ID
    ? trigger
    : (trigger.querySelector('#' + GST_BOOK_LATER_BUTTON_ID) || trigger);

  var form = document.querySelector('form[is="product-form"].main-product-form')
    || document.querySelector('form[is="product-form"]');
  if (!form) {
    _gstDebugLog('book-later: no product form found');
    return;
  }

  // Misma validación de zonas que el botón de agendar: los dos comprueban lo
  // mismo y solo se diferencian en el destino (calendario o carrito). Antes
  // este botón usaba _gstValidateGpoForm, que se saltaba los totales de
  // sesiones de los combos, la regla de colectivos y el caso de "hay opciones
  // pero ninguna zona resuelta".
  _gstApplyGpoOptionLimits();
  var pdpBookLater = window.__GST_BOOKABLE_PRODUCT__ || {};
  var validation = _gstValidateBookableSelection(pdpBookLater, _gstReadBookableForm(form));
  if (!validation.ok) {
    _gstDebugLog('book-later blocked: required options missing', { missing: validation.missing });
    _gstShowBookableError(form, validation.message || _gstRequiredOptionsMessage(validation.missing));
    return;
  }
  _gstClearBookableError(form);

  if (!btn.getAttribute('data-gst-book-later-label')) {
    btn.setAttribute('data-gst-book-later-label', (btn.textContent || GST_BOOK_LATER_LABEL).trim());
  }
  
  console.log('redirecting to checkout');
  var mainpid = document.getElementById('mainpid').value;
  //console.log(productId);
  if(mainpid == 15632407986524){
    console.log("product id is "+ mainpid);
    var ageGateDiv = document.getElementById('ageGate');
    ageGateDiv.classList.add('show');
    ageGateDiv.classList.remove('hidden');
    btn.setAttribute('data-clicked', 'true');
  }else{
    _gstEnsureTripleCheck(form, function () {
      _gstStartBookLaterAdd(form, btn);
    });
  }
  //_gstBookLaterAddViaZones(form, btn, _gstSetBookingJourney('book_later'));
  
}, true);

// Exact age in whole years for an ISO date (YYYY-MM-DD), or null when the date
// is missing/invalid/in the future. Calendar-accurate (no 365.25 approximation).
function _gstAgeFromDob(isoDate) {
  if (!isoDate) return null;
  var dob = new Date(isoDate + 'T00:00:00');
  if (isNaN(dob.getTime())) return null;
  var today = new Date();
  if (dob > today) return null;
  var age = today.getFullYear() - dob.getFullYear();
  var m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

// Age gate for the "menores de 26" combo: only customers under 26 may continue
// to the booking calendar. The gate is opened from _gstHandoffToBookingFlow for
// the configured product id; this just validates the entered date of birth.
// Guarded so booking.js stays error-free on pages without the gate markup.
(function () {
  var ageGateButton = document.getElementById('age_submit');
  if (!ageGateButton) return;

  function showAgeError(msg) {
    var box = document.getElementById('ageGate_error');
    if (box) {
      box.textContent = msg;
      box.hidden = false;
    } else {
      try { window.alert(msg); } catch (e) {}
    }
  }

  ageGateButton.addEventListener('click', function (e) {
    var bookLaterButton = document.getElementById('res_tarde_button').getAttribute('data-clicked');
    if (e && e.preventDefault) e.preventDefault();
    var input = document.getElementById('user_dob');
    var dob = input ? input.value : '';
    var age = _gstAgeFromDob(dob);

    if (age === null) {
      showAgeError('Introduce una fecha de nacimiento válida.');
      if (input && input.focus) input.focus();
      return;
    }
    if (age >= 26) {
      showAgeError('Lo sentimos, esta promoción es solo para menores de 26 años.');
      return;
    }
    var confirmEl = document.getElementById('age_confirm');
    if (confirmEl && !confirmEl.checked) {
      showAgeError('Debes marcar la casilla de confirmación para continuar.');
      return;
    }
    if(bookLaterButton == 'true'){
      var btn = document.getElementById(GST_BOOK_LATER_BUTTON_ID);
      var form = document.querySelector('form[is="product-form"].main-product-form')
        || document.querySelector('form[is="product-form"]');
      if (!form) {
        _gstDebugLog('book-later: no product form found');
        return;
      }
      _gstEnsureTripleCheck(form, function () {
        _gstStartBookLaterAdd(form, btn);
      });
    }else{
      window.location.href = '/pages/reservar-fecha';
    }
    
   // window.location.href = '/pages/reservar-fecha';
  });

  // Allow closing the modal by clicking the dimmed backdrop or pressing Escape.
  var ageGate = document.getElementById('ageGate');
  function closeAgeGate() {
    if (!ageGate) return;
    ageGate.classList.remove('show');
    ageGate.classList.add('hidden');
    var btn = document.getElementById('res_tarde_button');
    btn.setAttribute('data-clicked', 'false');
  }
  if (ageGate) {
    ageGate.addEventListener('click', function (e) {
      if (e.target === ageGate) closeAgeGate();
    });
  }
  var ageGateClose = document.getElementById('ageGate_close');
  if (ageGateClose) ageGateClose.addEventListener('click', closeAgeGate);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && ageGate && ageGate.classList.contains('show')) closeAgeGate();
  });
})();

// function createWrapper(i)
// {
//   var product_item_wrapper = document.createElement("div");
//   product_item_wrapper.innerHTML = "<a class='qty_minus' onclick='adjustValue(-1,"+i+", false)'>-</a><input class='input_qty' min='0' max='3' type='number' id='qty_"+i+"' name='qty_"+i+"' /><a class='qty_plus' onclick='adjustValue(1,"+i+", true)'>+</a>";
//   product_item_wrapper.className = "qty_wrapper";
//   return product_item_wrapper;
// }
// function adjustValue(value, i, opt) {
//   const input = document.getElementById('qty_'+i);
//   var input_value = input.value;
//   if(opt == false && input_value > 0){
//     input.value = Number(input.value) + value;
//   }
//   if(opt == true && input_value < 3){
//     input.value = Number(input.value) + value;
//   }
// }
// var mainpid2 = document.getElementById('mainpid').value;
// if(mainpid2 == 15632407986524){
//   setTimeout(function () { 
//     console.log('checking');
//     var divjes = {};
//     divjes = document.getElementsByClassName("gpo-ov-item");
//     for (var i = 0; i < divjes.length; i++)
//     {
//         divjes[i].append(createWrapper(i));
//     }
//   }, 1500);
// }